import { env } from '@/env';
import { notifySubmissionChange } from '@/lib/submissionRefresh';
import type { MilestoneKind } from '@shared/milestoneKinds';
import { devIdentityHeaders, rawFetchHeaders } from '@/lib/devIdentity';

/** Error thrown by the client for non-2xx responses. */
export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string, message?: string) {
    super(message ?? body ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Structured shape backend 4xx responses follow: `{ error, message, data? }`
 * where `data` may carry endpoint-specific detail (e.g. Phase 5.10e's
 * `BulkConfirmRejected` returns `{ notFound, notClassified, mismatchedSubmission }`
 * under `data`).
 */
export interface ApiErrorBody {
  /** Test #9 — assistant rate limits say how long to wait (seconds). */
  retryAfterSeconds?: number;
  error?: string;
  message?: string;
  data?: unknown;
  /** T-17 D11 — PermissionDenied bodies name the missing permission. */
  required?: { resource?: string; action?: string };
}

/**
 * Best-effort parse of an `ApiError.body` JSON payload. Returns `null` if
 * the input isn't an `ApiError` instance, has no body, or the body isn't
 * valid JSON (e.g. a transport-level failure where `err` is a plain
 * `Error`). Promoted from Phase 5.10f's DocumentsTabPanel-local helper
 * during Phase 5.10h so other admin pages can adopt the same
 * structured-error rendering pattern.
 */
/**
 * D-25 — what POST /api/v1/join/preview returns for the invited person.
 * Both fields are what the SERVER chose to disclose: `invitedBy` is a
 * display name (falling back to the inviting address), and `roleName` is
 * absent rather than guessed when the role name could not be read.
 */
export interface InvitationPreview {
  readonly invitedBy?: string;
  readonly roleName?: string;
}

/** D-25 — what POST /api/v1/join returns once the invitation is accepted. */
export interface AcceptedInvitation {
  readonly email: string;
  readonly roleId: string;
  readonly roleName?: string;
}

/**
 * D-25 — the `data` an InvitationEmailMismatch refusal carries. The address
 * is ALREADY MASKED by the server (`s•••••@example.com`): the full
 * address is never sent to a caller who is not the invitee, so there is
 * nothing for this client to mask.
 */
export interface InvitationMismatchData {
  readonly invitedEmail?: string;
}

export function parseApiErrorBody(err: unknown): ApiErrorBody | null {
  if (!(err instanceof ApiError)) return null;
  if (!err.body) return null;
  try {
    return JSON.parse(err.body) as ApiErrorBody;
  } catch {
    return null;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Skip envelope unwrap + JSON parse; used for file downloads. */
  raw?: boolean;
  /**
   * Phase dashboard-performance-fix — return the full `{ ok, data, pagination }`
   * envelope instead of just the unwrapped `data` field. Used by
   * `submissions.list` so the caller can access `pagination.total`.
   */
  fullEnvelope?: boolean;
  /** Body — string, FormData, Blob, etc. */
  body?: BodyInit;
  /** JSON-serializable payload; sets Content-Type automatically. */
  json?: unknown;
}

// Phase 4.3 wires the real MSAL token getter; dev mode returns null so
// the V2 backend's AUTH_ENABLED=false branch hands us a dev user identity.
let tokenGetter: () => Promise<string | null> = async () => null;

export function setTokenGetter(getter: () => Promise<string | null>): void {
  tokenGetter = getter;
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  error?: string;
  message?: string;
  /**
   * Phase dashboard-performance-fix — `total` replaces the old `count` field.
   * `total` is the full count of matching rows (ignoring limit/offset).
   * `count` (kept for backward compat) is the length of the current page.
   */
  pagination?: { limit: number; offset: number; total: number; count?: number };
}

/**
 * phase-fix-v2client-envelope-sweep — the type-level guard.
 *
 * `apiFetch` UNWRAPS the server's `{ ok, data }` envelope and resolves to the
 * `data` payload (see the bottom of this function). Declaring the *envelope*
 * as `T` is therefore always a lie: callers then write `res.data.…`, get
 * `undefined`, and throw a TypeError that a `.catch()` usually swallows —
 * leaving a screen silently empty.
 *
 * That defect shipped twice (`4bb79bf` Admin Forms crash; `e4daece` New
 * Submission wizard dead dropdowns, which broke submission creation for every
 * region). Both times the declared type lied and the test mocks repeated the
 * lie, so the suite stayed green.
 *
 * This type makes the lie a compile error: if `T` looks like an envelope,
 * `apiFetch` resolves to a branded error type that won't assign to it.
 * Opt out deliberately via the `fullEnvelope: true` / `raw: true` overloads.
 */
export type EnvelopeTypeError<Reason extends string> = {
  readonly __apiFetchEnvelopeError: Reason;
};

/**
 * True when `T` carries a top-level `data` key — i.e. it describes the wire
 * envelope rather than the payload. `{ ok, data }` and bare `{ data }` both
 * count; both are shapes apiFetch will have already unwrapped past.
 */
export type RejectEnvelope<T> = [T] extends [never]
  ? T
  : T extends { data: unknown }
    ? EnvelopeTypeError<'apiFetch unwraps { ok, data } — declare the UNWRAPPED payload type. Use { fullEnvelope: true } if you really need the envelope.'>
    : T;

// Overloads: `fullEnvelope` and `raw` are the two deliberate opt-outs, so they
// keep the plain `T`. Everything else goes through RejectEnvelope.
export async function apiFetch<T>(
  path: string,
  opts: RequestOptions & { fullEnvelope: true },
): Promise<T>;
export async function apiFetch<T = Response>(
  path: string,
  opts: RequestOptions & { raw: true },
): Promise<T>;
export async function apiFetch<T>(path: string, opts?: RequestOptions): Promise<RejectEnvelope<T>>;
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<unknown> {
  const url = `${env.VITE_API_BASE_URL}${path}`;
  const token = await tokenGetter();

  const headers: Record<string, string> = {
    // phase-dev-identity-and-user-switcher — the active dev identity rides on
    // every request in dev mode; empty object otherwise (and the backend only
    // reads the header when auth is disabled, so it is doubly inert deployed).
    ...devIdentityHeaders(),
    ...(opts.headers as Record<string, string>),
  };
  let body: BodyInit | undefined = opts.body;

  if (opts.json !== undefined) {
    body = JSON.stringify(opts.json);
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const init: RequestInit = { ...opts, headers };
  if (body !== undefined) init.body = body;

  const res = await fetch(url, init);

  if (!res.ok) {
    let bodyText = '';
    try {
      bodyText = await res.text();
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, bodyText, bodyText || res.statusText);
  }
  notifySubmissionChange(path, opts.method);
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as unknown as T;

  const json = (await res.json()) as Envelope<T>;
  // Phase dashboard-performance-fix — when fullEnvelope is set, return the
  // complete { ok, data, pagination } object so callers can access pagination.
  if (opts.fullEnvelope) return json as unknown as T;
  // Most endpoints return { ok, data } — unwrap. A few (validation
  // smoke-test) return { ok, results }; the caller passes the right T and
  // we hand back the parsed body whole when there's no `data` field.
  if (json && typeof json === 'object' && 'data' in json) {
    const unwrapped = json.data;
    assertNotDoubleWrapped(path, unwrapped);
    return unwrapped;
  }
  return json;
}

/**
 * phase-fix-v2client-envelope-sweep (§3.5c) — dev-only runtime tripwire.
 *
 * After unwrapping, the payload should be the domain object. If it STILL looks
 * like an envelope, either the route double-wrapped (`data: { ok, data }`) or
 * the caller is about to read one level too deep. Warn loudly in dev; silent
 * in production (never throw — a console warning must not break a screen).
 */
function assertNotDoubleWrapped(path: string, value: unknown): void {
  if (!import.meta.env.DEV) return;
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'ok' in value &&
    'data' in value
  ) {
    console.warn(
      `[apiFetch] ${path} resolved to a value that still looks like an envelope ` +
        `({ ok, data }). apiFetch already unwrapped once — the route may be ` +
        `double-wrapping, or the client type/call site may be reading too deep.`,
      value,
    );
  }
}

// ----- Entity types ----- //

// phase-ui-company-profiles-improvements (Item 4) — derived field-usage map
// served by GET /api/v1/company-profiles/field-usage. See the backend
// companyProfileFieldUsage.ts for the derivation (resolver + aliases +
// templates + gov-form mappings; nothing hand-maintained).
export interface CompanyProfileFieldConsumer {
  readonly kind: 'template' | 'govForm';
  readonly name: string;
  readonly detail: string;
  readonly required: boolean;
}
export interface CompanyProfileFieldUsage {
  readonly variables: readonly string[];
  readonly consumers: readonly CompanyProfileFieldConsumer[];
  /** phase-regional-contacts-in-generation — per-field role note (regional
   *  resolution / fallback role / honest "mapped but unconsumed"). */
  readonly note?: string;
}
export interface CompanyProfileFieldUsageMap {
  readonly fields: Readonly<Record<string, CompanyProfileFieldUsage>>;
  readonly coverage: string;
}

// Phase 5.X-company-profile-backend — JSON blob shape for regional
// agency contact cards. Server-side the field is `nvarchar(max)`; the
// GET response hydrates the JSON for the frontend (see
// `hydrateAgencyContacts` in src/routes/company-profiles.ts). Writes
// from the frontend may send either an object (preferred) or the
// already-serialized JSON string — the server validates both.
export interface AgencyContactBlob {
  contactName?: string | null;
  contactTitle?: string | null;
  contactEmail?: string | null;
  // Per-agency extras live alongside the contact triple. The shape is
  // intentionally loose: every leaf must be string/number/boolean/null
  // but the keys vary per agency (EMA may carry qppvName, MHRA may
  // carry ukRpName, etc).
  [k: string]: string | number | boolean | null | undefined;
}

// phase-profile-contact-rows — §MODEL. Contacts and signatories are rows, each
// tagged with a Function; Region null means the row applies to all regions.
export const CONTACT_FUNCTIONS = [
  'general',
  'labelling',
  'cmc',
  'clinical',
  'nonclinical',
  'safety',
  'regops',
  'other',
] as const;

export type ContactFunction = (typeof CONTACT_FUNCTIONS)[number];

/** Display labels (§MODEL). 'other' shows its FunctionOther text instead. */
export const FUNCTION_LABELS: Readonly<Record<ContactFunction, string>> = {
  general: 'General / Primary',
  labelling: 'Labelling',
  cmc: 'CMC / Quality',
  clinical: 'Clinical',
  nonclinical: 'Nonclinical',
  safety: 'Safety / Pharmacovigilance',
  regops: 'Regulatory Operations',
  other: 'Other',
};

/** Which child resource — the two share one shape and one CRUD surface. */
export type ProfileContactKind = 'contacts' | 'signatories';

export interface ProfileContactRow {
  Id: string;
  TenantId: string;
  CompanyProfileId: string;
  /** null = applies to all regions. Stored as the canonical region code. */
  Region: string | null;
  Function: ContactFunction;
  FunctionOther: string | null;
  IsPrimary: boolean;
  Name: string | null;
  Title: string | null;
  Email: string | null;
  /** Contacts only; signatories have no phone. */
  Phone?: string | null;
  CreatedAt?: string;
  UpdatedAt?: string | null;
}

export interface ProfileContactWrite {
  Region?: string | null;
  Function?: ContactFunction;
  FunctionOther?: string | null;
  IsPrimary?: boolean;
  Name?: string | null;
  Title?: string | null;
  Email?: string | null;
  Phone?: string | null;
}

/**
 * phase-entity-model-stage-1-delete-safety — what still uses a profile.
 * Computed server-side by ONE evaluator so the delete dialog and the delete
 * refusal can never disagree about the count.
 */
export interface CompanyProfileUsage {
  readonly submissionCount: number;
  readonly submissions: ReadonlyArray<{ id: string; label: string }>;
  readonly truncated: boolean;
}

export interface CompanyProfile {
  Id: string;
  CompanyName: string;
  ProfileName?: string | null;
  Agency?: string | null;
  // FDA-specific identifiers (Phase 3.1 backend canonical field list).
  DunsNumber?: string | null;
  FeiNumber?: string | null;
  FdaEsgTradingPartnerId?: string | null;
  EsgAccount?: string | null;
  UsFdaLicenseNumber?: string | null;
  // Address fields.
  Street?: string | null;
  City?: string | null;
  Province?: string | null;
  PostalCode?: string | null;
  Country?: string | null;
  Phone?: string | null;
  Fax?: string | null;
  ContactName?: string | null;
  ContactTitle?: string | null;
  // phase-fix-signatory-and-submission-date — person authorized to sign
  // regulatory correspondence generated from this profile.
  AuthorizedSignatoryName?: string | null;
  AuthorizedSignatoryTitle?: string | null;
  ContactEmail?: string | null;
  // Phase 5.X-company-profile-backend — identity extras
  TradingName?: string | null;
  Website?: string | null;
  SecondaryContactName?: string | null;
  SecondaryContactTitle?: string | null;
  SecondaryContactEmail?: string | null;
  // Phase 5.X-company-profile-backend — per-agency JSON contact blobs
  FdaAgencyContact?: AgencyContactBlob | null;
  HcAgencyContact?: AgencyContactBlob | null;
  EmaAgencyContact?: AgencyContactBlob | null;
  MhraAgencyContact?: AgencyContactBlob | null;
  PmdaAgencyContact?: AgencyContactBlob | null;
  TgaAgencyContact?: AgencyContactBlob | null;
  // Phase 5.X-company-profile-backend — sister-flat agency scalars
  EmaCode?: string | null;
  Eudravigilance?: string | null;
  UkLicence?: string | null;
  // Phase 7.1 — MHRA/PMDA/TGA international representative fields.
  MhraUkRepresentative?: string | null;
  PmdaLocalAgentName?: string | null;
  PmdaLocalAgentAddress?: string | null;
  PmdaLocalAgentPhone?: string | null;
  PmdaLocalAgentEmail?: string | null;
  TgaName?: string | null;
  TgaSponsorNumber?: string | null;
  // Phase 5.X-company-profile-backend — Compliance Settings tab.
  // Nullable boolean: null means "no org override; consult Settings".
  RequireDualControlDelete?: boolean | null;
  RequirePublishAttestation?: boolean | null;
  RequireReauthAtPublish?: boolean | null;
  // Phase FIX-company-profile-ux-and-assets — per-profile branding
  CompanyLogoUrl?: string | null;
  LetterheadTemplateUrl?: string | null;
  LetterheadMode?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  [k: string]: unknown;
}

// Phase 5.11 — PromptLibrary entry shape mirrors src/routes/prompts.ts.
// Phase 5.X-prompt-library-expansion added Status/Type/Version.
export interface PromptLibraryEntry {
  Id: string;
  Region: string;
  Module: number | null;
  Section: string | null;
  ScopeType: string | null;
  ScopeId: string | null;
  Title: string;
  PromptText: string;
  CreatedAt: string;
  CreatedBy: string;
  UpdatedAt: string | null;
  UpdatedBy: string | null;
  TenantId: string | null;
  // Phase 5.X-prompt-library-expansion.
  Status: string; // 'active' | 'draft'
  Type: string | null;
  Version: number;
  // Phase 8.3 — Layer 2 classification prompt columns.
  PromptType: string; // 'Template' | 'Classification'
  IsActive: boolean;
  FilenamePatterns: string | null; // JSON-encoded glob array
  GeneratingCompany: string | null;
  GeneratingCompanyLocation: string | null;
  DocumentType: string | null;
  ScanSection: string | null;
}

// Phase 8.4 — GeneratingCompanies entry shape
export interface GeneratingCompanyEntry {
  Id: string;
  TenantId: string | null;
  Name: string;
  NormalizedName: string;
  Location: string | null;
  InferredFrom: string;
  ConfidenceScore: number | null;
  Status: 'pending' | 'confirmed' | 'rejected';
  ReviewedBy: string | null;
  ReviewedAt: string | null;
  CreatedAt: string;
  /** Tracker row 8 — the scan job that inferred it, and who ran that scan. */
  ScanLearnJobId?: string | null;
  CreatedBy?: string | null;
  /** Tracker row 12 — its Classification prompts, and how many are active (what confirm/reject will change). */
  PromptCount?: number;
  ActivePromptCount?: number;
}

/** GET /scan-learn/:jobId — tracker rows 8–9. Applied counts are what was written (0 when failed). */
export interface ScanLearnJob {
  Id: string;
  TenantId: string | null;
  Source: string;
  FolderPath: string | null;
  Status: 'queued' | 'running' | 'complete' | 'failed' | string;
  FilesFound: number | null;
  FilesProcessed: number | null;
  FilesSkipped?: number | null;
  PromptsCreated: number | null;
  PromptsUpdated: number | null;
  CompaniesInferred: number | null;
  RulesAdded?: number | null;
  RulesUpdated?: number | null;
  /** JSON: [{file, error}] when failed; [{file, skipped}] when complete with skipped files. */
  Errors: string | null;
  FailureReason?: string | null;
  CreatedBy?: string | null;
  StartedAt: string | null;
  CompletedAt: string | null;
  /** Set only when the job's learning was applied. */
  AppliedAt?: string | null;
  CreatedAt: string;
  /** Tracker row 11 — when/by whom a finished job's outcome was shown; `outcomeAcknowledged` = it was. */
  OutcomeAcknowledgedAt?: string | null;
  OutcomeAcknowledgedBy?: string | null;
  outcomeAcknowledged?: boolean;
}

/** PATCH /generating-companies/:id — tracker row 12: the company, and what changed in its prompts. */
export interface GeneratingCompanyChangeResult {
  message: string;
  company: GeneratingCompanyEntry & {
    changes: { promptsActivated: number; promptsDeactivated: number; promptsRenamed: number };
  };
}

export interface PromptTestResponse {
  /** Tracker row 6 — the free-form test is a raw model call, NOT the classifier. */
  mode?: 'free-form';
  label?: string;
  promptId: string;
  input: string;
  output: string;
  latencyMs: number;
  model: string;
  tokensIn: number | null;
  tokensOut: number | null;
}

/**
 * POST /prompt-library/test — the classifier test (tracker row 6). The server
 * runs THE classification pipeline production runs (Layers 1–3). `basis` says
 * how far that equals a real classify: 'submission' (exactly, in the chosen
 * submission), 'equivalent' (no submission chosen, and none sets its own value
 * for the classification settings) or 'assumed' (no submission chosen, and some
 * do — then productionWouldRun is null).
 */
export type SettingSourceName = 'submission' | 'user' | 'org' | 'default' | 'unavailable';
export interface PromptClassifierTestResult {
  /** The answer (Layer 2's or Layer 3's). null only when the Layer 3 call failed — see layer3. */
  classification: string | null;
  confidence: number | null;
  rationale: string | null;
  decidedBy: 2 | 3;
  layer3: { status: 'not-needed' } | { status: 'ran'; classification: string; confidence: number } | { status: 'failed'; error: string | null };
  basis: 'submission' | 'equivalent' | 'assumed';
  basisText: string;
  productionWouldRun: string | null;
  settings: {
    promptLibraryClassificationEnabled: { value: boolean; source: SettingSourceName; orgLocked: boolean };
    learningFromCorrections: { value: boolean; source: SettingSourceName; orgLocked: boolean };
  };
  submissionOverrides: { count: number; submissionIds: string[] } | null;
  regionSource: 'submission' | 'assumed';
  promptLibraryEnabled: boolean;
  layer1:
    | { status: 'proposal'; section?: string; pattern: string; supportCount?: number; familyDisplayName?: string }
    | { status: 'ambiguous'; pattern: string; candidates?: Array<{ section: string; familyDisplayName: string; effectiveSupport: number }> }
    | { status: 'none'; pattern: string }
    | { status: 'error'; pattern: string; error: string };
  layer2: {
    status: 'matched' | 'no-match';
    reason: string | null;
    why: string;
    selectedPrompt: { promptId: string; title: string; score: number; matchedPatterns: string[]; region: string } | null;
    modelAnswer: { classification: string; confidence: number | null } | null;
  } | null;
  promptUnderTest: { id: string; title: string; isActive: boolean; evaluatedAsActive: boolean; wasSelected: boolean };
  region: string;
  submissionId: string | null;
  excerpt: { sentChars: number; totalChars: number; truncated: boolean; maxChars: number };
  model: string;
}
export interface PromptScanLearnResponse {
  scanned: number;
  learned: number;
}

/** POST /prompts/bulk-activate — what the server really changed (src/lib/prompts/bulkActivate.ts). */
export interface PromptBulkActivateResult {
  batchId: string;
  requested: number;
  updated: number;
  alreadyActive: number;
  notFound: number;
  notFoundIds: string[];
  changed: Array<{ id: string; title: string; from: string; fromIsActive: boolean; updatedAt: string }>;
}

/** POST /prompts/bulk-activate/:batchId/revert. */
export interface PromptBulkActivateRevertResult {
  batchId: string;
  reverted: number;
  skipped: number;
  revertedPrompts: Array<{ id: string; title: string; to: string }>;
  skippedPrompts: Array<{ id: string; title: string; reason: 'not-found' | 'changed-since' }>;
}

// phase-classification-stage-1-filename-layer
export interface ClassificationEvidenceEntry {
  familyCode: string;
  familyDisplayName: string;
  section: string;
  pattern: string;
  /** Human-confirmed documents ("confirmed matches") — what readyToPropose counts. */
  supportCount: number;
  effectiveSupport: number;
  /** Distinct folder-scanned files ("folder scan") — never counted toward readyToPropose. */
  scanSupportCount?: number;
  source: string;
  lastSeenAt: string;
  readyToPropose: boolean;
}
export interface ClassificationEvidenceListResponse {
  entries: ClassificationEvidenceEntry[];
}

// POST/PUT body — backend accepts the lowercased field names per the Phase
// 5.11 route's validator (region/module/section/scopeType/scopeId/title/
// promptText).
export interface PromptWriteBody {
  region: string;
  module?: number | null;
  section?: string | null;
  scopeType?: string | null;
  scopeId?: string | null;
  title: string;
  promptText: string;
  // Phase 5.X-prompt-library-expansion — optional free-form classifier
  // category ('classification', 'summary', etc.).
  type?: string | null;
  // Phase 8.3 — Layer 2 classification fields.
  promptType?: 'Template' | 'Classification';
  isActive?: boolean;
  filenamePatterns?: string | null;
  generatingCompany?: string | null;
  generatingCompanyLocation?: string | null;
  documentType?: string | null;
}

export interface PromptsListQuery {
  region?: string;
  module?: number;
  section?: string;
  scopeType?: string;
  scopeId?: string;
  page?: number;
  pageSize?: number;
  // Phase 8.3 — filter by prompt type.
  promptType?: 'Template' | 'Classification';
}

export interface PromptsListResponse {
  prompts: PromptLibraryEntry[];
  total: number;
  page: number;
  pageSize: number;
}

// Phase 5.14 — SpecFile shape mirrors the SpecFiles table (see
// migrations/20260518020000_create_spec_files_and_rbac.ts) and the response
// shape in src/routes/spec-files.ts. `Module` is stored as nvarchar(8) so
// it's a string (eCTD section codes like '3.2.s' aren't integers). FileSize
// is bigint server-side; tedious returns it as string when large enough, so
// the type accepts both.
export interface SpecFile {
  /** Managed versions change only through the tested update workflow. */
  SpecificationUpdateId?: string | null;
  Id: string;
  Region: string;
  Module: string | null;
  FileType: string;
  Title: string;
  Version: string | null;
  Description: string | null;
  StoragePath: string | null;
  FileName: string | null;
  FileSize: number | string | null;
  MimeType: string | null;
  Sha256: string | null;
  IsActive: boolean;
  CreatedAt: string;
  CreatedBy: string;
  UpdatedAt: string | null;
  UpdatedBy: string | null;
  TenantId: string | null;
  /** phase-spec-files-management — 1 when uploaded content is stored. */
  HasContent?: number | boolean;
  /** Availability and runtime use are independent server assessments. */
  ContentSource?: 'stored' | 'embedded' | 'unavailable';
  Support?: {
    status: 'reference-only' | 'validation-configured' | 'unavailable';
    message: string;
  };
  /** phase-spec-enforcement-wiring — multi-file sets + provenance. */
  CompanionOfId?: string | null;
  CompanionCount?: number;
  SourceUrl?: string | null;
  SourceFetchedAt?: string | null;
  AuthorityVersionLabel?: string | null;
  AlsoAppliesToRegions?: string | null;
  Companions?: SpecFile[];
}

export interface OfficialSpecPackage {
  id: string;
  title: string;
  authority: string;
  version: string;
  ectdVersion: '4.0';
  kind: string;
  sourceUrl: string;
  fetchedAt: string;
  sha256: string;
  byteLength: number;
  fileName: string;
  availability: 'available' | 'unavailable';
  support: { status: 'reference-only'; message: string };
  fileCount: number;
}

export interface OfficialSpecPackageFile {
  path: string;
  byteLength: number;
  sha256: string;
  mimeType: string;
  viewable: boolean;
}

export interface OfficialSpecPackageDetail {
  package: OfficialSpecPackage;
  files: OfficialSpecPackageFile[];
}

// POST/PUT body — backend's `pickWritable` accepts the lowercased field
// names per src/routes/spec-files.ts.
export interface SpecFileWriteBody {
  region: string;
  fileType: string;
  title: string;
  module?: string | null;
  version?: string | null;
  description?: string | null;
  storagePath?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  sha256?: string | null;
  isActive?: boolean;
}

export interface SpecFilesListQuery {
  region?: string;
  fileType?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
  /** Column to sort by. Must be one of the ALLOWED_SORT_COLUMNS on the backend. */
  sortBy?: string;
  /** Sort direction. Defaults to 'asc' when omitted. */
  sortOrder?: 'asc' | 'desc';
}

// List endpoint returns `{ ok, data: { specFiles, total, page, pageSize } }`.
// apiFetch unwraps `data`, so the caller sees the inner shape directly.
export interface SpecFilesListResponse {
  specFiles: SpecFile[];
  total: number;
  page: number;
  pageSize: number;
  /** T15-D3 — the server's canonical slots, resolved with the submission
   *  resolver's coverage rule. Absent → the page makes no slot claims. */
  slots?: SpecSlotView[];
  /** T15-D6 — the enforcement statement (server wording, one source) and the
   *  honest gap: active regional rows with no stored content are skipped. */
  enforcement?: {
    statement: string;
    activeWithoutContent: Array<{ Id: string; Title: string }>;
    contentGap: string | null;
  };
}

// T15-D3 — one canonical (region, file type) slot as the server resolved it.
export interface SpecSlotView {
  region: string;
  fileType: string;
  /** configured = own active row; covered = served by another region's row
   *  (AlsoAppliesToRegions); unresolved = nothing — new applications get no pin. */
  status: 'configured' | 'covered' | 'unresolved';
  spec: { Id: string; Title: string; Version: string | null; Region: string } | null;
  /** Server wording (one source): the title, "Covered by … (also X)", or the consequence. */
  message: string;
}

// T15-D8 — GET /api/v1/spec-files/resolve (unwrapped `data`).
export interface SpecFileResolution {
  compilerProfile?: import('./compilerProfiles').CompilerProfileCapability | null;
  region: string;
  regionCode: string | null;
  spec: {
    Id: string;
    Title: string;
    Version: string | null;
    Region: string;
    FileType: string;
  } | null;
  /** 'own' = the region's own file; 'shared' = covers it via AlsoAppliesToRegions. */
  coverage: 'own' | 'shared' | null;
  /** Set when the region resolves to nothing (no pin; regional validation skipped). */
  warning: string | null;
}

// Phase spec-file-upload-diff (TODO-071) — preview-diff request/response.
export interface SpecFileDiffRequest {
  fileName?: string;
  fileType?: string;
  fileContent: string;
  /** ID of the spec file the new one would supersede (looked up for currentVersion). */
  currentSpecFileId?: string | null;
  /** Optional inline copy of the current file's content — when present, used to compute removedSections / unchangedCount. */
  currentFileContent?: string | null;
}

// T15-D1 — the server's section comparison. When there is nothing to compare
// against (no version in force, or it has no stored bytes) `comparable` is
// false, `reason` says why and the lists are null — never "everything new".
export interface SpecFileComparison {
  comparable: boolean;
  reason: string | null;
  newSections: string[] | null;
  removedSections: string[] | null;
  unchangedCount: number | null;
  sectionsInFile: number;
}

export interface SpecFileDiffResult extends SpecFileComparison {
  currentVersion: string;
  newVersion: string;
}

// T15-D1 — GET /api/v1/spec-files/:id/review (unwrapped `data`).
export interface SpecFileReview {
  candidate: {
    Id: string;
    Title: string;
    Version: string | null;
    IsActive: boolean;
    Region?: string;
    FileType?: string;
  };
  active: { Id: string; Title: string; Version: string | null } | null;
  /** Send back as `reviewedActiveId` when activating (null = slot had none). */
  reviewedActiveId: string | null;
  diff: SpecFileComparison;
  /** T15-D2 — what activating it changes (tenant-scoped counts; server wording). */
  impact?: {
    pinnedToInForce: number;
    pinnedToCandidate?: number;
    unpinnedInProgress: number;
    unpinnedExamples: string[];
    /**
     * PR #33 — unpinned in-progress submissions whose fallback CHANGES but not
     * to the candidate: the activation retires a version they resolved to and
     * they fall back to a DIFFERENT active row. Mirrors SpecImpact on the
     * server (src/lib/spec-files/specImpact.ts).
     */
    fallbackChangedAway: number;
    fallbackChangedAwayExamples: string[];
    coveredRegions: string[];
    removedSections: string[] | null;
    statement: string;
  };
}

// Phase 5.19 — detailed health endpoint response. Mirrors the
// `DetailedHealthBody` shape on src/routes/health.ts. The `apiFetch`
// envelope-unwrap returns the inner `data` shape directly, so this is
// the on-the-wire shape MINUS the `{ ok, data }` wrapper.
export interface HealthDetailed {
  /** "healthy" when DB is up; "degraded" when DB SELECT 1 throws. */
  status: 'healthy' | 'degraded';
  /** From package.json's VERSION constant at boot. */
  version: string;
  /** From process.env.GIT_COMMIT_SHA. "unknown" until Phase 5.19.1 lands. */
  buildSha: string;
  /** ISO timestamp captured once per app boot. */
  startedAt: string;
  /** ISO timestamp at the moment the handler responded. */
  now: string;
  db: {
    status: 'up' | 'down';
    /** Round-trip latency for SELECT 1, in ms. null when down. */
    latencyMs: number | null;
  };
  migrations: {
    /** Count of completed migrations. null on listing failure. */
    count: number | null;
    /** Name of the most-recently-applied migration. null on failure. */
    latest: string | null;
  };
  // TODO-100 — additional probes (ESG / Auth / Storage).
  esg: {
    status: 'up' | 'down' | 'unknown';
    latencyMs: number | null;
    mode: 'stub' | 'real';
  };
  auth: {
    status: 'up' | 'down' | 'unknown';
    latencyMs: number | null;
    enabled: boolean;
  };
  storage: {
    status: 'up' | 'down' | 'not_configured' | 'unknown';
    driver: 'local' | 'azure-blob' | 's3' | null;
  };
  /** Test #9 — the AI assistant's configuration (key present / rejected / absent). */
  assistant: { status: 'configured' | 'not_configured' | 'key_invalid' };
  /** End-to-end handler latency, in ms. */
  responseTimeMs: number;
}

// Phase 5.9 — Settings 3-tier (org / user / submission) API.
//
// The backend exposes TWO distinct response shapes:
//
//  1. MERGED endpoints (`/api/v1/settings`, `/api/v1/settings/submission/:id`)
//     return a *map* keyed by setting Key:
//       { settings: { [Key]: { Value, Source, OrgLocked } } }
//     The `Source` indicator tells the UI which scope provided the final
//     effective value after merge (`org` → user → submission overlay).
//
//  2. SCOPE endpoints (`/api/v1/settings/scope/{org,user,submission/:id}`)
//     return a flat *array* of raw rows scoped to that single tier:
//       { rows: [{ Key, Value, OrgLocked, UpdatedAt, UpdatedBy }, …] }
//     `Value` is JSON-decoded server-side, so callers receive `unknown`
//     rather than a JSON-encoded string.
//
// Permissions (verified by reading src/routes/settings.ts):
//  - Read endpoints: auth-only (no `Settings:read` RBAC resource exists).
//    Submission-scoped reads additionally require `Submissions:read`.
//  - Org PUT/DELETE: `Users:manage` (admin only).
//  - User-self PUT/DELETE: auth-only.
//  - Submission PUT/DELETE: `Submissions:write` on the submission.
//
// Reused settings scope union — same string literals the backend uses.
export type SettingsScope = 'org' | 'user' | 'submission';

// One row's effective value after 3-tier merge — surfaced by the
// `listAll` / `getSubmission` endpoints.
export interface MergedSetting {
  Value: unknown;
  Source: SettingsScope;
  OrgLocked: boolean;
}

export type MergedSettingsMap = Record<string, MergedSetting>;

// apiFetch unwraps `data`, so this is the inner shape callers see.
export interface MergedSettingsResponse {
  settings: MergedSettingsMap;
}

// One raw row from a SCOPE endpoint — the per-tier shape (no merge).
export interface SettingsScopeRow {
  Key: string;
  Value: unknown; // server-side JSON-decoded; could be string / number / bool / object
  OrgLocked: boolean;
  UpdatedAt: string; // ISO 8601
  UpdatedBy: string | null;
}

export interface ScopeSettingsResponse {
  rows: SettingsScopeRow[];
}

// PUT body — `orgLocked` is only meaningful when writing the org row.
// The user/submission PUT endpoints accept the same body shape but
// ignore `orgLocked`. `value` is whatever JSON-serialisable JS value the
// caller wants stored; the backend re-JSON-encodes for storage.
export interface SettingsWriteBody {
  key: string;
  value: unknown;
  orgLocked?: boolean;
}

// Phase Wave D-2.a — ValidationRule shape mirrors the ValidationRules table
// (Phase 2.1 schema). The backend GET endpoint is pending; the page handles
// the failure gracefully until it lands.
export interface ValidationRule {
  Id: string;
  RuleId: string;
  Tier: string;
  Agencies: string;
  Category?: string | null;
  Severity: string;
  Description: string;
  Reference?: string | null;
  Region?: string | null;
  /**
   * phase-validation-rules-screen — 'agency' (a regulator/ICH mandates it)
   * vs 'internal' (AutoeCTD's own quality check). Recorded per rule; absent
   * only against a pre-migration backend.
   */
  Origin?: 'agency' | 'internal' | string;
  IsActive: boolean;
  IsBuiltin: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  Notes?: string | null;
  Implementation?: string | null;
  /**
   * Validation Rules rows 1–2 — what the registered implementation actually
   * does (derived server-side from the code, never from this row's text):
   * 'enforced' (can fail at error severity → blocks compile), 'advisory'
   * (can fail at warning/info → shown, never blocks), 'not-yet-enforced'
   * (no fail outcome at all). Absent only against an older backend.
   */
  Enforcement?: ValidationRuleEnforcement;
  /** false when no implementation is registered for this catalogue row. */
  Implemented?: boolean;
}

export type ValidationRuleEnforcement = 'enforced' | 'advisory' | 'not-yet-enforced';

/** Which rule set the catalogue + implementations form, and when the catalogue last changed. */
export interface ValidationRuleSet {
  version: string;
  lastChangedAt: string | null;
  ruleCount: number;
}

export type Region =
  | 'FDA'
  | 'EMA'
  | 'MHRA'
  | 'Health Canada'
  | 'PMDA'
  | 'TGA'
  | 'Swissmedic';

/**
 * claude-regional-fixes (issue 3) — product/company metadata shared by every
 * region in a parallel group. Deliberately NO per-market identity fields.
 */
export interface ParallelSharedMetadata {
  DrugName: string;
  InnName?: string;
  Applicant?: string;
  Indication?: string;
  TherapeuticArea?: string;
  CompanyProfileId?: string;
  EctdVersion?: string;
  DevelopmentPhase?: string;
}

/**
 * claude-regional-fixes (issue 3) — one region's explicit setup in a parallel
 * group: its own application type, activity code and market identifier. The
 * caller supplies the identifier appropriate to the region, chosen by the
 * canonical evaluator (NdaNumber for FDA + PMDA/Swissmedic, DossierId for HC +
 * TGA, EuApplicationUuid for EMA/MHRA). No field is copied across regions.
 */
export interface ParallelRegionSetup {
  SupplementEffectiveDateType?: string;
  FdaActivityStartSequence?: string;
  Region: string;
  SubmissionType: string;
  SubmissionTypeCode: string;
  SubmissionSubTypeCode?: string;
  SequenceNumber?: string;
  SequenceDescription?: string;
  ParentSubmissionId?: string;
  ApplicationPathway?: string;
  NdaNumber?: string;
  DossierId?: string;
  EuApplicationUuid?: string;
  ControlNumber?: string;
}

export interface Submission {
  /** Precise server revision used only for confirmed assistant corrections. */
  AssistantRevision?: string;
  /** Canonical application/parallel group returned by grouped dashboard queries. */
  DashboardGroupKey?: string;
  FdaActivityStartSequence?: string | null;
  Id: string;
  CompanyProfileId?: string | null;
  DrugName: string;
  Region: Region;
  SubmissionType?: string | null;
  Status: string;
  SequenceNumber: string;
  ControlNumber?: string | null;
  NdaNumber?: string | null;
  ApplicationPathway?: string | null;
  EuApplicationUuid?: string | null;
  DossierId?: string | null;
  EctdVersion?: string | null;
  ParentSubmissionId?: string | null;
  /** Phase 7.4 — HC eCTD Table 4 controlled vocabulary term. */
  SequenceDescription?: string | null;
  /** Phase 7.6 — links this submission to a parallel multi-region group. */
  ParallelGroupId?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  /** Phase 5.X-dashboard-totaldocs — total non-deleted, non-superseded documents. */
  TotalDocs?: number | null;
  /** Phase KB-submission-types-v3 — catalog code for the submission type. */
  SubmissionTypeCode?: string | null;
  /** Phase KB-submission-types-v3 — catalog code for the submission sub-type. */
  SubmissionSubTypeCode?: string | null;
  /** Legacy FDA activity labels require explicit review against the corrected catalog. */
  FdaActivityMetadataNeedsReview?: boolean;
  /** Phase KB-submission-types-v3 — supplement effective date type code. */
  SupplementEffectiveDateType?: string | null;
  // Phase 5.X-esg-poller-backend + esg-transmission-status-retry +
  // TODO-104 — ESG transmission + ACK receipt fields. All nullable:
  // a Draft submission has them all null; transmission flips
  // EsgSubmittedAt + EsgEnvelopeId; the poller flips EsgAckReceivedAt
  // + EsgAckTimestamp when the agency returns an MDN; EsgStaleAt fires
  // after the cron's overdue threshold. EsgAckPayload is the raw MDN.
  EsgSubmittedAt?: string | null;
  EsgAckReceivedAt?: string | null;
  EsgAckTimestamp?: string | null;
  EsgStaleAt?: string | null;
  EsgEnvelopeId?: string | null;
  EsgTransmissionSizeBytes?: number | null;
  EsgLastError?: string | null;
  EsgRetryCount?: number | null;
  EsgLastPolledAt?: string | null;
  EsgAckPayload?: string | null;
  /** Phase submission-type-fixes (TODO-062) — promoted from bracket-notation. */
  Indication?: string | null;
  /** Phase submission-type-fixes (TODO-062) — promoted from bracket-notation. */
  Applicant?: string | null;
  // claude-regional-fixes (issue 4) — stable, application-level metadata a
  // supplement inherits from its source sequence. GET /submissions/:id returns
  // the full row, so these are available for the New Supplement prefill.
  InnName?: string | null;
  TherapeuticArea?: string | null;
  DevelopmentPhase?: string | null;
  /** Phase submission-archive — true when archived (hidden from default dashboard). */
  IsArchived?: boolean;
  ArchivedAt?: string | null;
  ArchivedByUserId?: string | null;
  // phase-submission-spec-pinning — which regional specification version this
  // application files under. Set at creation (region's active default, or
  // inherited from the application family); Title/Version are enriched by
  // GET /submissions/:id for display.
  SpecFileId?: string | null;
  SpecFileTitle?: string | null;
  SpecFileVersion?: string | null;
  /**
   * phase-missing-profile-visibility — the same company-profile-link facts
   * generation uses (getCompanyProfileLinkStatus), enriched onto
   * `GET /submissions/:id` so the header flag and generate-control gating
   * can show the problem before the user presses generate. Only present on
   * the single-submission GET (list/dashboard rows don't carry it).
   */
  CompanyProfileLinkStatus?: CompanyProfileLinkStatus;
  /**
   * phase-admin-unlock — the currently-active unlock grant(s), if any. Drives
   * the mandatory "unlocked, by whom, and why" banner. Empty array when the
   * sequence is not currently unlocked (the common case, including "not
   * locked at all"). Only present on the single-submission GET.
   */
  ActiveUnlocks?: UnlockGrant[];
  [k: string]: unknown;
}

/** phase-admin-unlock — one row from `SubmissionUnlocks`. */
export type UnlockKind = 'edit' | 'renumber';
export interface UnlockGrant {
  Id: string;
  TenantId: string;
  SubmissionId: string;
  Kind: UnlockKind;
  Reason: string;
  UnlockedByUserId: string;
  UnlockedByEmail: string | null;
  UnlockedAt: string;
  RelockedAt: string | null;
  RelockedByUserId: string | null;
  RelockedByEmail: string | null;
  RelockKind: 'manual' | 'auto-compile' | null;
  RelockReason: string | null;
}

/** Mirrors src/lib/forms/companyProfileResolver.ts CompanyProfileLinkStatus. */
export type CompanyProfileLinkStatus =
  | { state: 'ok'; profileId: string }
  | { state: 'missing'; profileId: string }
  | {
      state: 'unlinked';
      fallbackProfileId: string | null;
      fallbackProfileName: string | null;
      profileCount: number;
    };

// Shared progress evidence mirrors computeStatusSnapshot() in
// src/routes/submissions.ts. Seven lifecycle stages are derived from
// document, validation, package, and agency evidence.
export type PipelineStageState = 'pending' | 'active' | 'done' | 'error';

export interface PipelineStage {
  readonly code?: 'unavailable' | 'partial' | 'stale' | 'legacy' | 'errors';
  readonly key: string;
  readonly label: string;
  readonly state: PipelineStageState;
  readonly detail?: string;
}

export interface StatusSnapshot {
  readonly submissionId: string;
  readonly status: string;
  readonly stages: readonly PipelineStage[];
  readonly currentStageKey: string;
  readonly updatedAt: string;
}

// Phase 5.X-pdf-form-field-extraction
export interface ExtractedField {
  name: string;
  value: string | null;
  fieldType: 'text' | 'checkbox' | 'dropdown' | 'radio' | 'optionlist' | 'signature' | 'unknown';
}

/**
 * Phase sibling-selector-toolbar (TODO-058 Part B) — one row returned by
 * GET /api/v1/submissions/:id/siblings. SequenceNumber and DocumentCount
 * widen to `string` because mssql returns numeric scalars as strings over
 * the wire in some envelope paths.
 */
export interface SubmissionSibling {
  readonly Id: string;
  readonly SequenceNumber: number | string;
  readonly SequenceDescription: string | null;
  readonly Status: string;
  readonly DocumentCount: number | string;
  readonly CompiledAt: string | null;
  readonly IsCurrent: boolean;
}

export interface ExtractFieldsResponse {
  fields: ExtractedField[];
  totalCount: number;
  prefilledCount: number;
}

export interface HistoricalDocument extends Document {
  SubSequenceNumber?: string;
  SequenceNumber?: string | number;
  HistoryState: 'active' | 'replaced' | 'deleted' | 'provisional' | 'unknown';
  HistoryTrust: 'finalized' | 'provisional' | 'unavailable';
  Eligible: boolean;
  EligibilityReasons: string[];
  ReplacedByDocumentIds: string[];
  DeletedByDocumentIds: string[];
}

export interface PriorDocumentReview {
  ok: true;
  data: HistoricalDocument[];
  history: { status: 'complete' | 'incomplete'; reasons: string[] };
}

export interface RelatedDocumentMatch {
  document: Document & { SubSequenceNumber?: string; SequenceNumber?: string | number };
  relationship: 'exact-duplicate' | 'same-text' | 'similar-text' | 'related';
  evidence: string[];
  score?: number;
  state: string;
  trust: string;
  eligible: boolean;
  blockers: string[];
}

export interface RelatedDocumentReview {
  sourceDocumentId: string;
  sourceRevision: string;
  historyToken?: string;
  status: 'complete' | 'incomplete' | 'unavailable' | 'not-checked';
  reasons: string[];
  coverage: { total: number; compared: number; remaining: number; exactHashesChecked: number };
  nextOffset: number | null;
  matches: RelatedDocumentMatch[];
}

export interface Document {
  /** Explicit FDA form-type vocabulary code for a manually uploaded form. */
  FdaFormType?: string | null;
  Id: string;
  /** Engine-maintained revision serialized by Node's Buffer JSON contract. */
  RowVersion?: { type: 'Buffer'; data: number[] };
  SubmissionId: string;
  FileName: string;
  /**
   * Phase 5.X-v1-parity-polish Feature 1 — the as-uploaded filename,
   * preserved before any FDA-compliant auto-rename. Backfilled to equal
   * FileName for legacy rows. Toggle in DocumentsTabPanel decides which
   * to render.
   */
  OriginalFileName?: string | null;
  ModuleNumber?: string | null;
  LifecycleOperation: 'new' | 'replace' | 'append' | 'delete' | string;
  // phase-parity-document-operation-staging — the prior document a
  // replace/append/delete acts on. ModifiedFile is the eCTD reference
  // string; ReplacesDocumentId is the UUID of the prior-sequence document.
  ReplacesDocumentId?: string | null;
  IsSuperseded?: boolean;
  ModifiedFile?: string | null;
  Sha256Hash?: string | null;
  FileSize?: number | null;
  FileUploadedAt?: string | null;
  ConfidenceScore?: number | null;
  /** Phase 5.1 classification — eCTD module code (e.g. `m2.7.1`). */
  Classification?: string | null;
  /** Phase 5.1 — self-reported confidence, 0..1. */
  ClassificationConfidence?: number | null;
  /** Phase 5.1 — 1–3 sentence justification. */
  ClassificationRationale?: string | null;
  /**
   * phase-classification-model-selection — WHICH model produced the stored
   * classification, and its catalog tier. Null/absent on rows classified
   * before provenance was recorded; every surface renders that as an honest
   * "not recorded" rather than inventing a model.
   */
  ClassificationModel?: string | null;
  ClassificationModelTier?: 'fast' | 'balanced' | 'advanced' | null;
  /** Phase 5.1 — user email or id that triggered the most recent classify. */
  ClassifiedBy?: string | null;
  ClassifiedAt?: string | null;
  /** Phase 5.10a — timestamp the AI classification was user-confirmed. */
  ConfirmedAt?: string | null;
  /** Phase 5.10a — email or id of the user who confirmed. */
  ConfirmedBy?: string | null;
  /** TODO-074 — true when the classification was confirmed automatically
   * by the `autoConfirmHighConfidence` gate (vs a manual review). The
   * backend sets this together with `ConfirmedBy = 'auto-classifier'`
   * during the classify route's auto-confirm path. */
  AutoConfirmed?: boolean | null;
  /** Phase 5.4 — id of the template used to generate this document, if any. */
  GeneratedFromTemplateId?: string | null;
  /**
   * phase-form-template-versioning — the template version (single-track int)
   * that produced this generated document; null for uploads and
   * pre-versioning generations.
   */
  GeneratedFromTemplateVersion?: number | null;
  /** phase-classification-stage-0-fixes — 'generated' marks Form-Templates
   * output whose placement is template-assigned and never AI-classified. */
  ClassificationStatus?: string | null;
  /**
   * phase-classification-stage-1-filename-layer — the Layer 1 proposal
   * computed at classify time from filename evidence alone. Advisory ONLY —
   * never applied to `Classification`; a human reviews it before
   * confirming. `Layer1ProposalStatus` is `'proposal' | 'ambiguous' | null`
   * ('ambiguous' = two+ families matched, never a coin-flip; null = no
   * qualifying pattern, Layer 3 ran unchanged).
   */
  Layer1ProposalStatus?: 'proposal' | 'ambiguous' | null;
  Layer1ProposedFamily?: string | null;
  Layer1ProposedSection?: string | null;
  Layer1ProposalSupportCount?: number | null;
  /** Phase 5.X-document-handling-enhancements — PDF signature detection.
   * `'digital'` (cryptographic /Sig field present), `'wet'` (heuristic
   * image on last page), `'none'` (parseable but neither), or `null`
   * (never detected — non-PDF, no upload yet, or pre-feature row). */
  SignatureStatus?: 'digital' | 'wet' | 'none' | string | null;
  SignatureDetectedAt?: string | null;
  /** Phase 5.X-document-handling-enhancements (Feature 3) — when this
   * doc is a cover letter (CtdSectionCode === '1.2'), captures the
   * sibling doc-list at the moment the cover letter was last marked
   * "in sync" (uploaded / replaced / explicitly re-snapped). JSON-
   * encoded string of an array of sibling Document.Id values. NULL
   * for non-cover-letter docs and for cover letters never snapshotted. */
  CoverLetterDocListSnapshot?: string | null;
  CoverLetterGeneratedAt?: string | null;
  /** Phase 7.2 — eCTD CTD section code (e.g. '1.2' for cover letter). */
  CtdSectionCode?: string | null;
  /** Phase 7.2 — document title (display name). */
  Title?: string | null;
  /** Phase compiler-document-lock (TODO-079) — when true the document
   * is pinned: reclassification, moves, deletions are all rejected with
   * 409 DocumentLocked. Toggle via PATCH /api/v1/documents/:id/lock. */
  IsLocked?: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  [k: string]: unknown;
}

// ----- Phase settings-auto-populate-on-confirm (TODO-074) ----- //

/**
 * Full envelope returned by POST /api/v1/documents/:id/confirm.
 * When `autoPopulateOnConfirm` is on and the document's CTD section
 * code maps to a known form, the backend includes `autoCreatedForm`
 * alongside the document data.
 */
export interface ConfirmResult {
  readonly ok: boolean;
  readonly data: Document;
  /** Present only when a SubmissionForms row was auto-created. */
  readonly autoCreatedForm?: {
    readonly displayName: string;
    readonly formType: string;
  };
  /** True when autoSyncCoverLetter was on and cover-letter regeneration was triggered. */
  readonly coverLetterSynced?: boolean;
}

// ----- Phase 5.10e/f — bulk-confirm contract ----- //

export interface BulkConfirmRequest {
  readonly documentIds: readonly string[];
}

export interface BulkConfirmResponse {
  readonly submissionId: string;
  readonly confirmed: readonly string[];
  readonly skipped: readonly string[];
  /** Phase 5.X-bulk-confirm — 'all' for auto-discover, 'explicit' for caller-supplied ids. */
  readonly mode?: 'all' | 'explicit';
}

/** Shape of the structured `data` payload returned with a 400
 *  `BulkConfirmRejected` response. `parseApiErrorBody` in
 *  `DocumentsTabPanel` extracts this from the raw error body. */
export interface BulkConfirmRejectedDetail {
  readonly notFound?: readonly string[];
  readonly mismatchedSubmission?: readonly string[];
  readonly notClassified?: readonly string[];
}

// ----- Phase 5.4 — document templates ----- //

export interface TemplateVariable {
  name: string;
  description?: string;
  required: boolean;
  defaultValue?: string;
}

/**
 * Tracker row 10 — GET /api/v1/document-templates/:id/usage. A submission
 * uses a template when one of its forms points at it; `activeCount` counts the
 * using submissions that are not locked (the ones a delete would break).
 */
export interface TemplateUsage {
  submissions: Array<{ id: string; name: string; status: string | null; isLocked: boolean }>;
  activeCount: number;
  totalCount: number;
}

export interface DocumentTemplate {
  Id: string;
  Name: string;
  Description: string | null;
  TargetModule: string;
  Body: string;
  Variables: TemplateVariable[];
  /** `null` for global templates; tenant id otherwise. */
  TenantId: string | null;
  CreatedBy: string;
  CreatedAt: string;
  UpdatedAt: string;
  // Metadata columns added by the KB-seed migration
  // (20260525015138_seed_document_templates_from_kb). The backend
  // (src/routes/document-templates.ts `rowToApi`) already returns these;
  // they were just not surfaced on the web type. Phase
  // phase-form-templates-ui-fixes (FT-1/2) consumes Region for the new
  // Region filter + column.
  Region?: string | null;
  TemplateType?: string | null;
  Language?: string | null;
  IsActive?: boolean;
  /**
   * phase-form-template-versioning — the template's live version number
   * (D-059 single-track: displayed as "N.0"). Null only for rows that
   * somehow predate the forward-semantics migration.
   */
  CurrentVersion?: number | null;
  /** Non-null when the template is soft-deleted (visible via includeDeleted). */
  DeletedAt?: string | null;
}

export interface TemplateVersion {
  Id: string;
  TemplateId: string;
  /** Null = version of a global (built-in) template: shared history. */
  TenantId: string | null;
  VersionNumber: number;
  Content: string;
  ChangeNote: string | null;
  ChangedByUserId: string | null;
  ChangedByUserName: string | null;
  CreatedAt: string;
}

/** Form Templates row 8 — one side of a version diff (`id: 'current'` = the live body). */
export interface TemplateVersionSide {
  id: string;
  versionNumber: number | null;
  isCurrent: boolean;
  changeNote: string | null;
  changedBy: string | null;
  createdAt: string | null;
}

/** Row 8 — GET /document-templates/:id/versions/:a/diff/:b. */
export interface TemplateVersionDiff {
  templateId: string;
  leftVersion: TemplateVersionSide;
  rightVersion: TemplateVersionSide;
  changedFields: Array<'Body'>;
  body: {
    added: number;
    removed: number;
    unchanged: number;
    hunks: Array<{ type: 'add' | 'remove' | 'same'; lines: string[] }>;
  };
  variables: { addedInRight: string[]; removedInRight: string[]; changed: string[] };
  identical: boolean;
}

export interface GenerationResult {
  documentId: string;
  fileName: string;
  storageKey: string;
  generation: {
    generatedContent: string;
    variablesUsed: Record<string, string>;
    variablesDefaulted: string[];
    usage: { inputTokens: number; outputTokens: number } | null;
    model: string;
  };
  /**
   * Phase phase-settings-link-injection — whether the backend applied
   * the autoInjectLinks pass, and whether the blue-underline style was
   * used. Optional for back-compat with older API responses.
   */
  linkInjection?: {
    applied: boolean;
    blueUnderline: boolean;
  };
}

export interface GenerateRequest {
  submissionId: string;
  variables: Record<string, string>;
  submissionContext?: Record<string, unknown>;
}

// Phase 5.X-v1-parity-polish Feature 3 — diff response mirrors
// src/routes/document-templates.ts diffTemplates().
export interface DocumentTemplateDiff {
  leftId: string;
  rightId: string;
  changedFields: ReadonlyArray<'Name' | 'Description' | 'TargetModule' | 'Body'>;
  variables: {
    addedInRight: TemplateVariable[];
    removedInRight: TemplateVariable[];
    changed: Array<{ name: string; left: TemplateVariable; right: TemplateVariable }>;
  };
  left: DocumentTemplate;
  right: DocumentTemplate;
}

export interface DocumentTemplatesListQuery {
  targetModule?: string;
  limit?: number;
  offset?: number;
  /** phase-form-template-versioning — include soft-deleted templates ("Show deleted"). */
  includeDeleted?: boolean;
}

// phase-template-variable-provenance — where each template variable's value
// comes from at generation time. Mirrors the classifier in
// src/lib/templates/variableProvenance.ts (the tagged-sentinel walk).
export type ProvenanceCategory =
  | 'profile'
  | 'regionalContact'
  | 'submission'
  | 'computed'
  // phase-template-variable-bindings — a user-configured binding decided this
  // variable's source, overriding the default chain.
  | 'bound'
  | 'none';

export interface VariableProvenance {
  token: string;
  category: ProvenanceCategory;
  source: string | null;
  description: string;
  /** phase-template-variable-bindings — a binding is in force ("modified from default"). */
  isBound?: boolean;
  /** Set when the binding points at something that no longer resolves. */
  bindingIssue?: string;
  /** Form Templates row 6 — will this variable actually get a value? */
  status?: BindingStatus;
  reason?: string;
  /** The value for the selected profile (resolved only). */
  resolvedPreview?: string;
  /** Form Templates row 7 — every source, in precedence order, the one in effect marked. */
  sources?: VariableSourceCandidate[];
}

export interface VariableSourceCandidate {
  rank: number;
  kind: 'binding' | 'exact' | 'alias' | 'default';
  description: string;
  inEffect: boolean;
  note: string;
  value?: string;
}

/**
 * Form Templates row 6 — resolved | empty (no value for this profile) | broken
 * (the source no longer exists) | unchecked (nothing selected to check against).
 */
export type BindingStatus = 'resolved' | 'empty' | 'broken' | 'unchecked';

export interface BindingHealthApi {
  status: BindingStatus;
  reason: string;
  resolvedPreview?: string;
}

/** Row 6 — a bound variable that produced nothing at generation, and why. */
export interface GenerateBindingIssue {
  variableName: string;
  status: BindingStatus;
  issue: string;
}

export interface TemplateBindingHealth {
  /** Lower-case template id. */
  templateId: string;
  needsAttention: number;
  broken: Array<{ variableName: string; reason: string }>;
}

export interface TemplateVariableProvenance {
  templateId: string;
  name: string;
  /** The true token count from the walk (authoritative over the stored count). */
  tokenCount: number;
  /** The template's stored Variables.length, or null if unparseable. */
  storedCount: number | null;
  region: string | null;
  isGlobal: boolean;
  /** phase-template-variable-bindings — built-ins are bound via tenant overrides. */
  isBuiltIn?: boolean;
  variables: VariableProvenance[];
}

// ─── phase-template-variable-bindings ───────────────────────────────────────

export type BindingSourceKind =
  | 'PROFILE_FIELD'
  | 'CONTACT'
  | 'SIGNATORY'
  | 'SUBMISSION_FIELD'
  | 'LITERAL';

// §CONTACT MODEL — `ContactFunction` is declared ONCE, above, by
// phase-profile-contact-rows (alongside CONTACT_FUNCTIONS and FUNCTION_LABELS).
// phase-template-variable-bindings originally repeated the identical union here;
// the two landed in parallel and collided on the same identifier. Deduped to the
// single §MODEL definition — the values were byte-identical, so nothing changed.

export type ContactRegionMode = 'template' | 'submission' | 'explicit';
export type ContactAttribute = 'name' | 'title' | 'email' | 'phone';

export interface VariableBindingApi {
  id: string;
  templateId: string;
  variableName: string;
  sourceKind: BindingSourceKind;
  sourceField: string | null;
  contactRegionMode: ContactRegionMode | null;
  contactRegion: string | null;
  contactFunction: ContactFunction | null;
  contactAttribute: ContactAttribute | null;
  literalValue: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Row 6 — its source can no longer exist (known without any profile). */
  isBroken?: boolean;
  brokenReason?: string | null;
}

export interface TemplateBindingsResponse {
  templateId: string;
  isBuiltIn: boolean;
  bindings: VariableBindingApi[];
}

export interface BindableField {
  key: string;
  label: string;
  from: string;
}

/** Derived server-side from the REAL resolvers — never a hand-written list. */
export interface BindableSources {
  profileFields: BindableField[];
  submissionFields: BindableField[];
  contactFunctions: ContactFunction[];
  contactAttributes: ContactAttribute[];
  contactRegionModes: ContactRegionMode[];
  sourceKinds: BindingSourceKind[];
}

/** The body of a set-binding request. */
export interface SetVariableBindingBody {
  sourceKind: BindingSourceKind;
  sourceField?: string;
  contactRegionMode?: ContactRegionMode;
  contactRegion?: string;
  contactFunction?: ContactFunction;
  contactAttribute?: ContactAttribute;
  literalValue?: string;
}

// ----- Phase 5.1 — classification ----- //

export interface ClassificationResult {
  Id: string;
  Classification: string;
  ClassificationConfidence: number;
  ClassificationRationale: string;
  ClassifiedBy: string;
  model: string;
  /**
   * phase-classification-model-selection — what actually ran, server-resolved.
   * `ClassificationModel` echoes `model`; `modelCapped` + `modelNote` are the
   * honest signal that the tenant's max-tier cap clamped the request down.
   */
  ClassificationModel?: string | null;
  ClassificationModelTier?: 'fast' | 'balanced' | 'advanced' | null;
  modelDisplayName?: string | null;
  modelCapped?: boolean;
  modelNote?: string | null;
}

// ----- Wave E.2 — Inspector right-pane response shapes ----- //

export interface DocumentLifecycleEntry {
  readonly sequenceNumber: string;
  readonly operation: string;
  readonly documentId: string;
  readonly fileName: string;
  readonly classifiedSection: string | null;
  readonly approvedAt: string | null;
  readonly approvedBy: string | null;
}

export interface DocumentLifecycle {
  readonly documentId: string;
  readonly submissionId: string;
  /**
   * `false` while V2 has no `LineageId` column on Documents. UI can use
   * this to render a "first occurrence in lifecycle" explainer.
   */
  readonly lineageTracked: boolean;
  readonly entries: readonly DocumentLifecycleEntry[];
}

export interface DocumentPlacementUpdate {
  readonly documentId: string;
  readonly oldSectionCode: string | null;
  readonly newSectionCode: string;
  readonly reason: string | null;
}

export interface DocumentClassificationApproval {
  readonly documentId: string;
  readonly approvedClassification: string;
  readonly isOverride: boolean;
  readonly reason: string | null;
  readonly approvedAt: string;
  readonly approvedBy: string;
}

export interface ValidationResult {
  ruleName: string;
  severity: 'error' | 'warning' | 'info';
  status: 'pass' | 'fail' | 'skip';
  message?: string;
  resultId?: string;
  details?: unknown;
}

/** POST /api/v1/admin/validation-smoke-test — always a dry run (T17-D10). */
export interface ValidationDiagnosticsRun {
  ok: boolean;
  dryRun: true;
  results: ValidationResult[];
}

// ----- Phase 5.8 — RBAC management surface ----- //

export interface UserSummary {
  Email: string;
  /**
   * phase-user-management-name-column — display name captured from the
   * identity provider at sign-in; null until first sign-in after capture
   * landed. Rendered as an em-dash when null.
   */
  Name: string | null;
  Roles: Array<{ Id: string; Name: string }>;
  AssignedAt: string;
  /** Phase user-invite-deactivate (TODO-103) — true when admin has soft-disabled this user. */
  IsDeactivated: boolean;
}

/** Phase user-invite-deactivate (TODO-101) — a pending invitation record. */
export interface UserInvitation {
  readonly id: string;
  readonly email: string;
  readonly role: { Id: string; Name: string };
  readonly invitedByUserId: string;
  readonly invitedAt: string;
  readonly expiresAt: string;
  readonly status: 'pending' | 'accepted' | 'cancelled';
  readonly inviteLink: string;
}

export interface InviteResult {
  readonly invitation: {
    readonly id: string;
    readonly email: string;
    readonly role: { Id: string; Name: string };
    readonly expiresAt: string;
  };
  readonly inviteLink: string;
}

export interface MeResponse {
  /** T17-D36 — the caller's id (the token's oid when signed in); optional for older backends. */
  UserId?: string;
  Email: string;
  Roles: Array<{ Id: string; Name: string; Description: string }>;
  Permissions: Array<{ Resource: string; Action: string }>;
  /**
   * T-06 R05 — server-side feature switches, carried on this existing
   * capability route rather than a new endpoint. Optional: an older backend
   * omits it, and the consumer treats a missing value as OFF, matching the
   * server default.
   */
  Features?: { invitesEnabled?: boolean };
}

// phase-dev-identity-and-user-switcher — the dev-only cast list (the backend
// 404s this route whenever auth is enabled).
export interface DevIdentitiesResponse {
  identities: Array<{ email: string; name: string; roleNames: string[]; tenantId: string }>;
  current: { email: string; name: string | null; switched: boolean };
  /** phase-dev-identity-seed-durability — true when the route's self-heal
   * verified/seeded the cast rows. Optional for older backends. */
  seeded?: boolean;
}

export interface Role {
  Id: string;
  Name: string;
  Description: string;
  Permissions: Array<{ Resource: string; Action: string }>;
  /** phase-roles-custom-role-ui — seeded roles are read-only; custom
   *  roles (created via POST /api/v1/roles) are editable. */
  IsSystemRole?: boolean;
  /** phase-fix-role-retire-not-delete — roles are never deleted, only
   *  retired. Retired roles grant no permissions, cannot be assigned,
   *  and are excluded from assignment pickers. */
  IsRetired?: boolean;
  RetiredAt?: string | null;
  /**
   * T-07 (D-27) — the active permissions this role grants that the CALLER
   * does not hold ("Resource.Action"), from the server's delegation evaluator.
   * Non-empty = the caller may not assign or revoke this role, and the dialog
   * says why. Absent (an older server): no hint — the role is offered and the
   * server's own refusal, which is readable, decides.
   */
  CallerMissing?: string[];
}

/** phase-roles-custom-role-ui — body shape for POST / PATCH /api/v1/roles. */
export interface RoleWriteBody {
  name: string;
  description?: string;
  permissions: Array<{ Resource: string; Action: string }>;
}

export interface UsersListQuery {
  page?: number;
  pageSize?: number;
  role?: string;
  search?: string;
}

export interface UsersListResponse {
  users: UserSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AuditLogEntry {
  Id: string;
  ActorId: string;
  ActorEmail: string | null;
  Action: string;
  TargetType: string | null;
  TargetId: string | null;
  Before: unknown;
  After: unknown;
  OccurredAt: string;
  IpAddress: string | null;
  UserAgent: string | null;
  RequestId: string | null;
}

export interface AuditLogListQuery {
  page?: number;
  pageSize?: number;
  // Phase 5.27 — new filter names. Backend resolves them with the legacy
  // aliases below; either set is accepted. Prefer the new names in new code.
  fromDate?: string;
  toDate?: string;
  actorEmail?: string;
  actorId?: string;
  targetType?: string;
  /**
   * Phase 5.X-audit-log-admin-viewer — exact-match filter on TargetId.
   * Combine with `targetType` for per-submission or per-document history.
   * Example: `targetType=Submissions&targetId=<submissionId>` → all audit
   * entries for that submission.
   */
  targetId?: string;
  action?: string;
  q?: string;
  /** `<column>:<dir>` — e.g. `OccurredAt:desc`. Default: `OccurredAt:desc`. */
  sort?: string;
  // Legacy filter aliases (pre-5.27). Kept for backward compatibility with
  // pre-5.27 callers; new code should use the names above.
  /** @deprecated use `fromDate` */ from?: string;
  /** @deprecated use `toDate` */ to?: string;
  /** @deprecated use `actorEmail` */ userEmail?: string;
  /** @deprecated use `targetType` */ resource?: string;
}

export interface AuditLogListResponse {
  entries: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  /** Phase 5.27 — echoes back the resolved sort (column:direction). */
  sort?: string;
  /** Phase 5.27a — true when the query plan UNION-ed into AuditLogArchive. */
  includesArchive?: boolean;
}

/** Phase 5.27 — distinct Action values present in the caller's tenant.
 *  Backs the frontend's Action filter dropdown. */
export interface AuditLogActionsResponse {
  actions: string[];
}

/** Phase 5.27a — request body for the manual retention-cleanup trigger.
 *  All fields are optional; the server falls back to the org-level
 *  `auditLog.activeRetentionDays` setting (default 7 years). */
export interface AuditLogRetentionCleanupBody {
  activeRetentionDays?: number;
  batchSize?: number;
  maxBatches?: number;
  dryRun?: boolean;
  /**
   * T-08 — the token the dry run issued. Required for a real run: the server
   * answers 409 StalePreview without it, so a destructive run is always the
   * one somebody was shown.
   */
  previewToken?: string;
}

/** Phase 5.27a — response from the manual retention-cleanup trigger. */
/**
 * phase-fix-v2client-envelope-sweep — this is the UNWRAPPED payload.
 * apiFetch strips the { ok, data } envelope; this type is what the client
 * method actually resolves to.
 */
export interface AuditLogRetentionCleanupResponse {
  moved: number;
  batches: number;
  cutoffDate: string;
  truncated: boolean;
  dryRun: boolean;
  activeRetentionDays: number;
  /** T-08 — present on a DRY RUN only: the authority to perform the real one. */
  previewToken?: string;
  /** How long that token lasts, so the dialog can say so if it needs to. */
  previewExpiresInMs?: number;
}

// ----- Phase 5.X-notifications-system — Notification API shapes ----- //

/** API shape for a single notification — matches what
 *  `src/routes/notifications.ts:rowToApi` emits. */
export interface NotificationApi {
  id: string;
  userId: string;
  tenantId: string | null;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  relatedEntityId: string | null;
  relatedEntityType: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsListQuery {
  /** When true, filter to unread (ReadAt IS NULL). */
  unread?: boolean;
  /** Page size, default 50 server-side, capped at 200. */
  limit?: number;
  /** ISO timestamp — return only rows older than this (cursor pagination). */
  before?: string;
}

export interface NotificationsListResponse {
  notifications: NotificationApi[];
  hasMore: boolean;
  total: number;
}

export interface NotificationsCountResponse {
  unread: number;
}

export interface NotificationsMarkReadResponse {
  notification: NotificationApi;
}

export interface NotificationsMarkAllReadResponse {
  updated: number;
}

export interface NotificationsClearResponse {
  deleted: number;
}

// ----- Wave E.4 — Validation results shapes ----- //
//
// Mirrors the JSON envelope returned by:
//   POST /api/v1/submissions/:id/validate
//   GET  /api/v1/submissions/:id/validation-results
//   GET  /api/v1/documents/:id/validation-results
//
// `apiFetch` unwraps `data`, so consumers see the inner shape directly.

export type ValidationStatus = 'pass' | 'fail' | 'skip';
export type ValidationSeverity = 'info' | 'warning' | 'error';

export interface ValidationResultEntry {
  readonly resultId: string;
  readonly ruleId: string;
  readonly ruleName: string;
  readonly severity: ValidationSeverity;
  readonly category: string | null;
  readonly description: string;
  readonly targetType: 'Submission' | 'Document' | string;
  readonly targetId: string;
  readonly status: ValidationStatus;
  readonly message: string | null;
  readonly details: unknown;
  readonly validatedAt: string;
  /**
   * phase-validation-rules-screen — 'agency' (regulator/ICH mandate) vs
   * 'internal' (AutoeCTD's own check). Null/absent from a pre-migration
   * backend; treated as agency for display.
   */
  readonly origin?: string | null;
}

/** Per-link detail emitted by the WAVE-E4-LINK-01 rule on `fail`. */
export interface LinkValidationBrokenLink {
  readonly text: string;
  readonly page: number;
  readonly reason: string;
  readonly targetType: 'page-anchor' | 'cross-document' | 'unknown' | string;
}
export interface LinkValidationDetails {
  readonly totalLinks: number;
  readonly externalLinks: number;
  readonly brokenLinks: ReadonlyArray<LinkValidationBrokenLink>;
  /**
   * navigation-foundation (coordinator finding 8) — the honest breakdown the
   * backend already computes. `totalLinks` is the number SEEN, not the number
   * PASSED: external links are out of scope (no network), remote (other-file)
   * targets and cycle/budget incompletes are reported but never asserted valid,
   * and `scanIncomplete` means the annotation walk was truncated. The UI must
   * derive "resolved" as total − external − remoteUnverified − incomplete −
   * broken rather than claiming all `totalLinks` passed.
   */
  readonly remoteUnverifiedLinks?: number;
  readonly incompleteLinks?: number;
  readonly scanIncomplete?: boolean;
  /**
   * phase-region-format-rules-and-docx-links — present on Word-native
   * results: how the links were extracted, whether this result is the
   * authoritative truth for the document or a labelled pre-conversion
   * (advisory) check, and the targets-vs-geometry caveat.
   */
  readonly method?: 'pdf' | 'docx';
  readonly check?: 'authoritative' | 'advisory';
  readonly caveat?: string;
}

/**
 * Wave E.5 — Stable rule-name identifiers for the 8 PDF/A compliance
 * rules. `WAVE-E5-PDFA-*` matches the `ValidationRule.name` /
 * `ValidationRules.RuleId` in the backend.
 */
export const PDFA_RULE_IDS = [
  'WAVE-E5-PDFA-VERSION',
  'WAVE-E5-PDFA-FONTS',
  'WAVE-E5-PDFA-SECURITY',
  'WAVE-E5-PDFA-JAVASCRIPT',
  'WAVE-E5-PDFA-MULTIMEDIA',
  'WAVE-E5-PDFA-BOOKMARKS',
  'WAVE-E5-PDFA-HYPERLINKS-RELATIVE',
  'WAVE-E5-PDFA-INITIAL-VIEW',
] as const;
export type PdfARuleId = (typeof PDFA_RULE_IDS)[number];

/**
 * Per-rule short labels used in the inspector's compliance summary.
 * Backend `ValidationRule.description` is verbose; the summary needs a
 * skim-friendly one-liner per row. Tooltip on hover surfaces the full
 * `ValidationResultEntry.description`.
 */
export const PDFA_RULE_LABELS: Readonly<Record<PdfARuleId, string>> = {
  'WAVE-E5-PDFA-VERSION': 'PDF version',
  'WAVE-E5-PDFA-FONTS': 'Fonts embedded',
  'WAVE-E5-PDFA-SECURITY': 'No password / restrictions',
  'WAVE-E5-PDFA-JAVASCRIPT': 'No JavaScript',
  'WAVE-E5-PDFA-MULTIMEDIA': 'No multimedia',
  'WAVE-E5-PDFA-BOOKMARKS': 'Bookmarks',
  'WAVE-E5-PDFA-HYPERLINKS-RELATIVE': 'Hyperlinks (relative paths)',
  'WAVE-E5-PDFA-INITIAL-VIEW': 'Initial view setting',
};

/**
 * T17-D43 - is the persisted validation a statement about the CURRENT submission? The same rule the publish
 * checklist uses: only `current` (a FULL run at least as new as the last document change) may be shown as
 * validated / passed. `detail` explains every other kind in the server's words.
 */
export interface ValidationCurrencyWire {
  readonly kind: 'never' | 'documents-only' | 'stale' | 'cannot-tell' | 'current';
  readonly detail?: string;
  readonly lastFullRunAt?: string;
}

export interface SubmissionValidationResultsResponse {
  readonly submissionId: string;
  readonly documentCount: number;
  readonly errorCount: number;
  readonly warningCount: number;
  readonly passCount: number;
  readonly skipCount: number;
  readonly documentsWithErrors: ReadonlyArray<string>;
  readonly documentsWithWarnings: ReadonlyArray<string>;
  readonly results: ReadonlyArray<ValidationResultEntry>;
  /** Optional: absent on an older backend and on the POST /validate response (a run that just finished is current). */
  readonly currency?: ValidationCurrencyWire;
}

export interface DocumentValidationResultsResponse {
  readonly documentId: string;
  readonly errorCount: number;
  readonly warningCount: number;
  readonly passCount: number;
  readonly skipCount: number;
  readonly documentsWithErrors: ReadonlyArray<string>;
  readonly documentsWithWarnings: ReadonlyArray<string>;
  readonly results: ReadonlyArray<ValidationResultEntry>;
}

// POST /api/v1/submissions/:id/validate returns the same shape as
// GET /validation-results. The alias makes the call-site reads (e.g.
// `Promise<SubmissionValidationRunResponse>`) self-documenting without
// declaring an empty interface.
export type SubmissionValidationRunResponse = SubmissionValidationResultsResponse;

// ----- Phase 5.X-background-validation-ui — validation-status endpoint ----- //

/** Response shape from GET /api/v1/submissions/:id/validation-status */
export interface ValidationStatusResponse {
  /**
   * `running` = in-flight (in-memory flag set); `complete` = a FULL validation run at least as new as the last
   * document change (T17-D43); `idle` = not validated (never run, per-document checks only, or out of date).
   */
  readonly status: 'idle' | 'running' | 'complete';
  /** T17-D43 - why `idle`, or `current` when `complete`; absent on an older backend. */
  readonly currency?: ValidationCurrencyWire;
  /** ISO-8601 timestamp of the most recent validation run, or null if never run. */
  readonly lastRunAt: string | null;
  /** Count of open (latest-per-rule-target) `fail` results with severity=error. */
  readonly errorCount: number;
  /** Count of open (latest-per-rule-target) `fail` results with severity=warning. */
  readonly warningCount: number;
  /** Count of open (latest-per-rule-target) `fail` results with severity=info. */
  readonly infoCount: number;
}

// ----- Phase 5.X-document-handling-enhancements (Features 2 + 3) ----- //

export interface AutoRenameSuggestion {
  readonly documentId: string;
  readonly currentName: string;
  readonly suggestedName: string;
  /** True when `suggestedName !== currentName`. Lets the UI gate the Apply button. */
  readonly changed: boolean;
  readonly reasoning: string;
}

export type CoverLetterSyncStatus =
  | { readonly status: 'not-applicable' }
  | { readonly status: 'never-generated'; readonly generatedAt: null }
  | { readonly status: 'in-sync'; readonly generatedAt: string | null }
  | {
      readonly status: 'out-of-sync';
      readonly generatedAt: string | null;
      readonly deltaDocs: {
        readonly added: ReadonlyArray<string>;
        readonly removed: ReadonlyArray<string>;
      };
    };

export interface CoverLetterSnapshotResult {
  readonly documentId: string;
  readonly siblingCount: number;
}

// Wave E.7 — Compiler types.

export interface CompilerCompileBody {
  /**
   * Operator-supplied reason for proceeding when validation errors are
   * present. Required when errors exist; the backend returns 422 otherwise.
   */
  overrideReason?: string;
  /** Whether to also emit a zipped wrapping of the folder. Default true. */
  generateZip?: boolean;
  /**
   * Phase 5.X-publish-governance — required. "test" prefixes the
   * sequence number with TEST- in the eCTD output; "final" is the
   * binding publish act. Backend returns 400 MissingPublishMode if
   * absent.
   */
  publishMode?: 'test' | 'final';
  /**
   * Phase 5.X-publish-governance — present when the org has
   * `reAuth = true`. The backend verifies the attestation token's
   * `iat` claim is within 60s of `now()` and the `oid` matches the
   * caller; rejects with 401 otherwise.
   */
  signatureAttestation?: {
    readonly signerOid: string;
    readonly signerEmail: string;
    readonly signatureTimestampUtc: string;
    readonly signatureMeaning: 'approval-for-publication';
    /** MSAL ID token from `acquireTokenPopup({prompt: 'login'})`. */
    readonly attestationToken: string;
  };
  /**
   * Phase 5.X-publish-governance — present when the submission's resolved
   * governance has `preP = true`. T-17 (T17-D14): exactly the manual item ids
   * served by GET /submissions/:id/prepublish-checklist, all `true`; the backend
   * returns 400 MissingChecklist / IncompleteChecklist (data.missing /
   * data.unchecked) / UnknownChecklistItem (data.unknown) otherwise.
   */
  checklistAcknowledgments?: Record<string, boolean>;
  /** True iff operator clicked "Publish anyway" on the unplaced-forms warning. */
  unplacedFormsAck?: boolean;
  /** True iff operator clicked "Publish anyway" on the sequence-order warning. */
  seqOrderAck?: boolean;
}

/** phase-partial-compile-with-exclusions — a document left out of a compile. */
export interface CompileExcludedDocument {
  documentId: string;
  fileName: string;
  module: string | null;
  reason: 'unclassified' | 'unconfirmed';
  /** D-038 — error failures on this excluded document, recorded on a draft package instead of blocking it. */
  validationErrors?: ReadonlyArray<{ ruleId: string; message: string | null }>;
}

export interface CompiledPackageSummary {
  id: string;
  submissionId: string;
  validationStatus: 'pass' | 'warnings' | 'errors' | 'overridden';
  totalLeafFiles: number;
  totalSizeBytes: number | null;
  overallSha256: string | null;
  generatedAt: string;
  generatedBy: string;
  hasZip: boolean;
  compileGateOverrideReason: string | null;
  /** phase-partial-compile-with-exclusions — completeness of this package. */
  isComplete?: boolean;
  excludedCount?: number;
  /** Present on the list endpoint (per-package exclusions). */
  excluded?: CompileExcludedDocument[];
  /**
   * phase-report-followups-batch (item 1) — which company profile's
   * identity is in this package's applicant fields. Null only when the
   * tenant has no company profiles at all.
   */
  appliedCompanyProfileId?: string | null;
  appliedCompanyProfileName?: string | null;
}

/**
 * phase-fix-v2client-envelope-sweep — this is the UNWRAPPED payload.
 * apiFetch strips the { ok, data } envelope; this type is what the client
 * method actually resolves to.
 */
export interface CompilerCompileResponse {
  package: CompiledPackageSummary;
  /** phase-partial-compile-with-exclusions — true when nothing was excluded. */
  isComplete?: boolean;
  /** The documents left out of this package (empty when complete). */
  excluded?: CompileExcludedDocument[];
  errorCount: number;
  warningCount: number;
  dtdValidation: {
    valid: boolean;
    level: 'dtd' | 'wellformed-only' | 'failed';
    note?: string;
  };
}

export interface CompilerCompileGateError {
  ok: false;
  error: 'ValidationGateBlock';
  message: string;
  errors: ReadonlyArray<{
    resultId: string;
    ruleId: string;
    ruleName: string;
    targetType: string;
    targetId: string;
    message: string | null;
  }>;
}

// Phase 5.X-settings-wiring-compliance — 422 body shape when the
// `blockPublishIfUnconfirmed` gate fires. Distinct from
// `CompilerCompileGateError` (validation rule failures) because the
// remedy is different: operator must confirm documents in the
// inspector, not write an override reason.
export interface CompilerPublishBlockedUnconfirmedError {
  ok: false;
  error: 'PUBLISH_BLOCKED_UNCONFIRMED';
  message: string;
  data: {
    unconfirmedCount: number;
    unconfirmedSample: ReadonlyArray<{
      id: string;
      fileName: string;
      ctdSectionCode: string | null;
    }>;
  };
}

/**
 * phase-fix-v2client-envelope-sweep — this is the UNWRAPPED payload.
 * apiFetch strips the { ok, data } envelope; this type is what the client
 * method actually resolves to.
 */
/** phase-partial-compile-with-exclusions — 422 body when a FINAL publish is
 *  refused because the compile would be partial (the RED LINE). */
export interface CompilerSubmitBlockedIncompleteError {
  ok: false;
  error: 'SUBMIT_BLOCKED_INCOMPLETE';
  message: string;
  data: {
    excludedCount: number;
    excluded: ReadonlyArray<CompileExcludedDocument>;
  };
}

/** phase-partial-compile-with-exclusions — 422 body when compile is attempted
 *  with zero confirmed documents. */
export interface CompilerNoConfirmedDocumentsError {
  ok: false;
  error: 'NoConfirmedDocuments';
  message: string;
  data?: { excludedCount: number };
}

/** phase-partial-compile-with-exclusions — the submit-readiness verdict
 *  (RED LINE) computed server-side for the client affordance. */
export interface CompileSubmitReadiness {
  submittable: boolean;
  isStale: boolean;
  reason: string | null;
  latestCompile: {
    id: string;
    generatedAt: string;
    isComplete: boolean;
    excludedCount: number;
    excluded: CompileExcludedDocument[];
    validationStatus: 'pass' | 'warnings' | 'errors' | 'overridden';
  } | null;
}

export interface CompilerListPackagesResponse {
  packages: CompiledPackageSummary[];
  /** phase-partial-compile-with-exclusions — current submit readiness. */
  readiness?: CompileSubmitReadiness;
}

// Phase 5.X-prepublish-checklist-autodetect — checklist items returned by
// GET /api/v1/submissions/:id/prepublish-checklist.
// T-17 (T17-D2/D14) — the endpoint now serves the WHOLE list (auto items
// evaluated server-side, then the manual items with their labels) plus the
// publish governance for THIS submission. The web declares no item ids.
export type PrePublishChecklistState = 'pass' | 'fail' | 'unknown' | 'manual';

export interface PrePublishChecklistItem {
  id: string;
  label: string;
  required: boolean;
  kind: 'manual' | 'auto';
  /** Auto: pass/fail (unknown once D3 lands). Manual: always 'manual'. */
  state: PrePublishChecklistState;
  /** Auto: the verdict; manual / unknown: null. */
  passed: boolean | null;
  detail?: string;
  failureMessage?: string | null;
}

export type PublishGovernanceSource = 'profile' | 'org' | 'default';

/** Publish governance resolved for ONE submission (own profile → org → false). */
export interface PublishGovernance {
  preP: boolean;
  reAuth: boolean;
  source: { preP: PublishGovernanceSource; reAuth: PublishGovernanceSource };
  companyProfileId: string | null;
}

/**
 * phase-fix-v2client-envelope-sweep — this is the UNWRAPPED payload.
 * apiFetch strips the { ok, data } envelope; this type is what the client
 * method actually resolves to.
 */
export interface PrePublishChecklistResponse {
  submissionId: string;
  governance: PublishGovernance;
  items: PrePublishChecklistItem[];
}

/** Phase 5.X-screen-review-part-c — response from POST …/test-inject. */
export interface TestInjectResponse {
  ok: boolean;
  /** Blob URL of the PDF preview with links injected; null when service not wired. */
  preview: string | null;
  message: string;
  documentId: string;
  submissionId: string;
}

// 422 body when the auto-detect checklist gate fires on POST /compile.
export interface CompilerPublishBlockedChecklistAutoError {
  ok: false;
  error: 'PUBLISH_BLOCKED_CHECKLIST_AUTO';
  message: string;
  data: {
    items: ReadonlyArray<{
      id: string;
      label: string;
      failureMessage: string | null;
    }>;
  };
}

/**
 * phase-fix-v2client-envelope-sweep — this is the UNWRAPPED payload.
 * apiFetch strips the { ok, data } envelope; this type is what the client
 * method actually resolves to.
 */
export interface CompilerFolderManifestResponse {
  packageId: string;
  submissionId: string;
  fileCount: number;
  /** T17-D7 — every expected file was found (an incomplete listing is a 409). */
  listingComplete: boolean;
  /**
   * Cloud drivers: a signed https `url`, `requiresAuth: false`. Local driver:
   * no url, `requiresAuth: true` — fetch via `v2.submissions.fetchCompileFile`.
   * `sizeBytes` is null when the driver did not report it.
   */
  files: Array<{ relativePath: string; sizeBytes: number | null; url?: string; requiresAuth: boolean }>;
}

/** T17-D8 — answer of `GET …/compile/:packageId/download?as=url`. */
export type CompileDownloadInfo =
  | { kind: 'signed-url'; url: string; fileName: string; sizeBytes: number | null; sha256: string | null }
  | { kind: 'stream'; url?: undefined; fileName: string; sizeBytes: number | null; sha256: string | null };

function isHttpsUrl(raw: unknown): raw is string {
  if (typeof raw !== 'string') return false;
  try {
    return new URL(raw).protocol === 'https:';
  } catch {
    return false;
  }
}


// ----- API surface ----- //

// ----- Phase 5.X-company-assets-letterhead — Company asset types ----- //

export interface CompanyAsset {
  id: string;
  tenantId: string | null;
  assetType: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  storageKey: string;
  uploadedByUserId: string;
  uploadedAt: string;
  isActive: boolean;
}

// phase-regional-letterhead-templates (TODO-077) — per-region letterhead
// variants stored in CompanyProfileLetterheads. The single
// `LetterheadTemplateUrl` column on CompanyProfile remains the "Default"
// fallback; entries here override it for specific regions.
export interface RegionalLetterhead {
  Id: string;
  CompanyProfileId: string;
  Region: string;
  LetterheadUrl: string;
  Label: string | null;
  CreatedAt?: string;
  /** phase-company-profile-rows-7-9 — this row's file is the profile default. */
  IsDefault?: boolean;
  /** An uploaded file (servable), as opposed to an external URL. */
  IsStoredFile?: boolean;
  /** Tracker row 14 — present when this write moved the profile version. */
  profileVersion?: ProfileVersionMove;
}

/**
 * Tracker row 14 — what a versioned save carries: the UpdatedAt it was made
 * from, and (optional) the values that were loaded, so a refusal can name
 * exactly the fields someone else changed.
 */
export interface SaveConcurrency {
  expectedUpdatedAt: string;
  baseline?: Record<string, unknown>;
}

/** Tracker row 14 — how a branding write moved the profile version. */
export interface ProfileVersionMove {
  previous: string | null;
  current: string | null;
}

/**
 * phase-company-profile-rows-7-9 (Row 9) — which company-profile letterhead a
 * generated cover letter used, or why none was.
 */
export interface CoverLetterLetterheadReport {
  profileMode: string;
  source: 'region' | 'default' | 'none' | null;
  region: string | null;
  label: string | null;
  applied: boolean;
  reason: string | null;
}

/**
 * phase-company-profile-rows-7-9 (Row 7) — what on a submission needs an
 * authorized signatory, and whether its company profile has one. The same
 * evaluator the generate routes enforce (422 MissingSignatory).
 */
export interface SignatoryRequirements {
  companyProfileId: string | null;
  profileName: string | null;
  region: string | null;
  signatory: { name: string; title: string } | null;
  coverLetterRequiresSignatory: boolean;
  forms: Array<{ formId: string; requiresSignatory: boolean }>;
}

export interface CompanyAssetsListResponse {
  /** Currently active asset for each type, keyed by `assetType`. */
  active: Record<string, CompanyAsset>;
  /** Inactive (historical) assets, newest first. */
  historical: CompanyAsset[];
}

// Phase dashboard-attention-surface (TODO-024) — shapes for the Dashboard
// "Needs your attention" aggregate. Mirror of `AttentionItem` in
// `src/routes/dashboard.ts`.
export type AttentionType =
  | 'pending_classification'
  | 'validation_errors'
  | 'awaiting_confirmation'
  | 'action_required';

export type AttentionSeverity = 'error' | 'warning' | 'info';

export interface AttentionItem {
  submissionId: string;
  ndaNumber: string;
  drugName: string;
  sequenceNumber: string;
  region: string;
  type: AttentionType;
  message: string;
  count: number;
  actionLabel: string;
  actionUrl: string;
  severity: AttentionSeverity;
}


// ---- phase-a2-assistant-panel — AI Intelligence Center ---------------------
/** Legacy tier override values (still accepted for back-compat). */
export type AssistantModelOverride = 'fast' | 'balanced' | 'advanced';

// phase-assistant-model-catalog — the picker + Auto router consume THIS
// server-driven catalog, not a frontend constant. An override may be a tier
// string (legacy) OR a catalog model id (e.g. 'claude-fable-5').
export type AssistantModelTier = 'fast' | 'balanced' | 'advanced';

export interface AssistantCatalogModel {
  modelId: string;
  displayName: string;
  tier: AssistantModelTier;
  preferredForTier: boolean;
}

export interface AssistantModelCatalogResponse {
  models: AssistantCatalogModel[];
  /** Tenant cap (D-033) — advanced picks above this are clamped server-side. */
  maxTier: AssistantModelTier;
}

/** Admin catalog entry — full pricing + provenance for Settings → Assistant. */
export interface AssistantAdminCatalogModel extends AssistantCatalogModel {
  inPerMTok: number;
  outPerMTok: number;
  enabled: boolean;
  sortOrder: number;
  pricingSource: string;
}

export interface AssistantAdminCatalogResponse {
  models: AssistantAdminCatalogModel[];
  isDefault: boolean;
  settingKey: string;
}

// phase-a5 — admin Assistant Activity view.
export interface AssistantActivityQuery {
  page?: number;
  pageSize?: number;
  sort?: string;
  q?: string;
  userEmail?: string;
  scope?: string;
  role?: string;
  fromDate?: string;
  toDate?: string;
}

export interface AssistantActivityEntry {
  Id: string;
  ConversationId: string;
  Role: 'user' | 'assistant';
  Content: string;
  Model: string | null;
  RoutedClass: string | null;
  Overridden: boolean;
  TokensIn: number | null;
  TokensOut: number | null;
  CreatedAt: string;
  UserEmail: string;
  Scope: 'submission' | 'global' | 'explanatory';
  SubmissionId: string | null;
}

export interface AssistantActivityResponse {
  entries: AssistantActivityEntry[];
  total: number;
  page: number;
  pageSize: number;
  sort: string;
  totals: {
    answers: number;
    tokensIn: number;
    tokensOut: number;
    estimatedCostUsd: number;
    /** phase-assistant-model-catalog — answers whose model left the catalog. */
    unpricedAnswers?: number;
    /** Provenance of the pricing figures behind estimatedCostUsd. */
    pricingSource?: string | null;
    /**
     * phase-classification-model-selection — per-source breakdown. The
     * headline `estimatedCostUsd` above is now the COMBINED figure across
     * these sources; this splits it so classification spend is attributed
     * rather than invisible. Optional so an older server still renders.
     */
    bySource?: {
      assistant: ModelSpendBreakdown;
      classification: ModelSpendBreakdown;
    };
  };
}

/** phase-classification-model-selection — one source's slice of model spend. */
export interface ModelSpendBreakdown {
  calls: number;
  tokensIn: number;
  tokensOut: number;
  estimatedCostUsd: number;
  unpriced: number;
}

/**
 * phase-classification-model-selection — the classification model selector's
 * catalog. Server-resolved from the SAME admin catalog the assistant uses;
 * above-cap models are omitted, so the selector never offers a choice the
 * server would clamp.
 */
export interface ClassificationModelCatalog {
  models: Array<{
    modelId: string;
    displayName: string;
    tier: 'fast' | 'balanced' | 'advanced';
  }>;
  maxTier: 'fast' | 'balanced' | 'advanced';
  /** What runs when the user picks nothing. */
  effectiveModelId: string;
  effectiveDisplayName: string;
  effectiveSource: 'request' | 'tenant-default' | 'catalog-default';
}

export interface AssistantRetentionPurgeResult {
  purgedConversations: number;
  purgedMessages: number;
  cutoffDate: string | null;
  retentionDays: number | 'unlimited';
  dryRun: boolean;
  unlimited: boolean;
}

export interface AssistantConversation {
  Id: string;
  Title: string | null;
  Scope: 'submission' | 'explanatory' | 'global';
  SubmissionId: string | null;
  /**
   * phase-a4 — per-conversation model override; null = Auto routing.
   * phase-assistant-model-catalog — may hold a catalog model id (e.g.
   * 'claude-fable-5') as well as a legacy tier string.
   */
  ModelOverride: string | null;
  CreatedAt: string;
  UpdatedAt: string | null;
  /** List endpoint only (phase-fix-assistant-conversation-labels-and-replay
   * Fix 1) — the submission's label fields; null when the submission was
   * deleted/hidden, or for non-submission scopes. */
  SubmissionDrugName?: string | null;
  SubmissionSequenceNumber?: string | null;
}

export interface AssistantMessage {
  Id: string;
  Role: 'user' | 'assistant';
  Content: string;
  Model: string | null;
  CreatedAt: string;
}

export interface AssistantCitation {
  sourceId: string;
  label: string;
  deepLink: string;
}

export type AssistantStreamEvent =
  | { type: 'token'; delta: string }
  | {
      type: 'done';
      model: string;
      modelTier: string;
      modelDisplay: string;
      /** phase-a4 — '· Recommended' vs '· Your selection' footer label. */
      modelSelectionLabel: 'Recommended' | 'Your selection';
      routedClass: string;
      recommendedTier: string;
      overridden: boolean;
      capped: boolean;
      /** Honest one-liner (cap / risky downgrade), or null. */
      routingNote: string | null;
      citations: AssistantCitation[];
      excluded: Array<{ key: string; missingPermission: string }>;
      footer: string;
    }
  | { type: 'error'; error: string; reason: string; message: string };

/**
 * Test #9 — GET /api/v1/assistant/status. `configured:false` carries the reason
 * the panel shows as a banner before the user types; "key invalid" is the
 * server's cached last provider rejection (5 minutes).
 */
export interface AssistantStatus {
  configured: boolean;
  reason?: 'AssistantNotConfigured' | 'AssistantKeyInvalid';
}

export function documentRevisionHeaders(revision: Document['RowVersion']): Record<string, string> {
  if (revision?.type !== 'Buffer' || revision.data.length !== 8 ||
      !revision.data.every((n) => Number.isInteger(n) && n >= 0 && n <= 255)) {
    throw new Error('Reload this document before saving: its revision is unavailable.');
  }
  return { 'If-Match': `"${revision.data.map((n) => n.toString(16).padStart(2, '0')).join('')}"` };
}

export const v2 = {
  assistant: {
    getStatus: (): Promise<AssistantStatus> => apiFetch('/api/v1/assistant/status'),
    createConversation: (data: {
      scope: 'submission' | 'explanatory' | 'global';
      submissionId?: string;
    }): Promise<AssistantConversation> =>
      apiFetch('/api/v1/assistant/conversations', { method: 'POST', json: data }),
    listConversations: (): Promise<AssistantConversation[]> =>
      apiFetch('/api/v1/assistant/conversations'),
    getMessages: (
      id: string,
    ): Promise<{ conversation: AssistantConversation; messages: AssistantMessage[] }> =>
      apiFetch(`/api/v1/assistant/conversations/${id}/messages`),
    // phase-a5 — admin activity view + retention purge.
    adminActivity: (
      query: AssistantActivityQuery = {},
    ): Promise<AssistantActivityResponse> => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && `${v}`.length > 0) params.set(k, `${v}`);
      }
      const qs = params.toString();
      return apiFetch(`/api/v1/admin/assistant/activity${qs ? `?${qs}` : ''}`);
    },
    runRetentionPurge: (body: {
      dryRun?: boolean;
      retentionDays?: number | 'unlimited';
    } = {}): Promise<AssistantRetentionPurgeResult> =>
      apiFetch('/api/v1/admin/assistant/retention-purge', { method: 'POST', json: body }),
    // phase-a4 — set/clear the per-conversation model override ('auto' clears).
    // phase-assistant-model-catalog — value may be a catalog model id too.
    setModelOverride: (
      id: string,
      modelOverride: string,
    ): Promise<AssistantConversation> =>
      apiFetch(`/api/v1/assistant/conversations/${id}`, {
        method: 'PATCH',
        json: { modelOverride },
      }),
    // phase-assistant-model-catalog — the picker's source of truth.
    getModelCatalog: (): Promise<AssistantModelCatalogResponse> =>
      apiFetch('/api/v1/assistant/model-catalog'),
    // Admin: the full catalog (enabled + disabled) with pricing.
    getAdminModelCatalog: (): Promise<AssistantAdminCatalogResponse> =>
      apiFetch('/api/v1/admin/assistant/model-catalog'),
    // Admin: persist an edited catalog (validated server-side).
    putAdminModelCatalog: (
      models: AssistantAdminCatalogModel[],
    ): Promise<{ models: AssistantAdminCatalogModel[] }> =>
      apiFetch('/api/v1/admin/assistant/model-catalog', {
        method: 'PUT',
        json: { models },
      }),
    // SSE ask — fetch + ReadableStream (EventSource cannot
    // carry the MSAL Authorization header). Yields typed frames.
    ask: async function* (
      id: string,
      content: string,
      signal: globalThis.AbortSignal,
    ): AsyncGenerator<AssistantStreamEvent, void, void> {
      const token = await tokenGetter();
      const headers = rawFetchHeaders(token, {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      });
      const res = await fetch(
        `${env.VITE_API_BASE_URL}/api/v1/assistant/conversations/${id}/messages`,
        { method: 'POST', headers, body: JSON.stringify({ content }), signal },
      );
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new ApiError(res.status, body, body || res.statusText);
      }
      if (!res.body) return;
      const reader = res.body.getReader();
      const decoder = new globalThis.TextDecoder();
      let buffer = '';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';
          for (const rawFrame of frames) {
            const lines = rawFrame.split('\n');
            const eventLine = lines.find((l) => l.startsWith('event: '));
            const dataLine = lines.find((l) => l.startsWith('data: '));
            if (!eventLine || !dataLine) continue;
            const type = eventLine.slice(7).trim();
            try {
              const data = JSON.parse(dataLine.slice(6)) as Record<string, unknown>;
              yield { type, ...data } as AssistantStreamEvent;
            } catch {
              // Malformed frame — drop and keep reading.
            }
          }
        }
      } finally {
        try {
          reader.releaseLock();
        } catch {
          /* already released */
        }
      }
    },
  },
  companyProfiles: {
    list: (): Promise<CompanyProfile[]> => apiFetch('/api/v1/company-profiles'),
    // phase-entity-model-stage-1-delete-safety — deletion is soft; these two
    // make recovery reachable (it was a database-only operation before).
    listDeleted: (): Promise<CompanyProfile[]> =>
      apiFetch('/api/v1/company-profiles?deleted=true'),
    restore: (id: string): Promise<CompanyProfile> =>
      apiFetch(`/api/v1/company-profiles/${id}/restore`, { method: 'POST' }),
    /** What still links to this profile — shown BEFORE a delete is confirmed. */
    usage: (id: string): Promise<CompanyProfileUsage> =>
      apiFetch(`/api/v1/company-profiles/${id}/usage`),
    get: (id: string): Promise<CompanyProfile> => apiFetch(`/api/v1/company-profiles/${id}`),
    // phase-profile-contact-rows — contacts and signatories are ROWS with a
    // Function dimension. Same CRUD shape for both; `kind` picks the resource.
    //
    // Each call branches into two LITERAL paths rather than interpolating
    // `kind`. scripts/check-v2client-routes.ts resolves paths statically, so an
    // interpolated segment would read as `:any` and the client↔route contract
    // would go unchecked for these five endpoints.
    contactRows: {
      list: (profileId: string, kind: ProfileContactKind): Promise<ProfileContactRow[]> =>
        kind === 'contacts'
          ? apiFetch(`/api/v1/company-profiles/${profileId}/contacts`)
          : apiFetch(`/api/v1/company-profiles/${profileId}/signatories`),
      create: (
        profileId: string,
        kind: ProfileContactKind,
        data: ProfileContactWrite,
      ): Promise<ProfileContactRow> =>
        kind === 'contacts'
          ? apiFetch(`/api/v1/company-profiles/${profileId}/contacts`, {
              method: 'POST',
              json: data,
            })
          : apiFetch(`/api/v1/company-profiles/${profileId}/signatories`, {
              method: 'POST',
              json: data,
            }),
      // Tracker row 14 — carries the row version it was made from
      // (`UpdatedAt ?? CreatedAt` of the row as loaded); a stale save is refused
      // with 409 ProfileRowChanged.
      update: (
        profileId: string,
        kind: ProfileContactKind,
        id: string,
        data: ProfileContactWrite,
        concurrency: SaveConcurrency,
      ): Promise<ProfileContactRow> =>
        kind === 'contacts'
          ? apiFetch(`/api/v1/company-profiles/${profileId}/contacts/${id}`, {
              method: 'PUT',
              json: { ...data, ...concurrency },
            })
          : apiFetch(`/api/v1/company-profiles/${profileId}/signatories/${id}`, {
              method: 'PUT',
              json: { ...data, ...concurrency },
            }),
      remove: (profileId: string, kind: ProfileContactKind, id: string): Promise<void> =>
        kind === 'contacts'
          ? apiFetch(`/api/v1/company-profiles/${profileId}/contacts/${id}`, { method: 'DELETE' })
          : apiFetch(`/api/v1/company-profiles/${profileId}/signatories/${id}`, {
              method: 'DELETE',
            }),
      setPrimary: (
        profileId: string,
        kind: ProfileContactKind,
        id: string,
      ): Promise<ProfileContactRow> =>
        kind === 'contacts'
          ? apiFetch(`/api/v1/company-profiles/${profileId}/contacts/${id}/set-primary`, {
              method: 'POST',
            })
          : apiFetch(`/api/v1/company-profiles/${profileId}/signatories/${id}/set-primary`, {
              method: 'POST',
            }),
    },
    // phase-ui-company-profiles-improvements (Item 4) — derived field-usage
    // map for the ⓘ tooltips (which forms/templates read each field).
    fieldUsage: (): Promise<CompanyProfileFieldUsageMap> =>
      apiFetch('/api/v1/company-profiles/field-usage'),
    // (Item 2) — direct URL for the stored branding asset (img src / iframe).
    brandingUrl: (id: string, kind: 'logo' | 'letterhead'): string =>
      `/api/v1/company-profiles/${id}/branding/${kind}`,
    // (Item 5) — direct URL for the starter letterhead .docx download.
    letterheadStarterTemplateUrl: (profileId?: string): string =>
      `/api/v1/company-profiles/letterhead-starter-template${profileId ? `?profileId=${encodeURIComponent(profileId)}` : ''}`,
    create: (data: Partial<CompanyProfile>): Promise<CompanyProfile> =>
      apiFetch('/api/v1/company-profiles', { method: 'POST', json: data }),
    // Backend uses PATCH for partial updates (see src/routes/company-profiles.ts).
    // Tracker row 14 — every save carries the version it was made from
    // (`expectedUpdatedAt` = the UpdatedAt that was loaded) and, for an honest
    // "what changed" on a conflict, the values that were loaded (`baseline`).
    // A stale save is refused with 409 ProfileChanged — see lib/concurrency.ts.
    update: (
      id: string,
      data: Partial<CompanyProfile>,
      concurrency: SaveConcurrency,
    ): Promise<CompanyProfile> =>
      apiFetch(`/api/v1/company-profiles/${id}`, {
        method: 'PATCH',
        json: { ...data, ...concurrency },
      }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/company-profiles/${id}`, { method: 'DELETE' }),
    // Phase FIX-company-profile-ux-and-assets — per-profile asset endpoints.
    // Tracker row 14 — each returns how it moved the profile version.
    uploadLogo: async (
      id: string,
      file: File,
    ): Promise<{ logoUrl: string; profileVersion?: ProfileVersionMove }> => {
      const fd = new FormData();
      fd.append('logo', file);
      return apiFetch(`/api/v1/company-profiles/${id}/logo`, { method: 'POST', body: fd });
    },
    deleteLogo: (id: string): Promise<{ profileVersion?: ProfileVersionMove } | undefined> =>
      apiFetch(`/api/v1/company-profiles/${id}/logo`, { method: 'DELETE' }),
    uploadLetterhead: async (
      id: string,
      file: File,
    ): Promise<{ letterheadUrl: string; profileVersion?: ProfileVersionMove }> => {
      const fd = new FormData();
      fd.append('letterhead', file);
      return apiFetch(`/api/v1/company-profiles/${id}/letterhead`, { method: 'POST', body: fd });
    },
    deleteLetterhead: (
      id: string,
    ): Promise<{ profileVersion?: ProfileVersionMove } | undefined> =>
      apiFetch(`/api/v1/company-profiles/${id}/letterhead`, { method: 'DELETE' }),
    // phase-regional-letterhead-templates (TODO-077) — per-region variants.
    listLetterheads: (id: string): Promise<RegionalLetterhead[]> =>
      apiFetch(`/api/v1/company-profiles/${id}/letterheads`),
    upsertLetterhead: (
      id: string,
      body: { region: string; letterheadUrl: string; label?: string | null },
    ): Promise<RegionalLetterhead> =>
      apiFetch(`/api/v1/company-profiles/${id}/letterheads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    // phase-company-profile-rows-7-9 (Row 9) — upload a letterhead FILE for one
    // region (text fields go BEFORE the file so the server sees them).
    uploadRegionalLetterhead: async (
      id: string,
      region: string,
      file: File,
      label?: string | null,
    ): Promise<RegionalLetterhead> => {
      const fd = new FormData();
      fd.append('region', region);
      if (label) fd.append('label', label);
      fd.append('letterhead', file);
      return apiFetch(`/api/v1/company-profiles/${id}/letterheads/upload`, { method: 'POST', body: fd });
    },
    /** Make this letterhead the profile default (replaces — clears — the previous default). */
    setDefaultLetterhead: (
      id: string,
      letterheadId: string,
    ): Promise<{
      defaultLetterheadId: string;
      letterheadUrl: string;
      region: string;
      profileVersion?: ProfileVersionMove;
    }> =>
      apiFetch(`/api/v1/company-profiles/${id}/letterheads/${letterheadId}/default`, { method: 'PUT' }),
    /** Direct URL of an uploaded regional letterhead file. */
    regionalLetterheadFileUrl: (id: string, letterheadId: string): string =>
      `/api/v1/company-profiles/${id}/letterheads/${letterheadId}/file`,
    deleteRegionalLetterhead: (id: string, letterheadId: string): Promise<void> =>
      apiFetch(`/api/v1/company-profiles/${id}/letterheads/${letterheadId}`, {
        method: 'DELETE',
      }),
  },
  // Phase 5.X-company-assets-letterhead — Company assets (logo + letterhead).
  companyAssets: {
    list: (): Promise<CompanyAssetsListResponse> =>
      apiFetch('/api/v1/company-assets'),
    /** Convenience: just the currently active asset of a given type
     *  (or null when none uploaded). */
    getActive: (type: 'logo' | 'letterhead'): Promise<CompanyAsset | null> =>
      apiFetch(`/api/v1/company-assets/active?type=${encodeURIComponent(type)}`),
    upload: async (
      type: 'logo' | 'letterhead',
      file: File,
    ): Promise<{ ok: boolean; asset: CompanyAsset }> => {
      const fd = new FormData();
      fd.append('file', file);
      // apiFetch infers Content-Type for FormData; we override `json` by
      // setting `body` directly.
      return apiFetch(`/api/v1/company-assets?type=${encodeURIComponent(type)}`, {
        method: 'POST',
        body: fd,
      });
    },
    contentUrl: (id: string): string =>
      `${env.VITE_API_BASE_URL}/api/v1/company-assets/${id}/content`,
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/company-assets/${id}`, { method: 'DELETE' }),
  },
  // Phase Wave D-2.a — Validation Rules list endpoint.
  // NOTE: the backend GET /api/v1/validation-rules route does not yet exist
  // (the ValidationRules table is populated by Phase 2.x seeds but only
  // exposed today through the smoke-test endpoint). A follow-up backend
  // phase needs to add the plain GET endpoint before this UI is usable
  // against production — see the Wave D-2.a report.
  validationRules: {
    list: (q: { region?: string; severity?: string } = {}): Promise<ValidationRule[]> => {
      const params = new URLSearchParams();
      if (q.region) params.set('region', q.region);
      if (q.severity) params.set('severity', q.severity);
      const qs = params.toString();
      return apiFetch(`/api/v1/validation-rules${qs ? `?${qs}` : ''}`);
    },
    /** Validation Rules rows 1–2 — the rule-set version shown in the page header. */
    ruleSet: (): Promise<ValidationRuleSet> => apiFetch('/api/v1/validation-rules/rule-set'),
    /**
     * Validation Rules rows 1–2 — "Export for review": download the CSV
     * (one row per rule, with the rule-set version). Authenticated fetch →
     * blob → save, like the other exports.
     */
    exportCsv: async (): Promise<string> => {
      const res = await apiFetch<Response>('/api/v1/validation-rules/export.csv', { raw: true });
      const disposition = res.headers.get('content-disposition') ?? '';
      const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'validation-rules.csv';
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
      return fileName;
    },
  },
  // Phase dashboard-attention-surface (TODO-024) — aggregate feed for the
  // Dashboard's "Needs your attention" surface.
  dashboard: {
    attentionItems: (): Promise<AttentionItem[]> =>
      apiFetch('/api/v1/dashboard/attention-items'),
  },
  submissions: {
    /**
     * Phase dashboard-performance-fix (TODO-042) — list submissions with
     * optional server-side filtering and pagination. Returns the full envelope
     * `{ data, pagination }` so callers can access `pagination.total` for
     * accurate page counts across all submissions (not just the loaded page).
     *
     * Pass `region`, `status`, or `search` to filter server-side.
     * Pass `limit` + `offset` for pagination (default limit=25, offset=0).
     */
    list: (params?: {
      groupBy?: 'application';
      limit?: number;
      offset?: number;
      region?: string;
      status?: string;
      search?: string;
      /** Phase submission-archive: pass true to include archived submissions. */
      includeArchived?: boolean;
    }): Promise<{
      data: Submission[];
      pagination: { limit: number; offset: number; total: number };
    }> => {
      const entries = params
        ? (Object.entries(params).filter(([, v]) => v != null) as [string, string | number][])
        : [];
      const qs =
        entries.length > 0
          ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`
          : '';
      return apiFetch(`/api/v1/submissions${qs}`, { fullEnvelope: true });
    },
    get: (id: string): Promise<Submission> => apiFetch(`/api/v1/submissions/${id}`),
    create: (data: Partial<Submission>): Promise<Submission> =>
      apiFetch('/api/v1/submissions', { method: 'POST', json: data }),
    update: (id: string, data: Partial<Submission>): Promise<Submission> =>
      apiFetch(`/api/v1/submissions/${id}`, { method: 'PATCH', json: data }),
    /**
     * assistant-confirmed-actions — apply a user-confirmed draft correction to
     * a small allowlist of metadata fields. This hits the SAME PATCH route as
     * `update` (same permissions, lock policy, validation hooks, audit); the
     * `assistantConfirmation` marker only adds the stricter server-side
     * contract (allowlist, explicit confirmation, reason, expected-before
     * snapshot). `expected` is the snapshot the user reviewed — a stale draft
     * or a replay is refused with 409 AssistantStaleDraft.
     */
    applyAssistantCorrection: (
      id: string,
      args: {
        fields: Record<string, string>;
        reason: string;
        expected: { UpdatedAt: string; AssistantRevision: string; fields: Record<string, string | null> };
      },
    ): Promise<Submission> =>
      apiFetch(`/api/v1/submissions/${id}`, {
        method: 'PATCH',
        json: {
          ...args.fields,
          assistantConfirmation: {
            confirmed: true,
            reason: args.reason,
            expected: args.expected,
          },
        },
      }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/submissions/${id}`, { method: 'DELETE' }),
    /** Phase submission-archive: archive or unarchive a submission. */
    archive: (id: string, archived: boolean): Promise<Submission> =>
      apiFetch(`/api/v1/submissions/${id}/archive`, { method: 'PATCH', json: { archived } }),
    /**
     * Phase 7.6 — create N parallel submissions (one per region) sharing
     * a single ParallelGroupId. Returns the shared group ID + all IDs.
     *
     * claude-regional-fixes (issue 3) — the contract is per-region: `shared`
     * carries product/company metadata only, and every entry in `regions`
     * brings its OWN application type, activity code and market identifier.
     * The server never copies one market's identity onto another, and the old
     * { baseSubmission, additionalRegions } shape is rejected.
     */
    createParallel: (data: {
      shared: ParallelSharedMetadata;
      regions: ParallelRegionSetup[];
    }): Promise<{ parallelGroupId: string; submissionIds: string[] }> =>
      apiFetch('/api/v1/submissions/parallel', { method: 'POST', json: data }),
    /**
     * Phase 7.6 — get all submissions in a parallel group.
     */
    getParallelGroup: (groupId: string): Promise<Submission[]> =>
      apiFetch(`/api/v1/submissions/parallel/${groupId}`),
    /**
     * Phase esg-transmission-status-retry (TODO-106) — manual retry of
     * the ESG transmission. Resolves with the updated Submission row;
     * rejects with 400 NotRetryable if the submission isn't in a
     * retryable state (Status!=Complete, never submitted, or already
     * acked without error).
     */
    transmitRetry: (id: string): Promise<Submission> =>
      apiFetch(`/api/v1/submissions/${id}/transmit-retry`, { method: 'POST' }),
    /**
     * Phase settings-monitor-esg-ack (TODO-074 partial) — on-demand
     * single-submission ACK poll. Backend bypasses the
     * `monitorEsgAcknowledgement` setting gate (explicit user intent).
     * Rejects with 400 when the submission hasn't been transmitted or
     * has already been acknowledged.
     */
    checkEsgAck: (
      id: string,
    ): Promise<{
      submissionId: string;
      acksReceived: number;
      rejections: number;
      errors: Array<{ submissionId: string; error: string }>;
    }> => apiFetch(`/api/v1/submissions/${id}/esg/check-ack`, { method: 'POST' }),
    /**
     * Wave E.4 — manually trigger a validation run across the submission
     * and every document within it. Returns the consolidated summary
     * + per-result list. Persists each result in ValidationResults so
     * subsequent GET /validation-results calls reflect the new run.
     */
    validateNow: (id: string): Promise<SubmissionValidationRunResponse> =>
      apiFetch(`/api/v1/submissions/${id}/validate`, { method: 'POST' }),
    /**
     * Wave E.4 — read the latest validation results for the submission
     * + every (non-deleted) document within it. De-duped server-side
     * to one row per (rule, target). Use this for the Compiler page's
     * status banner.
     */
    getValidationResults: (id: string): Promise<SubmissionValidationResultsResponse> =>
      apiFetch(`/api/v1/submissions/${id}/validation-results`),
    /**
     * Phase 5.X-background-validation-ui — lightweight polling endpoint.
     * Returns the current in-flight status (idle / running / complete) +
     * open issue counts. The UI polls this every 5 s when continuousValidation
     * is on; no polling for users with the setting off.
     */
    getValidationStatus: (id: string): Promise<ValidationStatusResponse> =>
      apiFetch(`/api/v1/submissions/${id}/validation-status`),
    // Shared status evidence for the indicators on submission screens.
    getStatus: (id: string): Promise<StatusSnapshot> =>
      apiFetch(`/api/v1/submissions/${id}/status`),
    /**
     * Wave E.7 — kick off the Compiler backend. POSTs to /compile, which:
     *   1. Re-runs every validator.
     *   2. Blocks with 422 if errors AND no overrideReason.
     *   3. Renames + places every doc, generates index.xml + regional XML
     *      + md5 checksum file, optionally zips, and writes everything
     *      to blob storage.
     *   4. Returns the persisted CompilerPackages row summary.
     */
    // phase-fix-publish-wizard-mode — MUST use `json:`, never a bare
    // `body: JSON.stringify(...)`. apiFetch only sets Content-Type for `json:`;
    // a bare string body goes out as text/plain, Fastify's default text parser
    // hands the route an UNPARSED STRING, and every field in it — including the
    // required publishMode — reads as undefined (the wizard's 400
    // MissingPublishMode, with acks/override silently dropped since Wave E.7).
    compile: (
      id: string,
      body: CompilerCompileBody = {},
    ): Promise<CompilerCompileResponse> =>
      apiFetch(`/api/v1/submissions/${id}/compile`, {
        method: 'POST',
        json: body,
      }),
    /** Wave E.7 — list historical compile packages newest-first. */
    listCompilePackages: (id: string): Promise<CompilerListPackagesResponse> =>
      apiFetch(`/api/v1/submissions/${id}/compile`),
    /**
     * T-17 (T17-D8) — download a compiled package's .zip THROUGH the
     * authenticated client. The old `buildCompileDownloadUrl` anchor carried
     * no Authorization header (every deployed download was a 401) and, on a
     * local-style server, followed a redirect to a `file://` path.
     *
     * Asks `?as=url` first (authenticated):
     *   - `{kind:'signed-url'}` -> navigate to the signed https URL;
     *   - `{kind:'stream'}`     -> re-request the same endpoint WITHOUT
     *     `as=url` with the same auth, then save the blob through a temporary
     *     anchor (`download = fileName`).
     * Rejects (never a silent no-op) on a failed request, an unrecognised
     * kind, or a signed URL that is not https.
     */
    compileDownload: async (id: string, packageId: string): Promise<void> => {
      const info = await apiFetch<CompileDownloadInfo>(
        `/api/v1/submissions/${id}/compile/${packageId}/download?as=url`,
      );
      if (info.kind === 'signed-url') {
        if (!isHttpsUrl(info.url)) {
          throw new Error('The server returned an invalid download link.');
        }
        window.location.assign(info.url);
        return;
      }
      if (info.kind !== 'stream') {
        throw new Error('The server returned an unrecognised download response.');
      }
      const res = await apiFetch<Response>(
        `/api/v1/submissions/${id}/compile/${packageId}/download`,
        { raw: true },
      );
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = info.fileName || 'package.zip';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
    },
    /**
     * T-17 (T17-D7/D8) — fetch ONE file of a compiled package through the
     * authenticated per-file route (manifest entries with `requiresAuth`).
     */
    fetchCompileFile: (id: string, packageId: string, relativePath: string): Promise<Response> =>
      apiFetch<Response>(
        `/api/v1/submissions/${id}/compile/${packageId}/files?path=${encodeURIComponent(relativePath)}`,
        { raw: true },
      ),
    /**
     * Wave E.7 — fetch the folder manifest for File System Access API
     * direct-folder-write. Each file carries a signed URL the browser
     * can fetch and stream into the chosen folder handle.
     */
    getCompileFolderManifest: (
      id: string,
      packageId: string,
    ): Promise<CompilerFolderManifestResponse> =>
      apiFetch(`/api/v1/submissions/${id}/compile/${packageId}/folder-manifest`),
    /**
     * Phase 5.X-prepublish-checklist-autodetect — fetch the
     * auto-detected pre-publish checklist items (DB-evaluated). The
     * modal renders these alongside the hardcoded manual items;
     * required failing auto items disable the Continue button and
     * the server re-evaluates them on POST /compile.
     */
    getPrePublishChecklist: (id: string): Promise<PrePublishChecklistResponse> =>
      apiFetch(`/api/v1/submissions/${id}/prepublish-checklist`),
    /**
     * Phase 5.X-screen-review-part-c — Test Inject.
     *
     * Requests a PDF preview of `documentId` with cross-document hyperlinks
     * injected. Returns `{ ok, preview, message, documentId, submissionId }`.
     * When `preview` is null the link-injection service is not yet available
     * and `message` describes the reason. When the service is available the
     * caller can open `preview` (a blob URL) in a new tab.
     */
    testInjectLinks: (
      submissionId: string,
      documentId: string,
    ): Promise<TestInjectResponse> =>
      apiFetch(`/api/v1/submissions/${submissionId}/test-inject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId }),
      }),
    /**
     * Phase 5.X-lifecycle-report-panel — fetch the full lifecycle audit
     * history for a submission. Returns AuditLog rows for the submission
     * itself and all its documents, newest-first, capped at 100.
     */
    getLifecycleHistory: (id: string): Promise<SubmissionLifecycleResponse> =>
      apiFetch(`/api/v1/submissions/${id}/lifecycle`),
    /**
     * Phase UI-12 — Get prior documents for a submission (earlier sequences
     * of the same NDA). Optional sectionCode filters to a specific CTD section.
     */
    getPriorDocuments: (
      submissionId: string,
      sectionCode?: string,
    ): Promise<Document[]> =>
      apiFetch(
        `/api/v1/submissions/${submissionId}/prior-documents${sectionCode ? `?sectionCode=${encodeURIComponent(sectionCode)}` : ''}`,
      ),
    getPriorDocumentReview: (
      submissionId: string,
      sectionCode?: string,
      currentDocumentId?: string,
      operation?: 'new' | 'replace' | 'append' | 'delete',
    ): Promise<PriorDocumentReview> => {
      const query = new URLSearchParams();
      if (sectionCode) query.set('sectionCode', sectionCode);
      if (currentDocumentId) query.set('currentDocumentId', currentDocumentId);
      if (operation) query.set('operation', operation);
      return apiFetch(`/api/v1/submissions/${submissionId}/prior-documents?${query}`, { fullEnvelope: true });
    },
    /**
     * Phase UI-12 — Get multi-sequence lifecycle summary for a submission's NDA.
     * Returns one row per sequence with DocumentCount + ConfirmedCount.
     */
    getSequenceHistory: (
      submissionId: string,
    ): Promise<
      Array<{
        Id: string;
        SequenceNumber: number | string;
        Status: string;
        SequenceDescription?: string | null;
        SubmissionType?: string | null;
        UpdatedAt: string;
        DocumentCount: number;
        ConfirmedCount: number;
      }>
    > => apiFetch(`/api/v1/submissions/${submissionId}/lifecycle-history`),
    /**
     * Phase sibling-selector-toolbar (TODO-058 Part B) — list ALL sequences
     * for the same application (NdaNumber + TenantId), including the
     * current one (IsCurrent=true). Ordered by SequenceNumber asc. The
     * toolbar sibling-sequence selector consumes this.
     */
    getSiblings: (
      id: string,
    ): Promise<
      Array<{
        Id: string;
        SequenceNumber: number | string;
        SequenceDescription: string | null;
        Status: string;
        DocumentCount: number | string;
        CompiledAt: string | null;
        IsCurrent: boolean;
      }>
    > => apiFetch(`/api/v1/submissions/${id}/siblings`),
    /**
     * Phase UI-13 — list prior sequences (strictly lower SequenceNumber)
     * for the same NDA. Used by the sequence-warning modal.
     */
    priorSequences: (
      id: string,
    ): Promise<Array<{ Id: string; SequenceNumber: string; Status: string }>> =>
      apiFetch(`/api/v1/submissions/${id}/prior-sequences`),
    /**
     * Phase UI-13 — agency confirmation convenience. PATCH the submission
     * to Status='Complete' with AgencyConfirmedAt stamped.
     */
    agencyConfirm: async (id: string): Promise<Submission> => {
      // F04: a button click is not receipt evidence. Use the existing
      // tenant-scoped gateway check, which refuses an untransmitted sequence.
      await apiFetch(`/api/v1/submissions/${id}/esg/check-ack`, { method: 'POST' });
      return apiFetch(`/api/v1/submissions/${id}`);
    },
    /**
     * Phase UI-13 — unlock a Complete/Published sequence back to Draft.
     * Server rejects with 409 if a higher SequenceNumber exists.
     */
    unlock: async (id: string, reason: string): Promise<Submission> => {
      await apiFetch(`/api/v1/submissions/${id}/unlock`, {
        method: 'POST', json: { kind: 'edit', reason },
      });
      return apiFetch(`/api/v1/submissions/${id}`);
    },
    /**
     * phase-admin-unlock — the ratified admin-unlock capability. A reasoned,
     * permission-gated grant of a controlled exception (edit | renumber) to
     * a locked sequence. Distinct from the crude UI-13 `unlock` above, which
     * only demotes Status Complete/Published → Draft; this grant does NOT
     * change Status/IsLocked at all — it lets specific mutation gates permit
     * modification while the sequence stays exactly as locked as it was.
     */
    grantUnlock: (
      id: string,
      body: { kind: UnlockKind; reason: string },
    ): Promise<{ grant: UnlockGrant; activeUnlocks: UnlockGrant[] }> =>
      apiFetch(`/api/v1/submissions/${id}/unlock`, { method: 'POST', json: body }),
    /** phase-admin-unlock — close an active grant. Also mandatory-reason + audited. */
    relockGrant: (
      id: string,
      body: { kind: UnlockKind; reason: string },
    ): Promise<{ grant: UnlockGrant; activeUnlocks: UnlockGrant[] }> =>
      apiFetch(`/api/v1/submissions/${id}/relock`, { method: 'POST', json: body }),
    /**
     * Phase UI-12 — classify-all SSE stream. Returns a raw fetch Response
     * so the caller can read the SSE stream directly.
     */
    classifyAllStream: async (
      submissionId: string,
      documentIds?: string[],
      /**
       * phase-classification-model-selection — the per-request model for this
       * batch. One model governs the whole run (the server resolves it once at
       * invocation) and is recorded per document. The server treats this as a
       * request, not a decision: unknown / disabled / above-cap falls back.
       */
      model?: string | null,
    ): Promise<Response> => {
      const token = await tokenGetter();
      const headers = rawFetchHeaders(token, { 'Content-Type': 'application/json' });
      const body: Record<string, unknown> = {};
      if (documentIds) body['documentIds'] = documentIds;
      if (model) body['model'] = model;
      return fetch(
        `${env.VITE_API_BASE_URL}/api/v1/submissions/${submissionId}/classify-all`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        },
      );
    },
    getNotificationSubscription: (id: string): Promise<{ isSubscribed: boolean }> =>
      apiFetch(`/api/v1/submissions/${id}/notification-subscription`),
    setNotificationSubscription: (id: string, isSubscribed: boolean): Promise<{ isSubscribed: boolean }> =>
      apiFetch(`/api/v1/submissions/${id}/notification-subscription`, { method: 'PUT', json: { isSubscribed } }),
    /**
     * phase-fix-classify-all-eligibility — poll the batch classify-all job the
     * POST above starts (it returns { jobId, totalDocs } and runs the work in
     * the background; there is no SSE body). `progress` is 0–100.
     */
    classifyAllStatus: (
      submissionId: string,
    ): Promise<{
      jobId: string;
      status: 'pending' | 'running' | 'complete' | 'error';
      progress: number;
      startedAt: string | null;
      completedAt: string | null;
    }> => apiFetch(`/api/v1/submissions/${submissionId}/classify-all/status`),
  },
  documents: {
    getRelatedDocuments: (id: string, offset = 0, limit = 20, options?: { historyToken?: string; sourceRevision?: string; operation?: 'replace' | 'append' | 'delete' }): Promise<RelatedDocumentReview> => {
      const query = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (options?.historyToken) query.set('historyToken', options.historyToken);
      if (options?.sourceRevision) query.set('sourceRevision', options.sourceRevision);
      if (options?.operation) query.set('operation', options.operation);
      return apiFetch(`/api/v1/documents/${id}/related-documents?${query}`);
    },
    listBySubmission: (submissionId: string, page?: { limit: number; offset: number }): Promise<Document[]> =>
      apiFetch(`/api/v1/documents?submissionId=${encodeURIComponent(submissionId)}${page ? `&limit=${page.limit}&offset=${page.offset}` : ''}`),
    get: (id: string): Promise<Document> => apiFetch(`/api/v1/documents/${id}`),
    create: (data: Partial<Document>): Promise<Document> =>
      apiFetch('/api/v1/documents', { method: 'POST', json: data }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/documents/${id}`, { method: 'DELETE' }),
    /**
     * T-17 (T17-D5) — drop a PENDING upload record (a document created by
     * `create` whose file upload never completed). Server: only the creating
     * user, only while the row is still pending (404 otherwise, including after
     * the upload succeeded). Callers treat this as best-effort cleanup.
     */
    abandonPendingUpload: (id: string): Promise<void> =>
      apiFetch(`/api/v1/documents/${id}/pending-upload`, { method: 'DELETE' }),
    // ----- Wave E.2 — Inspector right-pane endpoints ----- //
    /**
     * Wave E.2 — GET cross-sequence lifecycle history for a document.
     * Returns ONE call shape (not V1's N+1) with `lineageTracked: false`
     * while a LineageId column isn't yet on Documents — UI uses this
     * flag to render the "first occurrence in lifecycle" empty state.
     */
    getLifecycle: (submissionId: string, docId: string): Promise<DocumentLifecycle> =>
      apiFetch(`/api/v1/submissions/${submissionId}/documents/${docId}/lifecycle`),
    /**
     * Wave E.2 — PATCH a document's CTD section assignment. Optional
     * `reason` is captured in the AuditLog `after` payload.
     */
    updatePlacement: (
      id: string,
      body: { newSectionCode: string; reason?: string | null },
    ): Promise<DocumentPlacementUpdate> =>
      apiFetch(`/api/v1/documents/${id}/placement`, {
        method: 'PATCH',
        json: body,
      }),
    /**
     * Wave E.2 — POST classification approval. Server enforces:
     * `reason` is REQUIRED when `approvedClassification` differs from
     * the classifier's suggestion (Part 11 override discipline). Reject
     * the override with HTTP 400 `{ error: 'ReasonRequired' }` if so.
     */
    approveClassification: (
      id: string,
      body: {
        approvedClassification: string;
        reason?: string | null;
        classifierSuggestion?: string | null;
        classifierConfidence?: number | null;
      },
      revision: Document['RowVersion'],
    ): Promise<DocumentClassificationApproval> =>
      apiFetch(`/api/v1/documents/${id}/classification/approve`, {
        method: 'POST',
        json: body,
        headers: documentRevisionHeaders(revision),
      }),
    // phase-parity-document-operation-staging — assign the lifecycle operation
    // AND its prior-document target in one PATCH (the same endpoint the Compiler
    // inspector's LifecycleOperationEditor uses, so staging + Compiler can never
    // disagree). Used by the staging Documents table's prior-doc picker. For
    // 'new', pass modifiedFile/replacesDocumentId as null to clear any target.
    assignLifecycle: (
      id: string,
      body: {
        lifecycleOperation: 'new' | 'replace' | 'append' | 'delete';
        modifiedFile?: string | null;
        replacesDocumentId?: string | null;
        reason?: string | null;
      },
      revision: Document['RowVersion'],
    ): Promise<Document> =>
      apiFetch(`/api/v1/documents/${id}`, { method: 'PATCH', json: body, headers: documentRevisionHeaders(revision) }),
    /**
     * Wave E.3 — POST a replacement of an existing document with a
     * newly-uploaded document. The new doc inherits the old doc's
     * section + classification, `LifecycleOperation` flips to
     * 'replace', and `ReplacesDocumentId` points back to the old doc.
     * The old doc is marked `IsSuperseded = true` (NOT physically
     * deleted) so audit history is preserved.
     */
    replace: (
      oldDocumentId: string,
      body: { newDocumentId: string; reason?: string | null },
    ): Promise<{ oldDocument: Document; newDocument: Document }> =>
      apiFetch(`/api/v1/documents/${oldDocumentId}/replace`, {
        method: 'POST',
        json: body,
      }),
    /**
     * Wave E.4 — read the latest validation results for a single
     * document. Used by the inspector's Links tab + the document-level
     * validation indicator badge.
     */
    getValidationResults: (id: string): Promise<DocumentValidationResultsResponse> =>
      apiFetch(`/api/v1/documents/${id}/validation-results`),
    /**
     * Phase 5.1 — POST /api/v1/documents/:id/classify. Server downloads the
     * stored file, asks Claude to assign an eCTD code + confidence + rationale,
     * persists the result onto the row, and returns the new classification.
     */
    classify: (
      id: string,
      opts: { taxonomy?: string[]; model?: string | null } = {},
    ): Promise<ClassificationResult> =>
      apiFetch(`/api/v1/documents/${id}/classify`, {
        method: 'POST',
        json: {
          ...(opts.taxonomy !== undefined ? { taxonomy: opts.taxonomy } : {}),
          // phase-classification-model-selection — a REQUEST for a model; the
          // server applies precedence + the tenant cap before anything runs.
          ...(opts.model ? { model: opts.model } : {}),
        },
      }),
    /**
     * phase-classification-model-selection — the models classification may
     * use, from the same admin catalog the assistant uses, already filtered to
     * the tenant's cap, plus the effective default.
     */
    modelCatalog: (): Promise<ClassificationModelCatalog> =>
      apiFetch('/api/v1/classification/model-catalog'),
    /**
     * Phase 5.10a — POST /api/v1/documents/:id/confirm. Marks the AI
     * classification as user-confirmed. Backend stamps `ConfirmedAt` and
     * `ConfirmedBy` and returns the updated document row.
     *
     * Phase settings-auto-populate-on-confirm (TODO-074) — when the
     * `autoPopulateOnConfirm` setting is on and the document's CTD
     * section code maps to a form, the response additionally carries
     * `autoCreatedForm` describing the form that was auto-created.
     * Uses `fullEnvelope` so this top-level field is not discarded.
     */
    confirm: (id: string): Promise<ConfirmResult> =>
      apiFetch(`/api/v1/documents/${id}/confirm`, { method: 'POST', fullEnvelope: true }),
    /**
     * Phase 5.10a — DELETE /api/v1/documents/:id/confirm. Inverse of
     * `confirm`; clears `ConfirmedAt` / `ConfirmedBy`.
     */
    unconfirm: (id: string): Promise<Document> =>
      apiFetch(`/api/v1/documents/${id}/confirm`, { method: 'DELETE' }),
    /**
     * Phase compiler-document-lock (TODO-079) — toggle the per-document
     * lock. When locked=true, downstream PATCH/DELETE/reorder reject
     * with 409 DocumentLocked.
     */
    setLock: (id: string, locked: boolean): Promise<{ id: string; isLocked: boolean }> =>
      apiFetch(`/api/v1/documents/${id}/lock`, { method: 'PATCH', json: { locked } }),
    /**
     * Phase compiler-batch-operations — lock or unlock multiple documents
     * in a single request. IDs from a different tenant are silently ignored.
     * Returns the count of rows actually updated.
     */
    batchSetLock: (
      submissionId: string,
      documentIds: string[],
      locked: boolean,
    ): Promise<{ affected: number }> =>
      apiFetch(`/api/v1/submissions/${encodeURIComponent(submissionId)}/documents/batch-lock`, {
        method: 'POST',
        json: { documentIds, locked },
      }),
    /**
     * Phase 5.10e/f — atomic bulk-confirm of N documents under a single
     * submission. All-or-nothing: either every classified doc confirms
     * (200 with `confirmed[]` + `skipped[]`) or none do (400
     * `BulkConfirmRejected` with `data: {notFound, notClassified,
     * mismatchedSubmission}`). `?force=true` bypasses the not-classified
     * check (admin escape hatch — backend audits it as `forced: true`).
     */
    bulkConfirm: (
      submissionId: string,
      body: BulkConfirmRequest,
      options: { force?: boolean } = {},
    ): Promise<BulkConfirmResponse> => {
      const qs = options.force ? '?force=true' : '';
      return apiFetch(
        `/api/v1/submissions/${encodeURIComponent(submissionId)}/documents/bulk-confirm${qs}`,
        { method: 'POST', json: body },
      );
    },
    /**
     * Phase 5.X-bulk-confirm — auto-discover convenience: confirms every
     * unconfirmed classified document in the submission with a single
     * round-trip. Soft-deleted / unclassified / already-confirmed rows are
     * silently skipped. Returns the same shape as `bulkConfirm` with
     * `mode: 'all'`.
     */
    bulkConfirmAll: (
      submissionId: string,
      options: { force?: boolean } = {},
    ): Promise<BulkConfirmResponse> => {
      const qs = options.force ? '?force=true' : '';
      return apiFetch(
        `/api/v1/submissions/${encodeURIComponent(submissionId)}/documents/bulk-confirm${qs}`,
        { method: 'POST', json: { documentIds: [] } },
      );
    },
    uploadFile: async (id: string, file: File): Promise<Document> => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return apiFetch<Document>(`/api/v1/documents/${id}/file`, { method: 'POST', body: fd });
    },
    /**
     * Phase upload-progress-bar — multipart upload via XMLHttpRequest so
     * the caller can render a 0..100 progress bar driven by
     * `xhr.upload.onprogress` events. Falls back to `uploadFile` (plain
     * fetch, no progress) when XMLHttpRequest is unavailable (some
     * jsdom test environments without xhr-mock) — caller should be
     * prepared for `onProgress` to never fire in that case.
     *
     * Auth header is sourced from the same `tokenGetter` that `apiFetch`
     * uses so the dev-mode null-token path keeps working.
     */
    uploadFileWithProgress: async (
      id: string,
      file: File,
      onProgress: (pct: number) => void,
    ): Promise<Document> => {
      if (typeof globalThis.XMLHttpRequest !== 'function') {
        return v2.documents.uploadFile(id, file);
      }
      const fd = new FormData();
      fd.append('file', file, file.name);
      const token = await tokenGetter();
      const url = `${env.VITE_API_BASE_URL}/api/v1/documents/${encodeURIComponent(id)}/file`;
      return new Promise<Document>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e): void => {
          if (e.lengthComputable && e.total > 0) {
            const pct = Math.min(100, Math.max(0, Math.round((e.loaded / e.total) * 100)));
            onProgress(pct);
          }
        };
        xhr.upload.onload = (): void => onProgress(100);
        xhr.onload = (): void => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const env = JSON.parse(xhr.responseText) as Envelope<Document>;
              if (env && typeof env === 'object' && 'data' in env && env.data) {
                resolve(env.data);
              } else {
                resolve(env as unknown as Document);
              }
            } catch (err) {
              reject(err instanceof Error ? err : new Error('Invalid upload response'));
            }
            return;
          }
          reject(new ApiError(xhr.status, xhr.responseText, xhr.responseText || xhr.statusText));
        };
        xhr.onerror = (): void => reject(new Error('Upload network error'));
        xhr.onabort = (): void => reject(new Error('Upload aborted'));
        xhr.open('POST', url);
        // T17-D35 — the ONE raw-header builder: Authorization when signed in AND the dev
        // identity (x-dev-user) when one is selected. The XHR used to set only the
        // bearer, so a switched dev identity uploaded as dev@local.
        for (const [name, value] of Object.entries(rawFetchHeaders(token))) {
          xhr.setRequestHeader(name, value);
        }
        xhr.send(fd);
      });
    },
    downloadFile: (id: string): Promise<Response> =>
      apiFetch(`/api/v1/documents/${id}/file`, { raw: true }),
    // phase-fix-document-inline-viewer — same bytes, but the endpoint answers
    // with Content-Disposition: inline (view semantics; distinct audit action
    // stays download on the server). The pane renders the blob regardless.
    viewFile: (id: string): Promise<Response> =>
      apiFetch(`/api/v1/documents/${id}/file?disposition=inline`, { raw: true }),
    // phase-docx-preview — mammoth-rendered HTML for inline .docx preview.
    previewHtml: (id: string): Promise<{ html: string; cached: boolean }> =>
      apiFetch(`/api/v1/documents/${id}/preview-html`),
    deleteFile: (id: string): Promise<void> =>
      apiFetch(`/api/v1/documents/${id}/file`, { method: 'DELETE' }),
    /**
     * Phase 5.X-document-handling-enhancements (Feature 2) — request the
     * eCTD-canonical filename the backend would suggest for this doc.
     * Read-only — the caller PATCHes the new FileName separately after
     * the user confirms. 400 if the doc isn't classified yet.
     */
    autoRename: (id: string): Promise<AutoRenameSuggestion> =>
      apiFetch(`/api/v1/documents/${id}/auto-rename`, { method: 'POST' }),
    /**
     * Phase 5.X-document-handling-enhancements — generic PATCH for any
     * writable Document field (allowed set is server-side in
     * `ALLOWED_WRITE_FIELDS`, includes FileName for the Auto-rename
     * apply step). Callers should pass the minimal patch object.
     */
    update: (id: string, patch: Partial<Document>, revision: Document['RowVersion']): Promise<Document> =>
      apiFetch(`/api/v1/documents/${id}`, { method: 'PATCH', json: patch, headers: documentRevisionHeaders(revision) }),
    /**
     * Phase 5.X-document-handling-enhancements (Feature 3) — fetch the
     * cover-letter sync verdict for a cover-letter document (CtdSectionCode
     * === '1.2'). Non-cover-letter docs return status='not-applicable'.
     */
    coverLetterSyncStatus: (id: string): Promise<CoverLetterSyncStatus> =>
      apiFetch(`/api/v1/documents/${id}/cover-letter-sync-status`),
    /**
     * Phase 5.X-document-handling-enhancements (Feature 3) — capture the
     * current sibling-doc-list as the cover letter's snapshot. Used by
     * the inspector's "Mark cover letter as up to date" button when the
     * sync banner reports out-of-sync. 400 if the doc isn't section 1.2.
     */
    coverLetterSnapshot: (id: string): Promise<CoverLetterSnapshotResult> =>
      apiFetch(`/api/v1/documents/${id}/cover-letter-snapshot`, { method: 'POST' }),
    /**
     * Phase 5.X-pdf-form-field-extraction — extract AcroForm field values
     * from a PDF document. Returns field names, values, and types.
     */
    extractFields: (id: string): Promise<ExtractFieldsResponse> =>
      apiFetch(`/api/v1/documents/${id}/extract-fields`, { method: 'POST' }),
    /**
     * Phase 7.2 — Repopulate (regenerate) a cover letter document with the
     * current sibling document list.
     */
    repopulateCoverLetter: (
      id: string,
    ): Promise<{ ok: true; modeUsed: string; letterhead?: CoverLetterLetterheadReport }> =>
      apiFetch(`/api/v1/documents/${id}/repopulate-cover-letter`, { method: 'POST' }),
    /**
     * Phase UI-12 — Build the absolute URL to download/preview a document file.
     * Used by DocumentPreviewPane to construct the iframe src + download link.
     */
    getDownloadUrl: (id: string): string =>
      `${env.VITE_API_BASE_URL}/api/v1/documents/${id}/file`,
  },
  coverLetter: {
    /**
     * Phase 7.2 — Generate a cover letter PDF for the given submission.
     * Creates or updates the submission's section-1.2 document.
     */
    generate: (
      submissionId: string,
    ): Promise<{
      ok: true;
      documentId: string;
      modeUsed: string;
      letterhead?: CoverLetterLetterheadReport;
    }> =>
      apiFetch(`/api/v1/submissions/${submissionId}/generate-cover-letter`, { method: 'POST' }),
  },
  validation: {
    // T-17 (T17-D10) — admin-only (Users:manage) rule DIAGNOSTIC. It is a dry
    // run: nothing is saved and validation-status is unaffected. The persisted
    // run is `v2.submissions.validateNow`. (Replaces the old `validation.run`,
    // which wrote rows on every page open.)
    diagnostics: (submissionId: string): Promise<ValidationDiagnosticsRun> =>
      apiFetch('/api/v1/admin/validation-smoke-test', {
        method: 'POST',
        json: { submissionId },
      }),
  },
  packages: {
    // phase-compiler-retire-legacy — `generate` (the legacy POST
    // /generate-package client) and `downloadZip` (its PackagePage-only
    // download companion) are gone: the legacy generator is retired and the
    // backend route answers 410. Compiled-package download lives on the
    // Compiler page's post-compile chooser.
    getBackbone: (submissionId: string): Promise<Backbone> =>
      apiFetch(`/api/v1/submissions/${submissionId}/backbone`),
    // phase-package-viewer-live-structure — the DERIVED structure from live
    // data (folders/filenames/operations the compiler WOULD produce), with
    // compile-only values explicitly pending. See the route header for the
    // derivable-vs-compile-only boundary.
    getLiveStructure: (submissionId: string): Promise<LivePackageStructure> =>
      apiFetch(`/api/v1/submissions/${submissionId}/package-structure`),
  },
  documentReorder: {
    // phase-document-reorder-within-section — POST matches the existing
    // backend route. Array position drives sortOrder (0-based).
    reorder: (submissionId: string, orderedDocumentIds: string[]): Promise<{ ok: true }> =>
      apiFetch(`/api/v1/submissions/${submissionId}/documents/reorder`, {
        method: 'POST',
        json: {
          order: orderedDocumentIds.map((documentId, idx) => ({
            documentId,
            sortOrder: idx,
          })),
        },
      }),
  },
  annotations: {
    list: (documentId: string): Promise<Annotation[]> =>
      apiFetch(`/api/v1/documents/${documentId}/annotations`),
    create: (
      documentId: string,
      severity: AnnotationSeverity,
      comment: string,
    ): Promise<Annotation> =>
      apiFetch(`/api/v1/documents/${documentId}/annotations`, {
        method: 'POST',
        json: { severity, comment },
      }),
    resolve: (documentId: string, annotationId: string): Promise<Annotation> =>
      apiFetch(
        `/api/v1/documents/${documentId}/annotations/${annotationId}/resolve`,
        { method: 'PATCH' },
      ),
    delete: (documentId: string, annotationId: string): Promise<void> =>
      apiFetch(`/api/v1/documents/${documentId}/annotations/${annotationId}`, {
        method: 'DELETE',
      }),
  },
  documentTemplates: {
    /**
     * phase-ui-template-pdf-preview — rendered PDF preview of a template,
     * with every placeholder replaced by a «SAMPLE» marker and a
     * "PREVIEW — SAMPLE DATA" watermark. Nothing is persisted server-side.
     *
     * `raw: true` so we get the Response and can read `.blob()` — the same
     * pattern as `documents.downloadFile`. It matters here: apiFetch attaches
     * the bearer token, whereas pointing an <iframe> straight at the URL would
     * be an unauthenticated browser GET and 401 wherever auth is enabled.
     */
    previewPdf: (id: string, profileId?: string): Promise<Response> =>
      apiFetch(
        `/api/v1/document-templates/${id}/preview.pdf${profileId ? `?profileId=${encodeURIComponent(profileId)}` : ''}`,
        { raw: true },
      ),
    /**
     * phase-template-preview-company-profile — the Text tab's substituted body.
     * Placeholder mode renders `{{tokens}}` client-side; with a profileId this
     * returns the server-substituted text (real generation's resolver), so both
     * tabs resolve identically. `{ text, profileName }`.
     */
    previewText: (id: string, profileId?: string): Promise<{ text: string; profileName: string | null }> =>
      apiFetch(
        `/api/v1/document-templates/${id}/preview.text${profileId ? `?profileId=${encodeURIComponent(profileId)}` : ''}`,
      ),
    /**
     * phase-template-variable-provenance — where each variable in the template
     * body resolves from at generation time. Walked server-side through the
     * REAL resolvers (no forked resolution logic). Returns the true token count
     * plus the stored count so the client can flag stale metadata.
     */
    // phase-reconcile-contact-bindings — `profileId` resolves CONTACT/SIGNATORY
    // bindings against a real profile so each row can report WHICH step of the
    // resolution chain matched. Without it the walk is static and honestly
    // reports no step rather than guessing one.
    variableProvenance: (id: string, profileId?: string): Promise<TemplateVariableProvenance> =>
      profileId
        ? apiFetch(
            `/api/v1/document-templates/${id}/variable-provenance?profileId=${encodeURIComponent(profileId)}`,
          )
        : apiFetch(`/api/v1/document-templates/${id}/variable-provenance`),

    // phase-template-variable-bindings — bind a variable to a source, list the
    // tenant's bindings, and reset one back to the built-in default.
    bindableSources: (): Promise<BindableSources> =>
      apiFetch('/api/v1/template-variable-bindings/sources'),
    variableBindings: (id: string): Promise<TemplateBindingsResponse> =>
      apiFetch(`/api/v1/document-templates/${id}/variable-bindings`),
    setVariableBinding: (
      id: string,
      variable: string,
      body: SetVariableBindingBody,
    ): Promise<VariableBindingApi> =>
      // `json:` (not `body:`) — apiFetch sets Content-Type from it; a raw body
      // string reaches Fastify unparsed and the route 400s on a missing kind.
      apiFetch(
        `/api/v1/document-templates/${id}/variable-bindings/${encodeURIComponent(variable)}`,
        { method: 'PUT', json: body },
      ),
    resetVariableBinding: (id: string, variable: string): Promise<{ reset: boolean }> =>
      apiFetch(
        `/api/v1/document-templates/${id}/variable-bindings/${encodeURIComponent(variable)}`,
        { method: 'DELETE' },
      ),
    /** Form Templates row 6 — live check of a draft binding (read-only). */
    checkVariableBinding: (
      id: string,
      variable: string,
      body: SetVariableBindingBody & { profileId?: string },
    ): Promise<BindingHealthApi> =>
      apiFetch(
        `/api/v1/document-templates/${id}/variable-bindings/${encodeURIComponent(variable)}/check`,
        { method: 'POST', json: body },
      ),
    /** Row 6 — per template, how many of this tenant's bindings are broken. */
    bindingHealth: (): Promise<TemplateBindingHealth[]> =>
      apiFetch('/api/v1/template-variable-bindings/health'),
    list: (q: DocumentTemplatesListQuery = {}): Promise<DocumentTemplate[]> => {
      const params = new URLSearchParams();
      if (q.targetModule) params.set('targetModule', q.targetModule);
      if (q.limit !== undefined) params.set('limit', String(q.limit));
      if (q.offset !== undefined) params.set('offset', String(q.offset));
      if (q.includeDeleted) params.set('includeDeleted', 'true');
      const qs = params.toString();
      return apiFetch(`/api/v1/document-templates${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<DocumentTemplate> =>
      apiFetch(`/api/v1/document-templates/${id}`),
    create: (data: Partial<DocumentTemplate>): Promise<DocumentTemplate> =>
      apiFetch('/api/v1/document-templates', { method: 'POST', json: data }),
    // Backend uses PUT for updates (full or partial body).
    // phase-form-template-versioning — ChangeNote is REQUIRED: every edit
    // creates a new version and the server 400s a blank note.
    update: (id: string, data: Partial<DocumentTemplate> & { ChangeNote: string }): Promise<DocumentTemplate> =>
      apiFetch(`/api/v1/document-templates/${id}`, { method: 'PUT', json: data }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/document-templates/${id}`, { method: 'DELETE' }),
    // Tracker row 10 — which submissions use this template (via their forms);
    // the server refuses DELETE (409 TemplateInUse) while activeCount > 0.
    usage: (id: string): Promise<TemplateUsage> =>
      apiFetch(`/api/v1/document-templates/${id}/usage`),
    // phase-form-template-versioning — recover a soft-deleted template.
    undelete: (id: string): Promise<DocumentTemplate> =>
      apiFetch(`/api/v1/document-templates/${id}/undelete`, { method: 'POST', json: {} }),
    generate: (templateId: string, body: GenerateRequest): Promise<GenerationResult> =>
      apiFetch(`/api/v1/document-templates/${templateId}/generate`, {
        method: 'POST',
        json: body,
      }),
    // Phase 5.X-v1-parity-polish Feature 3 — diff two templates by id.
    diff: (id: string, otherId: string): Promise<DocumentTemplateDiff> =>
      apiFetch(`/api/v1/document-templates/${id}/diff/${otherId}`),
    /**
     * Form Templates row 8 — diff two versions of one template. Either side
     * may be `'current'` (the live body).
     */
    versionDiff: (id: string, versionId: string, otherVersionId: string): Promise<TemplateVersionDiff> =>
      apiFetch(
        `/api/v1/document-templates/${id}/versions/${encodeURIComponent(versionId)}/diff/${encodeURIComponent(otherVersionId)}`,
      ),
    // phase-template-version-history — list saved versions.
    versions: (id: string): Promise<TemplateVersion[]> =>
      apiFetch(`/api/v1/document-templates/${id}/versions`, { method: 'GET' }),
    // phase-template-version-history — restore a prior version.
    restore: (
      id: string,
      versionId: string,
      changeNote?: string,
    ): Promise<DocumentTemplate> =>
      apiFetch(`/api/v1/document-templates/${id}/versions/${versionId}/restore`, {
        method: 'POST',
        // ChangeNote matches the PUT route's casing; the server defaults a
        // blank note to "Restored to version N.0".
        json: changeNote ? { ChangeNote: changeNote } : {},
      }),
  },
  // Phase 5.11 — Prompt Library CRUD (backend at src/routes/prompts.ts).
  // Permissions: Prompts:read (all roles), Prompts:write + :delete (admin only).
  prompts: {
    list: (q: PromptsListQuery = {}): Promise<PromptsListResponse> => {
      const params = new URLSearchParams();
      if (q.region) params.set('region', q.region);
      if (q.module !== undefined) params.set('module', String(q.module));
      if (q.section) params.set('section', q.section);
      if (q.scopeType) params.set('scopeType', q.scopeType);
      if (q.scopeId) params.set('scopeId', q.scopeId);
      if (q.page !== undefined) params.set('page', String(q.page));
      if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
      // Phase 8.3 — optional PromptType filter.
      if (q.promptType) params.set('promptType', q.promptType);
      const qs = params.toString();
      return apiFetch(`/api/v1/prompts${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<PromptLibraryEntry> => apiFetch(`/api/v1/prompts/${id}`),
    create: (data: PromptWriteBody): Promise<PromptLibraryEntry> =>
      apiFetch('/api/v1/prompts', { method: 'POST', json: data }),
    update: (id: string, data: Partial<PromptWriteBody>): Promise<PromptLibraryEntry> =>
      apiFetch(`/api/v1/prompts/${id}`, { method: 'PUT', json: data }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/prompts/${id}`, { method: 'DELETE' }),
    // ----- Phase 5.X-prompt-library-expansion action routes ------
    test: (id: string, sampleInput?: string): Promise<PromptTestResponse> =>
      apiFetch(`/api/v1/prompts/${id}/test`, {
        method: 'POST',
        json: sampleInput !== undefined ? { sampleInput } : {},
      }),
    scanAndLearn: (args: {
      documentIds: string[];
      extractionMode: 'classification' | 'summary';
    }): Promise<PromptScanLearnResponse> =>
      apiFetch('/api/v1/prompts/scan-and-learn', { method: 'POST', json: args }),
    setStatus: (
      id: string,
      status: 'active' | 'draft',
    ): Promise<PromptLibraryEntry> =>
      apiFetch(`/api/v1/prompts/${id}/status`, {
        method: 'PATCH',
        json: { status },
      }),
    bulkActivate: (promptIds: string[]): Promise<PromptBulkActivateResult> =>
      apiFetch('/api/v1/prompts/bulk-activate', {
        method: 'POST',
        json: { promptIds },
      }),
    revertBulkActivate: (batchId: string): Promise<PromptBulkActivateRevertResult> =>
      apiFetch(`/api/v1/prompts/bulk-activate/${batchId}/revert`, { method: 'POST' }),
    duplicate: (id: string): Promise<PromptLibraryEntry> =>
      apiFetch(`/api/v1/prompts/${id}/duplicate`, { method: 'POST' }),
    // Tracker row 6 — the classifier test for Classification prompts: the same
    // classification pipeline POST /documents/:id/classify runs. With a
    // submissionId it uses that submission's region and settings exactly.
    testClassification: (args: {
      promptId: string;
      sampleFileName: string;
      sampleContent: string;
      submissionId?: string;
    }): Promise<PromptClassifierTestResult> =>
      apiFetch('/api/v1/prompt-library/test', { method: 'POST', json: args }),
  },
  // Phase 8.4 — Scan & Learn from eCTD archives.
  scanLearn: {
    start: (args: {
      source: 'folder' | 'import';
      folderPath?: string;
      submissionId?: string;
    }): Promise<{ jobId: string; status: string }> =>
      apiFetch('/api/v1/scan-learn', { method: 'POST', json: args }),
    getJob: (jobId: string): Promise<ScanLearnJob> => apiFetch(`/api/v1/scan-learn/${jobId}`),
    /** Tracker row 11 — the tenant's most recent job; null when there has never been one. */
    latest: async (): Promise<ScanLearnJob | null> => {
      try {
        return await apiFetch<ScanLearnJob>('/api/v1/scan-learn/latest');
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    /** Tracker row 11 — record that a finished job's outcome was shown (idempotent). */
    acknowledge: (jobId: string): Promise<ScanLearnJob> =>
      apiFetch(`/api/v1/scan-learn/${encodeURIComponent(jobId)}/acknowledge`, { method: 'POST' }),
  },
  generatingCompanies: {
    list: (q: {
      status?: 'pending' | 'confirmed' | 'rejected';
      page?: number;
      pageSize?: number;
    } = {}): Promise<{
      companies: GeneratingCompanyEntry[];
      total: number;
      page: number;
      pageSize: number;
    }> => {
      const params = new URLSearchParams();
      if (q.status) params.set('status', q.status);
      if (q.page) params.set('page', String(q.page));
      if (q.pageSize) params.set('pageSize', String(q.pageSize));
      const qs = params.toString();
      return apiFetch(`/api/v1/generating-companies${qs ? `?${qs}` : ''}`);
    },
    /**
     * Tracker row 12 — confirm / reject and/or edit name or location. The
     * server changes the company's prompts in the same transaction and says
     * what changed ("5 prompts deactivated").
     */
    review: async (
      id: string,
      data: { status?: 'confirmed' | 'rejected'; name?: string; location?: string | null },
    ): Promise<GeneratingCompanyChangeResult> => {
      const env = await apiFetch<{ ok: boolean; message: string; data: GeneratingCompanyChangeResult['company'] }>(
        `/api/v1/generating-companies/${encodeURIComponent(id)}`,
        { method: 'PATCH', json: data, fullEnvelope: true },
      );
      return { message: env.message, company: env.data };
    },
  },
  // Phase 5.14 — SpecFiles CRUD (backend at src/routes/spec-files.ts).
  // Permissions: SpecFiles:read (all roles), SpecFiles:write + :delete
  // (admin only). The backend uses PUT (not PATCH) for partial updates and
  // hard-deletes on DELETE.
  specFiles: {
    officialPackages: (): Promise<{ packages: OfficialSpecPackage[] }> =>
      apiFetch('/api/v1/spec-files/official-packages'),
    officialPackage: (packageId: string): Promise<OfficialSpecPackageDetail> =>
      apiFetch(`/api/v1/spec-files/official-packages/${encodeURIComponent(packageId)}`),
    downloadOfficialPackage: (packageId: string): Promise<Response> =>
      apiFetch(`/api/v1/spec-files/official-packages/${encodeURIComponent(packageId)}/download`, { raw: true }),
    officialPackageFile: (packageId: string, path: string): Promise<Response> =>
      apiFetch(`/api/v1/spec-files/official-packages/${encodeURIComponent(packageId)}/file?${new URLSearchParams({ path }).toString()}`, { raw: true }),
    list: (q: SpecFilesListQuery = {}): Promise<SpecFilesListResponse> => {
      const params = new URLSearchParams();
      if (q.region) params.set('region', q.region);
      if (q.fileType) params.set('fileType', q.fileType);
      if (q.isActive !== undefined) params.set('isActive', String(q.isActive));
      if (q.page !== undefined) params.set('page', String(q.page));
      if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
      if (q.sortBy) params.set('sortBy', q.sortBy);
      if (q.sortOrder) params.set('sortOrder', q.sortOrder);
      const qs = params.toString();
      return apiFetch(`/api/v1/spec-files${qs ? `?${qs}` : ''}`);
    },
    get: (id: string): Promise<SpecFile> => apiFetch(`/api/v1/spec-files/${id}`),
    // T15-D8 — the specification a NEW application in `region` would be
    // pinned to, from the same server resolver submission creation uses.
    resolve: (region: string, ectdVersion?: string): Promise<SpecFileResolution> => {
      const params = new URLSearchParams({ region });
      if (ectdVersion) params.set('ectdVersion', ectdVersion);
      return apiFetch(`/api/v1/spec-files/resolve?${params.toString()}`);
    },
    create: (data: SpecFileWriteBody): Promise<SpecFile> =>
      apiFetch('/api/v1/spec-files', { method: 'POST', json: data }),
    update: (id: string, data: Partial<SpecFileWriteBody>): Promise<SpecFile> =>
      apiFetch(`/api/v1/spec-files/${id}`, { method: 'PUT', json: data }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/spec-files/${id}`, { method: 'DELETE' }),
    // Phase spec-file-upload-diff (TODO-071) — preview an added/removed
    // section diff between an uploaded DTD/XSD and the existing active
    // spec file BEFORE committing the upload. Does not persist anything.
    previewDiff: (body: SpecFileDiffRequest): Promise<SpecFileDiffResult> =>
      apiFetch('/api/v1/spec-files/preview-diff', { method: 'POST', json: body }),
    // phase-spec-files-management — view / upload-version / explicit
    // active switch (see src/routes/spec-files.ts).
    content: (
      id: string,
    ): Promise<{
      content: string;
      source: 'stored' | 'embedded';
      fileName: string | null;
      sha256: string | null;
      title: string;
      fileType: string;
    }> => apiFetch(`/api/v1/spec-files/${id}/content`),
    uploadVersion: (
      id: string,
      body: {
        fileName: string;
        content: string;
        /** T15-D5 — REQUIRED, unique per slot; the server derives title + version from it. */
        versionLabel: string;
        description?: string;
        /** phase-spec-enforcement-wiring — set + provenance. */
        companions?: Array<{ fileName: string; content: string }>;
        sourceUrl?: string;
        authorityVersionLabel?: string;
      },
    ): Promise<{
      id: string;
      /** T15-D5 — "<slot family> <label>". */
      title: string;
      version: string;
      sha256: string;
      isActive: boolean;
      warning: string | null;
      companions?: Array<{ id: string; fileName: string; sha256: string }>;
    }> =>
      apiFetch(`/api/v1/spec-files/${id}/versions`, { method: 'POST', json: body }),
    // T15-D1 — what activating `id` would change (server-computed diff
    // against the version in force). Activation needs its reviewedActiveId.
    review: (id: string): Promise<SpecFileReview> => apiFetch(`/api/v1/spec-files/${id}/review`),
    setActive: (
      id: string,
      isActive: boolean,
      /** T15-D1 — required by the server to ACTIVATE (from review()); unused to deactivate. */
      reviewedActiveId?: string | null,
    ): Promise<{ id: string; isActive: boolean; deactivated: string[]; warning: string | null }> =>
      apiFetch(`/api/v1/spec-files/${id}/active`, {
        method: 'PATCH',
        json: reviewedActiveId === undefined ? { isActive } : { isActive, reviewedActiveId },
      }),
  },
  // Phase 5.9 — Settings 3-tier API (backend at src/routes/settings.ts).
  // Phase 5.20a wires the read endpoints to a UI; the write/delete methods
  // are exported here (additive) so Phase 5.20b can light up the edit flow
  // without re-touching the client. See the type comments above for the
  // distinction between MERGED (map-keyed) and SCOPE (row-array) responses.
  settings: {
    // GET /api/v1/settings — merged map for the caller (org + user).
    listAll: (): Promise<MergedSettingsResponse> => apiFetch('/api/v1/settings'),
    // GET /api/v1/settings/submission/:id — same merged shape, with
    // submission overrides layered on top of org + user. Requires
    // `Submissions:read` on the target submission.
    getSubmission: (submissionId: string): Promise<MergedSettingsResponse> =>
      apiFetch(`/api/v1/settings/submission/${encodeURIComponent(submissionId)}`),
    // GET /api/v1/settings/scope/org — raw org rows.
    listOrg: (): Promise<ScopeSettingsResponse> =>
      apiFetch('/api/v1/settings/scope/org'),
    // GET /api/v1/settings/scope/user — caller's own user-scope rows.
    listUser: (): Promise<ScopeSettingsResponse> =>
      apiFetch('/api/v1/settings/scope/user'),
    // GET /api/v1/settings/scope/submission/:id — raw submission rows.
    // Requires `Submissions:read`.
    listSubmission: (submissionId: string): Promise<ScopeSettingsResponse> =>
      apiFetch(`/api/v1/settings/scope/submission/${encodeURIComponent(submissionId)}`),

    // PUT /api/v1/settings/org — admin only (`Users:manage`). The body's
    // `orgLocked` is honoured; user/submission overrides for a locked key
    // will be rejected with HTTP 409.
    updateOrg: (body: SettingsWriteBody): Promise<SettingsScopeRow> =>
      apiFetch('/api/v1/settings/org', { method: 'PUT', json: body }),
    // PUT /api/v1/settings/user/me — caller's own scope (auth-only).
    updateUser: (body: SettingsWriteBody): Promise<SettingsScopeRow> =>
      apiFetch('/api/v1/settings/user/me', { method: 'PUT', json: body }),
    // PUT /api/v1/settings/submission/:id — requires `Submissions:write`.
    updateSubmission: (
      submissionId: string,
      body: SettingsWriteBody,
    ): Promise<SettingsScopeRow> =>
      apiFetch(`/api/v1/settings/submission/${encodeURIComponent(submissionId)}`, {
        method: 'PUT',
        json: body,
      }),

    // DELETE — hard deletes. Same permission rules as the PUT siblings.
    deleteOrg: (key: string): Promise<void> =>
      apiFetch(`/api/v1/settings/org/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      }),
    deleteUser: (key: string): Promise<void> =>
      apiFetch(`/api/v1/settings/user/me/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      }),
    deleteSubmission: (submissionId: string, key: string): Promise<void> =>
      apiFetch(
        `/api/v1/settings/submission/${encodeURIComponent(submissionId)}/${encodeURIComponent(key)}`,
        { method: 'DELETE' },
      ),
  },
  // Phase 5.19 — detailed devops health endpoint. Auth-only (no resource
  // permission); operators / on-call see version, DB state, migrations, etc.
  // Phase 5.19.2 wires this to the /admin/system-health page.
  health: {
    detailed: (): Promise<HealthDetailed> => apiFetch('/api/v1/health/detailed'),
  },
  // phase-dev-identity-and-user-switcher — dev-only (404 when auth enabled).
  dev: {
    identities: (): Promise<DevIdentitiesResponse> => apiFetch('/api/v1/dev/identities'),
  },
  // Phase 5.8 — RBAC management surface.
  users: {
    getMe: (): Promise<MeResponse> => apiFetch('/api/v1/users/me'),
    list: (q: UsersListQuery = {}): Promise<UsersListResponse> => {
      const params = new URLSearchParams();
      if (q.page !== undefined) params.set('page', String(q.page));
      if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
      if (q.role) params.set('role', q.role);
      if (q.search) params.set('search', q.search);
      const qs = params.toString();
      return apiFetch(`/api/v1/users${qs ? `?${qs}` : ''}`);
    },
    assignRole: (email: string, roleId: string): Promise<UserSummary | null> =>
      apiFetch(`/api/v1/users/${encodeURIComponent(email)}/roles`, {
        method: 'POST',
        json: { roleId },
      }),
    // D-14 — `confirmSelfLockout` is the explicit acknowledgement the backend
    // requires before a user removes their OWN Users:manage-granting role.
    revokeRole: (
      email: string,
      roleId: string,
      opts?: { confirmSelfLockout?: boolean },
    ): Promise<UserSummary | null> =>
      apiFetch(
        `/api/v1/users/${encodeURIComponent(email)}/roles/${encodeURIComponent(roleId)}${
          opts?.confirmSelfLockout ? '?confirmSelfLockout=true' : ''
        }`,
        { method: 'DELETE' },
      ),
    deleteUser: (email: string, opts?: { confirmSelfLockout?: boolean }): Promise<void> =>
      apiFetch(`/api/v1/users/${encodeURIComponent(email)}${opts?.confirmSelfLockout ? '?confirmSelfLockout=true' : ''}`, { method: 'DELETE' }),
    // Phase user-invite-deactivate (TODO-103)
    deactivate: (email: string, reason?: string): Promise<{ email: string; isDeactivated: boolean }> =>
      apiFetch(`/api/v1/users/${encodeURIComponent(email)}/deactivate`, {
        method: 'PATCH',
        json: { deactivated: true, ...(reason ? { reason } : {}) },
      }),
    reactivate: (email: string): Promise<{ email: string; isDeactivated: boolean }> =>
      apiFetch(`/api/v1/users/${encodeURIComponent(email)}/deactivate`, {
        method: 'PATCH',
        json: { deactivated: false },
      }),
    // Phase user-invite-deactivate (TODO-101)
    invite: (email: string, roleId: string, message?: string): Promise<InviteResult> =>
      apiFetch('/api/v1/users/invite', {
        method: 'POST',
        json: { email, roleId, ...(message ? { message } : {}) },
      }),
    listInvitations: (): Promise<UserInvitation[]> =>
      apiFetch('/api/v1/users/invitations'),
    cancelInvitation: (id: string): Promise<void> =>
      apiFetch(`/api/v1/users/invitations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    /**
     * D-25 — what an invitation IS, without accepting it. A READ: the server
     * writes no row and no audit entry, so opening an invitation link can
     * never be mistaken for using it.
     *
     * POST, not GET, because the token is a credential and must travel in the
     * body — a token in a URL path lands in access logs, proxy logs, referrer
     * headers and browser history.
     *
     * The wrong person gets 403 InvitationEmailMismatch carrying an ALREADY
     * MASKED address; the full address never reaches this client at all.
     */
    previewInvitation: (token: string): Promise<InvitationPreview> =>
      apiFetch('/api/v1/join/preview', { method: 'POST', json: { token } }),
    /**
     * D-25 — accept an invitation. The token is the ONLY thing sent; who the
     * caller is comes from the signed-in session, never from the client.
     */
    acceptInvitation: (token: string): Promise<AcceptedInvitation> =>
      apiFetch('/api/v1/join', { method: 'POST', json: { token } }),
    resendInvitation: (id: string): Promise<{ inviteLink: string; expiresAt: string }> =>
      apiFetch(`/api/v1/users/invitations/${encodeURIComponent(id)}/resend`, { method: 'POST' }),
  },
  roles: {
    list: (): Promise<Role[]> => apiFetch('/api/v1/roles'),
    // phase-roles-custom-role-ui — admin-only CRUD on custom roles.
    create: (body: RoleWriteBody): Promise<Role> =>
      apiFetch('/api/v1/roles', { method: 'POST', json: body }),
    update: (id: string, body: RoleWriteBody): Promise<Role> =>
      apiFetch(`/api/v1/roles/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        json: body,
      }),
    // phase-fix-role-retire-not-delete — roles are never deleted, only
    // retired (and can be reactivated). The DELETE endpoint no longer exists.
    retire: (id: string): Promise<Role> =>
      apiFetch(`/api/v1/roles/${encodeURIComponent(id)}/retire`, { method: 'POST' }),
    reactivate: (id: string): Promise<Role> =>
      apiFetch(`/api/v1/roles/${encodeURIComponent(id)}/reactivate`, { method: 'POST' }),
  },
  permissions: {
    /** phase-roles-custom-role-ui — admin-only catalogue used to build
     *  the permission checkbox grid in the Create / Edit Role modal. */
    list: (): Promise<Array<{ Resource: string; Action: string }>> =>
      apiFetch('/api/v1/permissions'),
  },
  auditLog: {
    list: (q: AuditLogListQuery = {}): Promise<AuditLogListResponse> => {
      const qs = auditLogQueryString(q);
      return apiFetch(`/api/v1/audit-log${qs ? `?${qs}` : ''}`);
    },
    /** Phase 5.27 — distinct Actions in the tenant for the filter dropdown. */
    listActions: (): Promise<AuditLogActionsResponse> =>
      apiFetch('/api/v1/audit-log/actions'),
    /**
     * Phase 5.27 — build the absolute CSV-export URL for the current
     * filters. Used by the frontend's "Export CSV" button via an
     * anchor-with-`download` attribute, which is the standard pattern
     * for triggering a file download from a same-origin endpoint.
     */
    buildExportUrl: (q: AuditLogListQuery = {}): string => {
      const qs = auditLogQueryString(q);
      return `${env.VITE_API_BASE_URL}/api/v1/audit-log/export${qs ? `?${qs}` : ''}`;
    },
    /** Matrix D-30: carry the same bearer/dev identity as every other request. */
    exportCsv: async (q: AuditLogListQuery = {}): Promise<void> => {
      const qs = auditLogQueryString(q);
      const response = await apiFetch<Response>(`/api/v1/audit-log/export${qs ? `?${qs}` : ''}`, { raw: true });
      const objectUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = response.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'audit-log.csv';
      document.body.appendChild(anchor);
      try { anchor.click(); } finally {
        anchor.remove();
        globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
    },
    /**
     * D-30 — fetch the export as an AUTHENTICATED request.
     *
     * The page used to render `<a href={buildExportUrl(...)} download>`. A
     * browser anchor cannot carry a header, so that request went out with no
     * `Authorization` and no `x-dev-user`: in dev it always ran as the
     * unswitched `dev@local` whatever the switcher said, with auth enabled it
     * was simply unauthenticated, and a refusal could not be shown — the
     * browser navigated and handed the person an error page or a file full of
     * JSON.
     *
     * `raw: true` gives the Response so the caller can take `.blob()`;
     * apiFetch still throws ApiError on a non-2xx, so a 422 ExportTooLarge
     * reaches the page as an error it can display.
     */
    downloadExport: async (q: AuditLogListQuery = {}): Promise<Blob> => {
      const qs = auditLogQueryString(q);
      const res = await apiFetch<Response>(
        `/api/v1/audit-log/export${qs ? `?${qs}` : ''}`,
        { raw: true },
      );
      return res.blob();
    },
    /**
     * Phase 5.27a — trigger the nightly archive cleanup on demand.
     * Admin-only (`AuditLog:write`). Body fields are all optional; the
     * server falls back to the org-level `auditLog.activeRetentionDays`
     * setting (default 7 years) when `activeRetentionDays` is omitted.
     */
    runRetentionCleanup: (
      body: AuditLogRetentionCleanupBody = {},
    ): Promise<AuditLogRetentionCleanupResponse> =>
      // D-29 — `json:` rather than `body:`. apiFetch sets
      // `Content-Type: application/json` only for `json:`, so this request
      // previously went out with NO content type, which the route now
      // refuses with 415. `json:` also guarantees a real boolean on the
      // wire rather than a stringified one.
      apiFetch('/api/v1/admin/audit-log/retention-cleanup', {
        method: 'POST',
        json: body,
      }),
  },
  // Phase 5.X-notifications-system — user-scoped in-app notifications.
  // Backend at src/routes/notifications.ts. SSE stream available via
  // streamNotifications (async generator).
  notifications: {
    list: (q: NotificationsListQuery = {}): Promise<NotificationsListResponse> => {
      const params = new URLSearchParams();
      if (q.unread) params.set('unread', 'true');
      if (q.limit !== undefined) params.set('limit', String(q.limit));
      if (q.before) params.set('before', q.before);
      const qs = params.toString();
      return apiFetch(`/api/v1/notifications${qs ? `?${qs}` : ''}`);
    },
    count: (): Promise<NotificationsCountResponse> =>
      apiFetch('/api/v1/notifications/count'),
    markRead: (id: string): Promise<NotificationsMarkReadResponse> =>
      apiFetch(`/api/v1/notifications/${id}`, { method: 'PATCH' }),
    markAllRead: (): Promise<NotificationsMarkAllReadResponse> =>
      apiFetch('/api/v1/notifications/mark-all-read', { method: 'PATCH' }),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/notifications/${id}`, { method: 'DELETE' }),
    clear: (): Promise<NotificationsClearResponse> =>
      apiFetch('/api/v1/notifications/clear?confirmText=DELETE', { method: 'DELETE' }),
    /**
     * SSE stream of new notifications for the current user. Uses
     * fetch + ReadableStream because
     * EventSource can't send the Bearer header MSAL requires.
     *
     * Yields parsed Notification objects from `event: notification`
     * frames. Ignores `event: connected` (heartbeat-style init) and
     * `:keep-alive` comment frames.
     */
    stream: async function* (
      signal: globalThis.AbortSignal,
    ): AsyncGenerator<NotificationApi, void, void> {
      const token = await tokenGetter();
      const headers = rawFetchHeaders(token, { Accept: 'text/event-stream' });
      const res = await fetch(`${env.VITE_API_BASE_URL}/api/v1/notifications/stream`, {
        headers,
        signal,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new ApiError(res.status, body, body || res.statusText);
      }
      if (!res.body) return;
      const reader = res.body.getReader();
      const decoder = new globalThis.TextDecoder();
      let buffer = '';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';
          for (const frame of frames) {
            // Lines we care about: `event: notification` followed by
            // `data: <json>`. Frames without those (e.g., heartbeat
            // comments starting with `:` or the initial `event:
            // connected`) are skipped.
            const lines = frame.split('\n');
            const eventLine = lines.find((l) => l.startsWith('event: '));
            if (!eventLine || eventLine.slice('event: '.length) !== 'notification') continue;
            const dataLine = lines.find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              yield JSON.parse(dataLine.slice(6)) as NotificationApi;
            } catch {
              /* malformed frame — drop and keep reading */
            }
          }
        }
      } finally {
        try {
          reader.releaseLock();
        } catch {
          /* already released */
        }
      }
    },
  },
  govForms: {
    list: (region?: string): Promise<GovernmentForm[]> => {
      const qs = region ? `?region=${encodeURIComponent(region)}` : '';
      return apiFetch(`/api/v1/form-templates/gov-forms${qs}`);
    },
    uploadPdf: async (
      file: File,
      meta: { region: string; formKey: string; formName: string; allowManualStorage?: boolean },
    ): Promise<GovFormUploadResponse> => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('region', meta.region);
      fd.append('formKey', meta.formKey);
      fd.append('formName', meta.formName);
      if (meta.allowManualStorage) fd.append('allowManualStorage', 'true');
      return apiFetch('/api/v1/form-templates/upload-pdf', { method: 'POST', body: fd });
    },
    identifyPdf: async (file: File, options?: { allowManualStorage?: boolean }): Promise<GovFormIdentifyResponse> => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      if (options?.allowManualStorage) fd.append('allowManualStorage', 'true');
      return apiFetch('/api/v1/form-templates/identify-pdf', { method: 'POST', body: fd });
    },
    downloadOriginal: async (id: string, formName?: string): Promise<void> => {
      const response = await apiFetch<Response>(`/api/v1/form-templates/gov-forms/${id}/pdf`, { raw: true });
      const objectUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      const safeName = Array.from(formName ?? 'official-form', c => c.charCodeAt(0) < 32 || /[\\/<>:"|?*]/.test(c) ? '_' : c).join('');
      anchor.download = `${safeName}.pdf`;
      document.body.appendChild(anchor);
      try { anchor.click(); }
      finally { anchor.remove(); globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 0); }
    },
    getMappings: (id: string): Promise<GovFormMapping[]> =>
      apiFetch(`/api/v1/form-templates/gov-forms/${id}/mappings`),
    saveMappings: (
      id: string,
      mappings: Array<
        Pick<GovFormMapping, 'PdfFieldName' | 'AutoeCTDFieldPath' | 'AutoFillEnabled' | 'IsRequired'>
      >,
    ): Promise<GovFormMapping[]> =>
      apiFetch(`/api/v1/form-templates/gov-forms/${id}/mappings`, {
        method: 'PUT',
        json: { mappings },
      }),
    /** Row 4 — preview against a REAL submission (no sample data). */
    testMapping: (id: string, submissionId: string): Promise<GovFormTestResult[]> =>
      apiFetch(`/api/v1/form-templates/gov-forms/${id}/test-mapping`, {
        method: 'POST',
        json: { submissionId, date: localIsoDate() },
      }),
    /** Row 4 — the canonical mapping paths (the same catalog save validates against). */
    mappingPaths: (): Promise<{ paths: GovFormMappingPath[]; aliases: string[] }> =>
      apiFetch('/api/v1/form-templates/gov-forms/mapping-paths'),
    delete: (id: string): Promise<void> =>
      apiFetch(`/api/v1/form-templates/gov-forms/${id}`, { method: 'DELETE' }),
  },
  notificationSubscriptions: {
    get: (): Promise<NotificationSubscriptionState> =>
      apiFetch('/api/v1/users/me/notification-subscriptions'),
    save: (
      subscriptions: Array<{ ruleId: string | null; isSubscribed: boolean }>,
    ): Promise<unknown> =>
      apiFetch('/api/v1/users/me/notification-subscriptions', {
        method: 'PUT',
        json: { subscriptions },
      }),
  },
  /** Phase node-extension-compiler-ui (TODO-048 Part B) — per-submission node extensions CRUD. */
  nodeExtensions: {
    list: (submissionId: string): Promise<NodeExtension[]> =>
      apiFetch(`/api/v1/submissions/${submissionId}/node-extensions`),
    create: (
      submissionId: string,
      body: { SectionCode: string; ExtensionCode: string; ExtensionLabel: string; SortOrder?: number },
    ): Promise<NodeExtension> =>
      apiFetch(`/api/v1/submissions/${submissionId}/node-extensions`, { method: 'POST', json: body }),
    update: (
      submissionId: string,
      extId: string,
      body: Partial<{ SectionCode: string; ExtensionCode: string; ExtensionLabel: string; SortOrder: number }>,
    ): Promise<NodeExtension> =>
      apiFetch(`/api/v1/submissions/${submissionId}/node-extensions/${extId}`, {
        method: 'PATCH',
        json: body,
      }),
    delete: (submissionId: string, extId: string): Promise<void> =>
      apiFetch(`/api/v1/submissions/${submissionId}/node-extensions/${extId}`, { method: 'DELETE' }),
  },
  /** phase-submission-milestones — dated regulatory commitments per sequence. */
  milestones: {
    /** All milestones on one sequence, ordered by due date. */
    listForSubmission: (submissionId: string): Promise<SubmissionMilestone[]> =>
      apiFetch(`/api/v1/submissions/${submissionId}/milestones`),
    /**
     * Tenant-wide window for the home calendar, ordered by due date. Dates are
     * `YYYY-MM-DD`; `limit` defaults to 50 on the server and is capped at 500.
     */
    listWindow: (params: {
      from?: string;
      to?: string;
      limit?: number;
    } = {}): Promise<SubmissionMilestoneWithSubmission[]> => {
      const qs = new URLSearchParams();
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return apiFetch(`/api/v1/milestones${query ? `?${query}` : ''}`);
    },
    create: (submissionId: string, body: CreateMilestoneBody): Promise<SubmissionMilestone> =>
      apiFetch(`/api/v1/submissions/${submissionId}/milestones`, { method: 'POST', json: body }),
    update: (milestoneId: string, body: UpdateMilestoneBody): Promise<SubmissionMilestone> =>
      apiFetch(`/api/v1/milestones/${milestoneId}`, { method: 'PATCH', json: body }),
    /** `reason` is recorded in the audit row's AfterJson. */
    delete: (milestoneId: string, reason?: string): Promise<void> =>
      apiFetch(`/api/v1/milestones/${milestoneId}`, {
        method: 'DELETE',
        ...(reason ? { json: { reason } } : {}),
      }),
  },
  /** Phase 7.4 — HC controlled vocabulary for sequence descriptions. */
  hcSequenceDescriptions: {
    list: (submissionType?: string): Promise<Array<{ code: string; label: string; applicableTypes: string[] }>> => {
      const qs = submissionType ? `?submissionType=${encodeURIComponent(submissionType)}` : '';
      return apiFetch(`/api/v1/hc-sequence-descriptions${qs}`);
    },
  },
  /** Phase 7.3 — SPL (Structured Product Labeling) parse + preview. */
  spl: {
    preview: (documentId: string): Promise<{ html: string }> =>
      apiFetch(`/api/v1/documents/${documentId}/spl-preview`),
    parseSpl: (submissionId: string, documentId: string): Promise<SplMetadata> =>
      apiFetch(`/api/v1/submissions/${submissionId}/parse-spl`, {
        method: 'POST',
        json: { documentId },
      }),
  },

  // ----- Phase forms-templates-unified -----
  //
  // Phase phase-fix-admin-forms-tab-crash — IMPORTANT: `apiFetch` UNWRAPS the
  // `{ ok, data }` envelope and resolves to `data` itself (see apiFetch: "Most
  // endpoints return { ok, data } — unwrap"). These signatures used to claim
  // the full envelope, which TypeScript happily accepted (the shape is asserted
  // through the generic, never checked), so callers wrote `res.data` and got
  // `undefined` at runtime — that is what crashed the Admin Forms tab with
  // "Cannot read properties of undefined (reading 'length')". The types below
  // now describe what actually resolves. Use `{ fullEnvelope: true }` if a
  // caller ever needs the envelope's sibling fields back.
  submissionForms: {
    /** List all forms for a submission. */
    list: (submissionId: string): Promise<SubmissionForm[]> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms`),

    /** phase-company-profile-rows-7-9 (Row 7) — the pre-generate signatory check. */
    signatoryRequirements: (submissionId: string): Promise<SignatoryRequirements> =>
      apiFetch(`/api/v1/submissions/${submissionId}/signatory-requirements`),

    /**
     * Load required forms into a submission. phase-forms-workflow-follow-up
     * (Item A) — pass `selection` (requirement keys from the picker) to load
     * only those; omit it to load all Required (backward-compatible).
     */
    loadRequired: (submissionId: string, selection?: string[]): Promise<SubmissionForm[]> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/load-required`, {
        method: 'POST',
        ...(selection ? { json: { selection } } : {}),
      }),

    /** Item A — the loadable requirements (forms + templates) for the picker. */
    requirementsPicker: (submissionId: string): Promise<RequirementPickerItem[]> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/requirements-picker`),

    /** Item B — applicable DocumentTemplates for the submission's region (fallback dropdown). */
    templateOptions: (submissionId: string): Promise<FormTemplateOption[]> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/template-options`),

    /** Manually add a form to a submission. */
    add: (
      submissionId: string,
      body: { displayName: string; formType?: string; templateId?: string; governmentFormId?: string },
    ): Promise<SubmissionForm> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms`, { method: 'POST', json: body }),

    /** Update a form's metadata (displayName, templateId, governmentFormId, status). */
    update: (
      submissionId: string,
      formId: string,
      body: { displayName?: string; templateId?: string; governmentFormId?: string; status?: string },
    ): Promise<SubmissionForm> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/${formId}`, {
        method: 'PATCH',
        json: body,
      }),

    /** Delete a form from a submission. */
    delete: (submissionId: string, formId: string): Promise<{ ok: boolean }> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/${formId}`, { method: 'DELETE' }),

    /**
     * Generate or fill content for a form (template → Claude; government → PDF fill).
     *
     * phase-fix-path-b-variable-resolution §2.5 — reads the FULL envelope,
     * because the `warnings` list (variables that fell back to a default or are
     * left as a visible `{{placeholder}}`) rides alongside `data` rather than
     * inside the SubmissionForm domain object. The default unwrap would drop it.
     */
    generate: (
      submissionId: string,
      formId: string,
      // phase-fix-signatory-and-submission-date — the cover letter's date comes
      // from a value the user confirms at generation time. Optional because
      // government forms don't use it; when present it is sent as `letterDate`
      // and the server maps submission_date → it (never defaulting server-side).
      opts?: { letterDate?: string },
    ): Promise<SubmissionFormGenerateResult> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/${formId}/generate`, {
        method: 'POST',
        fullEnvelope: true,
        // Form Templates row 4 — `date` is the user's own calendar day, which a
        // government-form `date` mapping fills (the server's UTC day can differ).
        json: { ...(opts?.letterDate ? { letterDate: opts.letterDate } : {}), date: localIsoDate() },
      }),

    /** Returns the PDF download URL for a form. */
    pdfUrl: (submissionId: string, formId: string): string =>
      `/api/v1/submissions/${submissionId}/forms/${formId}/pdf`,

    /** Approve a form and promote it to a Document. */
    approve: (
      submissionId: string,
      formId: string,
      body?: { ctdSectionCode?: string },
    ): Promise<SubmissionForm> =>
      apiFetch(`/api/v1/submissions/${submissionId}/forms/${formId}/approve`, {
        method: 'POST',
        json: body ?? {},
      }),

    /** List all catalog entries (system + tenant). */
    catalog: (): Promise<RequiredFormsCatalogEntry[]> => apiFetch('/api/v1/forms/catalog'),

    /** List required forms for a region/type with deduplication. */
    required: (region: string, type: string): Promise<RequiredFormsCatalogEntry[]> =>
      apiFetch(`/api/v1/forms/required/${encodeURIComponent(region)}/${encodeURIComponent(type)}`),
  },

  // Phase phase-fix-admin-forms-tab-crash — same envelope-unwrap correction as
  // `submissionForms` above: apiFetch resolves to `data`, not `{ ok, data }`.
  submissionRequirements: {
    /** List all template catalog entries (system defaults + tenant overrides). */
    listTemplates: (
      region?: string,
      submissionType?: string,
    ): Promise<RequiredTemplatesCatalogEntry[]> => {
      const params = new URLSearchParams();
      if (region) params.set('region', region);
      if (submissionType) params.set('submissionType', submissionType);
      const qs = params.toString();
      return apiFetch(`/api/v1/required-templates-catalog${qs ? `?${qs}` : ''}`);
    },

    /** List templates for a specific region + submission type. */
    listTemplatesByType: (
      region: string,
      type: string,
    ): Promise<RequiredTemplatesCatalogEntry[]> =>
      apiFetch(
        `/api/v1/required-templates-catalog/${encodeURIComponent(region)}/${encodeURIComponent(type)}`,
      ),

    /** Combined requirements (forms + templates) for a region + submission type. */
    getRequirements: (
      region: string,
      type: string,
      // R1 — optional activity filter (SubmissionTypesCatalog code).
      activity?: string | null,
    ): Promise<SubmissionRequirementsData> =>
      apiFetch(
        `/api/v1/submission-requirements/${encodeURIComponent(region)}/${encodeURIComponent(type)}${
          activity ? `?activity=${encodeURIComponent(activity)}` : ''
        }`,
      ),

    /** Add a new tenant template catalog entry. */
    addTemplate: (body: {
      region: string;
      submissionType: string;
      templateType: string;
      templateName: string;
      isRequired?: boolean;
      conditional?: string | null;
      notes?: string | null;
      /** R1 — optional activity tag (SubmissionTypesCatalog code). */
      activityCode?: string | null;
    }): Promise<RequiredTemplatesCatalogEntry> =>
      apiFetch('/api/v1/required-templates-catalog', { method: 'POST', json: body }),

    /** Update a tenant (custom) template catalog entry. */
    updateTemplate: (
      id: string,
      body: Partial<{
        templateName: string;
        isRequired: boolean;
        conditional: string | null;
        notes: string | null;
        activityCode: string | null;
        subTypeCode: string | null;
      }>,
    ): Promise<RequiredTemplatesCatalogEntry> =>
      apiFetch(`/api/v1/required-templates-catalog/${id}`, { method: 'PATCH', json: body }),

    /** Delete a tenant (custom) template catalog entry. */
    deleteTemplate: (id: string): Promise<{ ok: boolean }> =>
      apiFetch(`/api/v1/required-templates-catalog/${id}`, { method: 'DELETE' }),

    /**
     * Item C — create-or-update a tenant OVERRIDE of a built-in requirement.
     * `catalog` selects forms vs templates; `baselineId` is the built-in row.
     */
    overrideRequirement: (body: {
      catalog: 'form' | 'template';
      baselineId: string;
      status?: boolean;
      conditional?: string | null;
      name?: string;
      notes?: string | null;
      activityCode?: string | null;
      subTypeCode?: string | null;
    }): Promise<unknown> =>
      apiFetch('/api/v1/submission-requirements/override', { method: 'POST', json: body }),

    /** Item C — reset (delete) a tenant override; the built-in baseline is restored. */
    resetOverride: (catalog: 'form' | 'template', id: string): Promise<{ ok: boolean }> =>
      apiFetch(`/api/v1/submission-requirements/override/${catalog}/${id}`, { method: 'DELETE' }),
  },

  /** Phase KB-submission-types-v3 — SubmissionTypesCatalog lookup. */
  submissionTypes: {
    list: (params: {
      region?: string;
      applicationType?: string;
    }): Promise<SubmissionTypesData> => {
      const qs = new URLSearchParams();
      if (params.region) qs.set('region', params.region);
      if (params.applicationType) qs.set('applicationType', params.applicationType);
      const q = qs.toString();
      return apiFetch(`/api/v1/submission-types${q ? `?${q}` : ''}`);
    },
  },
  // phase-classification-stage-1-filename-layer — read-only pattern-evidence
  // listing that fills the Prompt Library's Layer 1 panel with reality.
  classificationEvidence: {
    list: (): Promise<ClassificationEvidenceListResponse> =>
      apiFetch('/api/v1/classification-evidence'),
  },
};

/** Phase KB-submission-types-v3 — a sub-type entry in SubmissionTypesCatalog. */
export interface SubmissionSubType {
  code: string;
  name: string;
  supplementEffectiveDateType: string | null;
}

/** Phase KB-submission-types-v3 — a grouped submission type with optional sub-types. */
export interface SubmissionTypeGroup {
  code: string;
  name: string;
  requiresPriorDocumentSet: boolean;
  description: string | null;
  sortOrder: number;
  sourceUrl: string | null;
  subTypes: SubmissionSubType[];
}

/**
 * Phase KB-submission-types-v3 — GET /api/v1/submission-types.
 *
 * This is the **unwrapped** payload. The server sends `{ ok, data }` and
 * `apiFetch` returns `json.data`, so this is what callers actually receive.
 *
 * phase-fix-r0-region-alias-submission-types — this type previously described
 * the whole `{ ok, data }` envelope, so every caller wrote `res.data.…` and
 * silently got `undefined`. Same defect class as the Admin Forms crash fixed
 * in `4bb79bf` ("apiFetch unwraps {ok,data}, client types lied"); that pass
 * missed this endpoint.
 */
export interface SubmissionTypesData {
  /** The resolved canonical region code ('Health Canada' in → 'HC' out). */
  region: string | null;
  applicationType: string | null;
  applicationTypes: string[];
  submissionTypes: SubmissionTypeGroup[];
  /**
   * Present only when the requested region could not be resolved. The lists
   * come back empty so the UI keeps rendering; this names the unrecognised
   * value so the failure is observable instead of silent.
   */
  warning?: string;
}

export interface NotificationSubscriptionRule {
  ruleId: string;
  code: string;
  name: string;
  category: string | null;
  severity: string;
  isSubscribed: boolean;
}

export interface NotificationSubscriptionState {
  global: { ruleId: null; isSubscribed: boolean };
  rules: NotificationSubscriptionRule[];
}

export interface GovernmentForm {
  Id: string;
  TenantId: string;
  Region: string;
  FormKey: string;
  FormName: string;
  Description: string | null;
  PdfBlobKey: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  FieldCount: number;
  AutoFillCount: number;
  /**
   * phase-fix-unfillable-form-detection — automated-fill verdict computed at
   * upload: 'fillable' | 'xfa-dynamic' | 'xfa-hybrid' | 'encrypted' |
   * 'no-fields'. null = uploaded before the verdict existed (self-heals at
   * the next generate attempt).
   */
  FillCapability: string | null;
  /** Form Templates row 5 — mappings whose PDF field disappeared on a re-upload. */
  BrokenMappingCount?: number;
}

export interface GovFormMapping {
  Id: string;
  GovernmentFormId: string;
  PdfFieldName: string;
  AutoeCTDFieldPath: string | null;
  AutoFillEnabled: boolean;
  /** Admin-set required flag (kept on save — omitting it would clear it). */
  IsRequired?: boolean | null;
  /** Row 5 — the field is no longer in the uploaded PDF. */
  IsBroken?: boolean | null;
  BrokenReason?: string | null;
  /** Row 5 — advisory: the current field this broken mapping most likely became. */
  SuggestedField?: string | null;
  SuggestionScore?: number | null;
}

/** Row 5 — what a re-upload did to the existing mappings. */
export interface GovFormRevision {
  added: string[];
  removed: string[];
  brokenMappings: Array<{ pdfFieldName: string; autoeCTDFieldPath: string | null }>;
  suggestedRenames: Array<{ from: string; to: string; score: number }>;
}

export interface GovFormUploadResponse {
  form: GovernmentForm;
  fieldCount: number;
  prefilledCount: number;
  revision?: GovFormRevision;
}

export interface GovFormIdentifyResponse {
  filename: string;
  fieldCount: number;
  prefilledCount: number;
  fields: Array<{ name: string; value: string | null; fieldType: string }>;
}

/** The user's local calendar day as YYYY-MM-DD (what a form's `date` field shows). */
function localIsoDate(d: Date = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Form Templates tracker row 4 — one field of the "Test mapping" preview,
 * resolved against a real submission by the same resolver generation uses.
 */
export interface GovFormTestResult {
  pdfFieldName: string;
  mappedTo: string | null;
  resolvedValue: string | null;
  status: 'filled' | 'empty' | 'unknown-path';
  /** Where the value came from, or why there is none. */
  reason: string;
}

/** Row 4 — one canonical mapping path for the searchable path picker. */
export interface GovFormMappingPath {
  path: string;
  group: 'Date' | 'Submission' | 'Company profile';
}

function auditLogQueryString(q: AuditLogListQuery): string {
  const params = new URLSearchParams();
  if (q.page !== undefined) params.set('page', String(q.page));
  if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
  if (q.fromDate) params.set('fromDate', q.fromDate);
  if (q.toDate) params.set('toDate', q.toDate);
  if (q.actorEmail) params.set('actorEmail', q.actorEmail);
  if (q.actorId) params.set('actorId', q.actorId);
  if (q.targetType) params.set('targetType', q.targetType);
  if (q.targetId) params.set('targetId', q.targetId);
  if (q.action) params.set('action', q.action);
  if (q.q) params.set('q', q.q);
  if (q.sort) params.set('sort', q.sort);
  // Legacy aliases (pre-5.27). Only send if the new field wasn't set so
  // the backend receives a single canonical value per concept.
  if (q.from && !q.fromDate) params.set('from', q.from);
  if (q.to && !q.toDate) params.set('to', q.to);
  if (q.userEmail && !q.actorEmail) params.set('userEmail', q.userEmail);
  if (q.resource && !q.targetType) params.set('resource', q.resource);
  return params.toString();
}

export type AnnotationSeverity = 'info' | 'warning' | 'error';

export interface Annotation {
  Id: string;
  DocumentId: string;
  SubmissionId: string;
  TenantId: string;
  UserId: string;
  Severity: AnnotationSeverity;
  Comment: string;
  CreatedAt: string;
  UpdatedAt: string;
  ResolvedAt: string | null;
  ResolvedByUserId: string | null;
}

/** phase-package-viewer-live-structure — one leaf of the DERIVED structure. */
export interface LiveStructureLeaf {
  readonly documentId: string;
  readonly fileName: string;
  readonly sourceFileName: string;
  readonly path: string;
  readonly ctdSection: string;
  readonly operation: string;
  readonly replacesDocumentId: string | null;
  readonly wouldBeExcluded: boolean;
  readonly exclusionReason: string | null;
  /** Always the literal 'pending-compile' — never a computed value. */
  readonly md5: 'pending-compile';
  readonly sizeBytes: 'pending-compile';
}

export interface LivePackageStructure {
  readonly submissionId: string;
  readonly region: string;
  readonly sequenceFolder: string;
  readonly leaves: readonly LiveStructureLeaf[];
  readonly unplaced: ReadonlyArray<{ documentId: string; sourceFileName: string }>;
  readonly wouldExcludeCount: number;
  readonly latestCompile: {
    readonly id: string;
    readonly generatedAt: string;
    readonly isComplete: boolean;
    readonly excludedCount: number;
    readonly excluded: ReadonlyArray<{ documentId: string; fileName: string; module: string | null; reason: string }>;
    readonly validationStatus: 'pass' | 'warnings' | 'errors' | 'overridden';
  } | null;
  readonly isStale: boolean;
}

export interface BackboneSection {
  sectionCode: string;
  title: string;
  leafCount: number;
}

export interface BackboneModule {
  moduleNumber: string;
  sections: BackboneSection[];
}

export interface ParsedBackboneStructure {
  sequenceNumber: string;
  region: string;
  modules: BackboneModule[];
}

export interface Backbone {
  backboneXml: string | null;
  parsedStructure: ParsedBackboneStructure | null;
}

// ----- Phase 5.X-lifecycle-report-panel -----

/** A single event in a submission's lifecycle audit history. */
export interface LifecycleEvent {
  /** AuditLog row ID. */
  readonly id: string;
  /** Action name, e.g. 'document.lifecycle.change', 'submission.update'. */
  readonly action: string;
  /** 'Document' or 'Submission'. */
  readonly targetType: string;
  /** ID of the affected document or submission. */
  readonly targetId: string;
  /** ActorId (user UUID). */
  readonly userId: string;
  /** ActorEmail or ActorId fallback. */
  readonly userName: string;
  /** ISO 8601 timestamp. */
  readonly timestamp: string;
  /** State before the change, or null. */
  readonly before: Record<string, unknown> | null;
  /** State after the change, or null. */
  readonly after: Record<string, unknown> | null;
}

/**
 * phase-fix-v2client-envelope-sweep — this is the UNWRAPPED payload.
 * apiFetch strips the { ok, data } envelope; this type is what the client
 * method actually resolves to.
 */
export interface SubmissionLifecycleResponse {
  readonly events: readonly LifecycleEvent[];
}

// ----- Phase forms-templates-unified -----

/** A form instance on a submission (cover letter, gov form, or manual). */
export interface SubmissionForm {
  id: string;
  submissionId: string;
  formType: 'template' | 'government' | 'manual';
  templateId: string | null;
  governmentFormId: string | null;
  displayName: string;
  /** draft | ready | approved | manual */
  status: string;
  generatedContent: string | null;
  /** true when the form has a stored PDF blob ready to download */
  hasPdf: boolean;
  /**
   * phase-fix-path-c-flatten-guardrail — number of government-form fields left
   * blank (and therefore live/unfilled) on the stored PDF. >0 means the PDF was
   * NOT flattened and still needs completing. 0 for template/manual forms and
   * for a fully-filled (flattened) government form.
   */
  unfilledFieldCount: number;
  /**
   * phase-generate-with-gaps — the LAST generation's gap list: required values
   * with no source that were substituted with a visible `«GAP»` marker in the
   * output. Non-empty means the document has placeholders; the client disables
   * Approve with the reason and the server refuses approval authoritatively. A
   * clean regeneration clears this.
   */
  gaps: GenerationGap[];
  /**
   * phase-fix-unfillable-form-detection — the linked government form's
   * fill-capability verdict ('fillable' | 'xfa-dynamic' | 'xfa-hybrid' |
   * 'encrypted' | 'no-fields'); null for templates or pre-verdict rows.
   * Anything non-null and non-'fillable' means Generate cannot auto-fill.
   */
  govFillCapability: string | null;
  /**
   * phase-form-template-versioning — the template version (single-track int,
   * shown as "N.0") that produced the LAST generation; null for government/
   * manual forms and pre-versioning generations.
   */
  templateVersionNumber: number | null;
  /**
   * True when the template has moved past templateVersionNumber AND the
   * parent sequence is still in progress (locked sequences are never
   * flagged). Informational only — the user chooses whether to regenerate.
   */
  templateVersionStale: boolean;
  /** Plain-language sentence for the stale chip's tooltip; null when not stale. */
  templateVersionStaleReason: string | null;
  sourceType: 'required' | 'manual-add';
  sortOrder: number | null;
  approvedAt: string | null;
  approvedBy: string | null;
  promotedDocumentId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** phase-fix-path-c-flatten-guardrail — one unfilled government-form field. */
export interface UnfilledGovFormField {
  pdfFieldName: string;
  autoeCTDFieldPath: string;
  required: boolean;
  /** Form Templates row 5 — 'mapping broken' when the PDF field disappeared. */
  reason?: string;
  detail?: string;
}

/**
 * phase-generate-with-gaps — one gap: a required value with no source that was
 * rendered as a visible `«GAP»` marker instead of blocking generation.
 */
export interface GenerationGap {
  /** The field / template-variable key, e.g. `sponsorAddress`. */
  key: string;
  /** Humanized for display, e.g. `Sponsor Address`. */
  label: string;
}

/**
 * Envelope returned by `submissionForms.generate`.
 *
 * phase-fix-path-b-variable-resolution §2.5 — `warnings.variablesDefaulted`
 * names the template variables that had no real value and fell back to a
 * default or were left visible as `{{placeholder}}`.
 * phase-fix-path-c-flatten-guardrail — `warnings.fieldsUnfilled` names the
 * government-form fields that generated blank and were left live (the form was
 * stored unflattened). Exactly one of the two is present, per form type.
 */
export interface SubmissionFormGenerateResult {
  ok: boolean;
  data: SubmissionForm;
  preview?: string;
  /**
   * phase-generate-with-gaps — `warnings.gaps` names the required values that
   * generated as a visible `«GAP»` marker (present on both form types). The row
   * carries the same list on `data.gaps` for the persisted chip + approve gate.
   */
  warnings?: {
    variablesDefaulted?: string[];
    fieldsUnfilled?: UnfilledGovFormField[];
    gaps?: GenerationGap[];
    /** Form Templates row 6 — bound variables that produced nothing. */
    bindingIssues?: GenerateBindingIssue[];
  };
}

/** A row from RequiredFormsCatalog. */
export interface RequiredFormsCatalogEntry {
  Id: string;
  TenantId: string | null;
  Region: string;
  SubmissionType: string;
  FormType: string;
  GovernmentFormKey: string | null;
  DisplayName: string;
  Required: boolean;
  SortOrder: number;
}

/** A row from RequiredTemplatesCatalog. */
export interface RequiredTemplatesCatalogEntry {
  id: string;
  tenantId: string | null;
  region: string;
  submissionType: string;
  /** R1 — activity key (null = applies to every activity of the type). */
  applicationType?: string | null;
  activityCode?: string | null;
  templateType: string;
  templateName: string;
  isRequired: boolean;
  conditional: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // phase-forms-workflow-follow-up (Item C) — override annotations.
  isOverride?: boolean;
  overridesCatalogId?: string | null;
  baseline?: {
    isRequired: boolean;
    conditional: string | null;
    templateName: string;
    notes: string | null;
    activityCode: string | null;
  } | null;
}

/** phase-forms-workflow-follow-up (Item A) — one loadable requirement in the picker. */
export interface RequirementPickerItem {
  key: string;
  displayName: string;
  formType: 'government' | 'manual' | 'template';
  requirement: 'required' | 'conditional' | 'optional';
  templateType: string | null;
  alreadyLoaded: boolean;
  defaultChecked: boolean;
}

/** phase-forms-workflow-follow-up (Item B) — a DocumentTemplate option for the fallback dropdown. */
export interface FormTemplateOption {
  id: string;
  name: string;
  templateType: string | null;
  region: string | null;
  tenantId: string | null;
}

/** Combined response from /api/v1/submission-requirements/:region/:type */
export interface SubmissionRequirementsData {
  region: string;
  submissionType: string;
  forms: Array<{
    id: string;
    tenantId: string | null;
    region: string;
    submissionType: string;
    applicationType?: string | null;
    activityCode?: string | null;
    formType: string;
    governmentFormKey: string | null;
    displayName: string;
    required: boolean;
    conditional?: string | null;
    notes?: string | null;
    sortOrder: number;
    // Item C — override annotations.
    isOverride?: boolean;
    overridesCatalogId?: string | null;
    baseline?: {
      required: boolean;
      conditional: string | null;
      displayName: string;
      notes: string | null;
      activityCode: string | null;
    } | null;
  }>;
  templates: RequiredTemplatesCatalogEntry[];
}

// ----- Phase node-extension-compiler-ui (TODO-048 Part B) -----

export interface NodeExtension {
  Id: string;
  SubmissionId: string;
  SectionCode: string;
  ExtensionCode: string;
  ExtensionLabel: string;
  SortOrder: number;
  CreatedAt: string;
  UpdatedAt: string;
}

// ----- phase-submission-milestones — dated regulatory commitments per sequence -----

/**
 * A milestone as the API returns it. `dueDate` is a calendar date
 * (`YYYY-MM-DD`, no time); `completedAt` is an ISO timestamp or null. `kind`
 * comes from the ONE shared definition (`src/lib/submissions/milestoneKinds.ts`,
 * imported here through the `@shared/milestoneKinds` alias — never re-listed).
 */
export interface SubmissionMilestone {
  id: string;
  submissionId: string;
  kind: MilestoneKind;
  dueDate: string;
  label: string | null;
  notes: string | null;
  completedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** Tenant-wide list rows also carry the owning sequence's identity. */
export interface SubmissionMilestoneWithSubmission extends SubmissionMilestone {
  submission: { drugName: string; sequenceNumber: string; region: string };
}

export interface CreateMilestoneBody {
  kind: MilestoneKind;
  dueDate: string;
  /** Required when `kind === 'custom'`; optional otherwise. */
  label?: string | null;
  notes?: string | null;
}

export interface UpdateMilestoneBody {
  dueDate?: string;
  kind?: MilestoneKind;
  /** Required when the (new or existing) kind is `custom`. */
  label?: string | null;
  notes?: string | null;
  /** `true` stamps `completedAt` now; `false` clears it. */
  completed?: boolean;
  /** Recorded in the audit row's AfterJson. */
  reason?: string;
}

// ----- Phase 7.3 — SPL parse + preview (GAP-H-07) -----

export interface SplActiveIngredient {
  name: string;
  strength: string;
  unit: string;
}

export interface SplMetadata {
  drugName: string;
  labelerCode: string;
  ndcCodes: string[];
  activeIngredients: SplActiveIngredient[];
  dosageForm: string;
  routeOfAdministration: string;
  marketingStatus: string;
  applicationNumber?: string;
  setId: string;
  versionNumber: string;
}
